import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  checkWidgetInstallation,
  checkOrderConversions,
  getDashboardAnalytics,
  getDashboardData,
  getDashboardSettings,
  getHaravanDiscounts,
  getHaravanProducts,
  login,
  loginWithHaravanSession,
  requestAccount,
  saveAnnouncementBar,
  saveCurrencyConverter,
  saveCustomCursor,
  saveDashboardData,
  saveDashboardSettings,
  saveProductCountdown,
  saveShoppableVideos,
  scanShoppablePageSections,
  type AnnouncementBarsDisplayMode,
  type AnnouncementBarsGlobalSettings,
  type AnnouncementBarsTransition,
  type AnnouncementBarSettings,
  type CurrencyCode,
  type CurrencyConverterSettings,
  type CurrencySwitcherPosition,
  type CustomCursorSettings,
  type DashboardData,
  type DashboardSettings,
  type DashboardSession,
  type HaravanDiscountOption,
  type HaravanProductOption,
  type InstallationCheckResult,
  type LeadRow,
  type ProductCountdownItem,
  type ProductCountdownSettings,
  type RewardDraft,
  type ShoppablePageSection,
  type ShoppablePlacementPreset,
  type ShoppableVideoCarousel,
  type ShoppableVideoItem,
  type ShoppableVideosSettings,
  type WheelConfig,
  type DashboardAnalyticsWindow,
} from './api';
import { getFirebaseAnalytics, trackFirebasePageView } from './firebase-analytics';
import {
  getHaravanSessionToken,
  getHaravanShopOrigin,
  isHaravanEmbeddedContext,
} from './haravan-embedded';
import type { CampaignConfig, LeadField, SpinWheelSegment } from '../../frontend-widget/src/types';

const sessionKey = 'spin-dashboard-session';
const languageKey = 'spin-dashboard-language';
const sidebarCollapsedKey = 'spin-dashboard-sidebar-collapsed';
const widgetSnippet = '<script async src="https://widget.dadaplugins.com/widget.js"></script>';
type DashboardLanguage = 'en' | 'vi';
type DashboardView =
  | 'spinWheel'
  | 'customCursor'
  | 'currencyConverter'
  | 'announcementBar'
  | 'shoppableVideos'
  | 'productCountdown'
  | 'installation'
  | 'settings';
type SpinWheelView = 'overview' | 'leads';
type ShoppableVideosView = 'settings' | 'videos';
type AnnouncementBarView = 'bars' | 'settings';
type AppRoute = 'home' | 'dashboard' | 'blogIndex' | 'blog';
type MetricIcon =
  | 'impressions'
  | 'uniqueImpressions'
  | 'clicks'
  | 'uniqueClicks'
  | 'leads'
  | 'views'
  | 'announcementClicks'
  | 'dismisses';
type AnalyticsPeriod = 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';
type LeadsSortKey = 'purchased' | 'purchaseAmount' | 'received';
type SortDirection = 'asc' | 'desc';

const supportedDashboardCurrencies: CurrencyCode[] = [
  'USD',
  'EUR',
  'JPY',
  'INR',
  'CNY',
  'IDR',
  'THB',
  'KRW',
  'MYR',
];

const hiddenHaravanScopes = new Set([
  'openid',
  'profile',
  'address',
  'email',
  'phone',
  'org',
  'userinfo',
  'grant_service',
]);

const currencyRateFallbacks: Record<CurrencyCode, number> = {
  USD: 0.000039,
  EUR: 0.000036,
  JPY: 0.0061,
  INR: 0.0033,
  CNY: 0.00028,
  IDR: 0.62,
  THB: 0.00135,
  KRW: 0.052,
  MYR: 0.00016,
};

const blogSlug = '/blog/toi-uu-chuyen-doi-spin-the-wheel-haravan-dada-plugins';
const customCursorBlogSlug = '/blog/custom-cursor-haravan-brand-upgrade-re-nhat';
const blogMeta = {
  title: '4 Tầng Chuyển Đổi Spin The Wheel: Từ Impression Đến Coupon',
  description:
    'Học cách tối ưu từng bước trong phễu Spin Wheel của Dada Plugins: impression→click→capture→coupon. Màu sắc, văn bản, xác minh lead, và giải thưởng bumper 1 triệu VND.',
  keywords:
    'tối ưu tỷ lệ chuyển đổi Spin the Wheel Haravan, spin the wheel conversion optimization Haravan, Dada Plugins tùy chỉnh, impression to click Haravan, coupon dispatch Dada, xác minh số điện thoại Zalo, bumper prize Spin Wheel',
  publishedAt: '2026-05-22',
  modifiedAt: '2026-05-22',
  category: 'Spin Wheel CRO',
};

const customCursorBlogMeta = {
  title: 'Custom Cursor Haravan: Nâng Cấp Brand Chỉ Mất 5 Phút',
  description:
    'Custom cursor là micro-interaction đơn giản nhất để tăng brand recall và time on site. Học cách cài và tùy chỉnh với Dada Plugins trên Haravan.',
  keywords:
    'custom cursor website bán hàng Haravan, custom cursor ecommerce Haravan brand, Dada customer cursor, cursor tùy chỉnh website, tăng time on site Haravan, micro-interaction ecommerce',
  publishedAt: '2026-05-24',
  modifiedAt: '2026-05-24',
  category: 'Custom Cursor',
};

const landingMeta = {
  en: {
    title: 'Dada Plugins | Haravan Growth Plugins',
    description:
      'Dada Plugins builds Haravan plugins that help merchants increase revenue, capture leads, launch storefront widgets, and optimize store operations.',
    keywords:
      'Dada Plugins, Haravan plugins, Haravan app, ecommerce widget, spin wheel, lead capture, coupon widget, Vietnam ecommerce',
  },
  vi: {
    title: 'Dada Plugins | Plugin tăng trưởng Haravan',
    description:
      'Dada Plugins phát triển plugin Haravan giúp merchant tăng doanh thu, thu thập lead, tối ưu chuyển đổi storefront và cải thiện quy trình vận hành.',
    keywords:
      'Dada Plugins, plugin Haravan, ứng dụng Haravan, widget ecommerce, spin wheel Haravan, thu thập lead, coupon widget, thương mại điện tử Việt Nam',
  },
} as const;

const blogIndexMeta = {
  en: {
    title: 'Dada Plugins Blog | Haravan Growth Playbooks',
    description:
      'Dada Plugins blog with Haravan growth playbooks for revenue, lead capture, storefront conversion, and merchant operations.',
    keywords:
      'Haravan growth blog, ecommerce conversion, Dada Plugins blog, lead capture playbook, storefront optimization',
  },
  vi: {
    title: 'Dada Plugins Blog | Playbook tăng trưởng Haravan',
    description:
      'Blog Dada Plugins chia sẻ playbook tăng trưởng Haravan về doanh thu, thu thập lead, tối ưu conversion storefront và vận hành merchant.',
    keywords:
      'blog Haravan, tăng trưởng ecommerce, Dada Plugins blog, tối ưu chuyển đổi storefront, thu thập lead Haravan',
  },
} as const;

const blogArticle = {
  title:
    '4 Tầng Chuyển Đổi Trong Spin The Wheel: Từ Impression Đến Coupon - Hướng Dẫn Tối Ưu Từng Bước',
  deck: 'Phễu chuyển đổi của Spin the Wheel không phải một bước - mà là bốn. Mỗi tầng có điểm rò rỉ riêng, và Dada Plugins cho bạn công cụ để vá từng điểm đó.',
  intro: [
    'Hầu hết chủ shop nhìn vào Spin Wheel và chỉ thấy một số: số lead captured. Nhưng giữa lần đầu tiên widget hiện ra trên màn hình khách hàng đến lúc họ nhận được coupon, có bốn tầng chuyển đổi riêng biệt.',
    'Dada Plugins cho bạn 100% khả năng tùy chỉnh ở mỗi tầng. Bài viết này đi qua từng tầng, chỉ đúng nơi conversion hay thất thoát, và cách fix nó.',
  ],
  funnel: [
    ['Tầng 1', 'Impressions', 'Widget hiện ra'],
    ['Tầng 2', 'Clicks', 'Khách bắt đầu spin'],
    ['Tầng 3', 'Captures', 'Data được submit'],
    ['Tầng 4', 'Coupons', 'Lead được xác minh'],
  ],
  layers: [
    {
      number: '01',
      title: 'Impressions: Làm Cho Đúng Người Thấy Widget',
      metric: 'Widget visibility',
      question: 'Bao nhiêu % visitor thực sự thấy vòng quay trước khi rời đi?',
      body: 'Impression không phải pageview. Impression là số lần widget thực sự render và khách hàng có cơ hội nhìn thấy nó. Hai biến số kiểm soát điều này hoàn toàn trong Dada: trigger timing và frequency cap.',
      bullets: [
        'Homepage: dùng exit-intent vì traffic đa dạng và intent chưa rõ.',
        'Trang sản phẩm: exit-intent hoặc 15 giây để nudge khách đang cân nhắc.',
        'Blog / Content: 30-45 giây, khi khách đã đọc xong và receptive với offer.',
        'Checkout: không dùng, vì interrupt sẽ phá conversion.',
      ],
      callout:
        'Tối ưu frequency cap: 1 lần / 3 ngày với cap 2-3 lần / user. Đủ để catch khách quay lại mà không tạo cảm giác spam.',
    },
    {
      number: '02',
      title: 'Clicks: Văn Bản + Màu Sắc Quyết Định Tất Cả',
      metric: 'Impression → Spin',
      question: 'Trong số những người thấy widget, bao nhiêu % thực sự click và bắt đầu spin?',
      body: 'Conversion từ impression sang click phụ thuộc mạnh vào headline và colour scheme. Headline phải trả lời câu hỏi: tại sao tôi nên dừng lại và làm điều này?',
      bullets: [
        '"Quay ngay - Nhận tới 1,000,000đ" tốt hơn "Quay vòng may mắn!" vì nêu rõ giá trị tối đa.',
        '"Bạn có phần thưởng đang chờ" dùng ownership framing, khiến phần thưởng có cảm giác thuộc về khách.',
        'Màu sắc nên khớp ngành hàng: beauty hợp hồng nude / gold, FMCG hợp đỏ / cam / vàng, tech hợp navy / bạc / trắng.',
        'A/B test màu trong 7 ngày, chỉ đổi màu và giữ nguyên mọi yếu tố khác.',
      ],
      callout:
        'Store Haravan điển hình có thể thấy chênh lệch 15-30% CTR giữa màu tốt và màu kém phù hợp với ngành.',
    },
    {
      number: '03',
      title: 'Captures: Từ Spin Đến Submit',
      metric: 'Spin → Data submitted',
      question: 'Trong số những người đã spin, bao nhiêu % thực sự điền và submit thông tin?',
      body: 'Tầng này bị ảnh hưởng bởi giá trị phần thưởng, lượng thông tin được yêu cầu và thiết kế form. Cấu trúc prize pool quyết định phần lớn capture rate.',
      bullets: [
        'Bumper prize: đặt giải rất cao, ví dụ 1,000,000đ, với xác suất 0.5-1% để tạo kỳ vọng.',
        'Core prize: giảm 50,000-100,000đ với xác suất 40-50%, đây là conversion engine.',
        'Entry prize: freeship hoặc giảm 20,000đ với xác suất 35-45%, để không ai ra về tay không.',
        'Form nên hỏi email hoặc số điện thoại - chỉ một trong hai nếu mục tiêu là capture rate tối đa.',
      ],
      callout:
        'Nguyên tắc vàng: càng ít field càng tốt. Ba field trở lên thường kéo capture rate xuống dưới 25% trong context Spin Wheel.',
    },
    {
      number: '04',
      title: 'Coupon Dispatch: Lead Được Xác Minh Là Lead 100% Có Giá Trị',
      metric: 'Capture → Verified lead',
      question: 'Trong số data đã capture, bao nhiêu % là thông tin thật và có thể reach được?',
      body: 'Không có xác minh, list dễ bị email giả, số điện thoại sai và data làm méo analytics. Với tích hợp external plugin, coupon có thể chỉ dispatch sau khi thông tin được xác minh.',
      bullets: [
        'Khách spin và submit số điện thoại hoặc email.',
        'Hệ thống gửi OTP qua Zalo hoặc email.',
        'Khách nhập OTP để xác nhận.',
        'Chỉ sau bước này, coupon mới được dispatch.',
      ],
      callout:
        'Kết quả: mọi lead trong list của bạn đều là real, reachable contact. Đây không chỉ là data - đây là verified lead.',
    },
  ],
  comparisonRows: [
    ['Impressions (1,000)', '700 thấy widget (70%)', '900 thấy (90%)', 'Trigger timing đúng'],
    ['Clicks', '70 click (10%)', '270 click (30%)', 'Headline + colour scheme'],
    ['Captures', '35 submit (50%)', '175 submit (65%)', 'Prize structure + form đơn giản'],
    ['Verified', '~20 real leads (57%)', '~165 verified (94%)', 'OTP verification'],
    [
      'Kết quả cuối',
      '20 verified leads (2%)',
      '165 verified leads (16.5%)',
      'Tất cả 4 tầng cộng lại',
    ],
  ],
  campaigns: [
    [
      'Tết Nguyên Đán',
      'Đỏ + Vàng + Kim tuyến',
      'Lì xì may mắn - Quay để nhận ngay!',
      'Lì xì 1,000,000đ',
    ],
    ['30/4 Thống Nhất', 'Đỏ + Vàng Sao', 'Quà mừng ngày lịch sử - Chỉ hôm nay', 'Voucher 500,000đ'],
    [
      '2/9 Quốc Khánh',
      'Đỏ + Vàng Sao',
      'Ưu đãi đặc biệt Quốc Khánh cho bạn',
      'Freeship + 200,000đ',
    ],
    ['11/11 Sale', 'Đen + Vàng', '11.11 - Quay ngay, thắng ngay, mua ngay', 'Giảm 1,000,000đ'],
  ],
};

const customCursorArticle = {
  title: 'Custom Cursor Là Brand Upgrade Rẻ Nhất Bạn Chưa Thực Hiện Trên Haravan',
  deck: 'Quick guide: tại sao custom cursor quan trọng hơn vẻ ngoài, cách nó ảnh hưởng đến brand recall và time on site, và cách cài đặt với Dada Plugins trong 5 phút.',
  intro: [
    'Trong tất cả các thay đổi có thể thực hiện trên store Haravan hôm nay, custom cursor có thể là thứ có tỷ lệ effort / impact tốt nhất trong toàn bộ Dada stack.',
    '5 phút setup. Không cần code. Không ảnh hưởng đến tốc độ load. Nhưng nó thay đổi một thứ rất tinh tế và rất quan trọng: trải nghiệm cảm nhận của khách hàng khi tương tác với brand của bạn.',
  ],
  stats: [
    ['+12-18%', 'Time on site', 'Novelty effect khiến khách tương tác lâu hơn'],
    ['-5-8%', 'Bounce rate', 'Curiosity tạo thêm engagement ở desktop'],
    ['1', 'Brand touchpoint', 'Cursor hiện diện liên tục trong suốt session'],
    ['5 phút', 'Setup time', 'Upload file, bật plugin, kiểm tra storefront'],
  ],
  sections: [
    {
      number: '01',
      title: 'Đây Không Chỉ Là Vẻ Ngoài',
      metric: 'Brand recall',
      question: 'Điểm tương tác nào khách hàng luôn nhìn thấy khi browse store?',
      body: 'Hiểu lầm phổ biến nhất về custom cursor là: "nó chỉ là aesthetic". Điều này không đúng. Cursor là thứ duy nhất trên màn hình mà người dùng luôn nhìn vào khi họ đang browse. Không giống hero banner có thể bị scroll qua, popup có thể bị close, hoặc sidebar có thể bị ignore. Con trỏ chuột là điểm tiếp xúc liên tục giữa người dùng và brand.',
      bullets: [
        'Logo nhỏ hoặc icon sản phẩm biến mỗi chuyển động chuột thành một brand touchpoint.',
        'Cursor phù hợp với brand tạo cảm giác storefront được chăm chút hơn.',
        'Micro-interaction tinh tế không làm gián đoạn hành trình mua hàng như popup hoặc chat bubble.',
      ],
      callout:
        'Khi bạn thay thế con trỏ mặc định bằng cursor phù hợp với brand, bạn đang tạo brand presence tại đúng điểm khách hàng tương tác.',
    },
    {
      number: '02',
      title: 'Vì Sao Người Dùng Việt Nam Responsive Với Micro-interaction',
      metric: 'Time on site',
      question: 'Một chi tiết nhỏ có thể khiến khách ở lại lâu hơn không?',
      body: 'Người dùng desktop Việt Nam, đặc biệt nhóm 18-35 tuổi tại thành thị, quen với giao diện game, app mobile nhiều hiệu ứng và social media giàu animation. Một custom cursor là điều bất ngờ tích cực: không annoying như popup, không intrusive như chat bubble, chỉ là một chi tiết nhỏ khiến họ tò mò và nghịch thêm một chút.',
      bullets: [
        '"Nghịch thêm một chút" nghĩa là time on site tăng.',
        'Time on site tăng tạo thêm cơ hội xem sản phẩm, add to cart và nhận diện brand.',
        'Với fashion, beauty, lifestyle và premium goods, chi tiết cảm xúc thường tạo khác biệt lớn.',
      ],
      callout:
        'Cursor không thay thế offer hay UX tốt, nhưng nó làm storefront có cảm giác khác biệt ngay từ lần chạm đầu tiên.',
    },
    {
      number: '03',
      title: 'Cài Đặt Với Dada Plugins Trong 3 Bước',
      metric: 'No code setup',
      question: 'Merchant Haravan có cần developer để bật custom cursor không?',
      body: 'Dada Plugins đưa phần setup vào dashboard để merchant có thể tự bật, tắt và thay file cursor mà không sửa theme. Plugin cũng tự disable trên touch device, vì mobile không có khái niệm con trỏ chuột.',
      bullets: [
        'Trong Dada Dashboard, chọn Custom Cursor và upload file PNG, SVG, WebP, GIF hoặc JPG.',
        'Dùng file 32x32px đến 64x64px, nền trong suốt, tốt nhất dưới 10KB để di chuyển mượt.',
        'Bật plugin, lưu, sau đó kiểm tra desktop storefront. Trên mobile, cursor tự fallback về mặc định.',
      ],
      callout:
        'SVG thường là lựa chọn tốt nhất vì nhẹ, scale tốt ở nhiều độ phân giải và giữ cạnh sắc nét.',
    },
  ],
  cursorTypes: [
    ['Logo / Icon brand', 'Premium brands muốn reinforce identity', 'Logo thu nhỏ 16x16px hoặc brand icon', 'Tránh logo quá lớn che nội dung'],
    ['Sản phẩm hero', 'Store có một hero SKU rõ ràng', 'Hạt cà phê, giọt serum, icon sneaker', 'Tránh hình quá abstract khó hiểu'],
    ['Mascot / Character', 'Brand có mascot hoặc persona cụ thể', 'Linh vật nhỏ theo brand', 'Mascot quá phức tạp sẽ mất nét'],
    ['Campaign cursor', 'Dịp lễ, flash sale, seasonal campaign', 'Hoa mai, trái tim, bolt lightning', 'Nhớ tắt khi campaign kết thúc'],
    ['Minimalist dot', 'Brand thời trang tối giản hoặc premium', 'Dot nhỏ theo brand color', 'Cần brand color đủ mạnh'],
  ],
  campaigns: [
    ['Tết', 'Phất xanh lá, đào, hoa mai vàng'],
    ['Valentine', 'Trái tim nhỏ theo con trỏ'],
    ['Halloween', 'Bí ngô nhỏ cho store phù hợp'],
    ['11/11', 'Lightning icon để reinforce flash sale energy'],
    ['Mùa đông', 'Bông tuyết nhỏ nhẹ'],
  ],
};

const blogPosts = [
  {
    slug: blogSlug,
    title: blogArticle.title,
    excerpt: blogArticle.deck,
    category: blogMeta.category,
    readTimeKey: 'readTime',
    date: blogMeta.publishedAt,
    metaTitle: blogMeta.title,
    metaDescription: blogMeta.description,
    keywords: blogMeta.keywords,
    language: 'vi',
    kind: 'spinWheel',
  },
  {
    slug: customCursorBlogSlug,
    title: customCursorArticle.title,
    excerpt: customCursorArticle.deck,
    category: customCursorBlogMeta.category,
    readTimeKey: 'readTime',
    date: customCursorBlogMeta.publishedAt,
    metaTitle: customCursorBlogMeta.title,
    metaDescription: customCursorBlogMeta.description,
    keywords: customCursorBlogMeta.keywords,
    language: 'vi',
    kind: 'customCursor',
  },
] as const;

const translations = {
  en: {
    merchantDashboard: 'Merchant dashboard',
    access: 'Access',
    landingEyebrow: 'Haravan growth plugins',
    landingTitle: 'Dada Plugins',
    landingSubtitle:
      'We build Haravan plugins that help merchants increase revenue, capture qualified leads, optimize sales, and streamline operational workflows.',
    landingHeroCta: 'Open dashboard',
    requestAccount: 'Request an account',
    startNow: 'Start now',
    requestAccountTitle: 'Request your Dada Plugins account',
    requestAccountBody:
      'Send your store details. We will contact you shortly while we activate your profile.',
    storeUrl: 'Store URL',
    emailAddress: 'Email address',
    phoneNumber: 'Phone number',
    sendRequest: 'Send request',
    accountRequestSuccess:
      'Thank you. We will contact you as soon as possible, we are activating your profile.',
    accountRequestFailed: 'Unable to send the request. Please try again.',
    localPhonePlaceholder: '901 234 567',
    landingSecondaryCta: 'Explore plugins',
    landingMetricRevenue: 'Revenue tools',
    landingMetricLeads: 'Lead capture',
    landingMetricOps: 'Ops workflows',
    landingSectionTitle: 'Built for stores that want sharper growth loops.',
    landingSectionBody:
      'Dada Plugins focuses on practical storefront tools that launch fast, fit Haravan themes, and give merchants clearer ways to act on customer intent.',
    revenueGrowth: 'Increase revenue',
    revenueGrowthBody:
      'Launch storefront experiences that convert visitors into buyers with timely offers and campaign controls.',
    leadCapture: 'Capture leads',
    leadCaptureBody:
      'Collect useful customer contacts directly from engaging onsite widgets and export them for follow-up.',
    workflowOptimization: 'Optimize operations',
    workflowOptimizationBody:
      'Reduce manual work with focused tools for merchandising, promotions, analytics, and store processes.',
    storefrontLayer: 'Storefront layer',
    storefrontLayerBody: 'Interactive widgets that feel native inside Haravan themes.',
    merchantControls: 'Merchant controls',
    merchantControlsBody: 'Simple dashboards for campaigns, design, leads, and configuration.',
    actionableData: 'Actionable data',
    actionableDataBody: 'Useful metrics and exports that support follow-up and optimization.',
    workflowTitle: 'From visitor attention to merchant action.',
    workflowStep1: 'Engage visitors with focused storefront experiences.',
    workflowStep2: 'Collect lead data and reward intent safely.',
    workflowStep3: 'Improve campaigns through analytics and operational controls.',
    finalCtaTitle: 'Launch smarter Haravan experiences with Dada Plugins.',
    finalCtaBody:
      'Access the merchant dashboard to configure campaigns, coupons, colors, and leads.',
    landingCta: 'Access dashboard',
    blog: 'Blog',
    blogEyebrow: 'Growth playbook',
    blogSectionTitle: 'Spin Wheel conversion, broken down by funnel layer.',
    blogSectionBody:
      'A practical guide to optimizing impressions, clicks, captures, and verified coupon delivery for Haravan stores.',
    blogIndexTitle: 'Dada Plugins Blog',
    blogIndexBody:
      'Playbooks for Haravan merchants who want better revenue, lead capture, storefront conversion, and operational workflows.',
    readArticle: 'Read article',
    backHome: 'Back to home',
    readTime: '8 min read',
    overview: 'Overview',
    leads: 'Leads',
    customCursor: 'Custom Cursor',
    currencyConverter: 'Currency Converter',
    announcementBar: 'Announcement Bar',
    shoppableVideos: 'Shoppable Videos',
    installation: 'Installation',
    settings: 'Settings',
    dashboardSections: 'Dashboard sections',
    language: 'Language',
    logout: 'Logout',
    saveChanges: 'Save changes',
    saving: 'Saving...',
    previewWidget: 'Preview widget',
    hidePreview: 'Hide preview',
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
    loadingDashboard: 'Loading dashboard...',
    analyticsPeriod: 'Analytics period',
    period: 'Period',
    today: 'Today',
    yesterday: 'Yesterday',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    customRange: 'Custom range',
    fromDate: 'From',
    toDate: 'To',
    overviewDescription:
      'Configure wheel copy, colors, coupon codes, probabilities, and campaign state.',
    leadsDescription:
      'Review contacts collected by the wheel and the coupon each customer received.',
    customCursorDescription: 'Enable a branded cursor across the storefront.',
    currencyConverterDescription:
      'Convert VND storefront prices into selected currencies with optional auto-detection.',
    announcementBarDescription: 'Show a clickable store announcement with optional countdown.',
    shoppableVideosDescription:
      'Create a watch-and-buy storefront section with linked products.',
    productCountdown: 'Product Countdown',
    productCountdownDescription:
      'Show a product-specific countdown beside the product name on selected product pages.',
    productCountdownList: 'Product countdowns',
    productCountdownListDescription: 'Create countdowns tied to individual Haravan products.',
    productCountdownCreate: 'Create',
    productCountdownEdit: 'Edit',
    productCountdownBack: 'Back to list',
    productCountdownDelete: 'Delete countdown',
    productCountdownDeleteConfirm: 'Delete this product countdown? This action cannot be undone.',
    productCountdownDeleted: 'Product countdown deleted',
    productCountdownSaved: 'Product countdown saved',
    productCountdownNoItems: 'No product countdowns yet.',
    productCountdownSettings: 'Countdown settings',
    productCountdownProduct: 'Product',
    productCountdownStart: 'Start date',
    productCountdownEnd: 'End date',
    productCountdownAlwaysActive: 'Always active until end date',
    productCountdownSelectProduct: 'Select product',
    productCountdownNoProduct: 'No product selected',
    productCountdownSchedule: 'Schedule',
    productCountdownPosition: 'Position',
    productCountdownPositionRight: 'Right of title',
    productCountdownPositionLeft: 'Left of title',
    productCountdownPositionAbove: 'Above title',
    productCountdownPositionBelow: 'Below title',
    productCountdownBackgroundColor: 'Background color',
    productCountdownTextColor: 'Text color',
    productCountdownBorderRadius: 'Border radius',
    productCountdownBorderWidth: 'Border width',
    productCountdownBorderColor: 'Border color',
    saveProductCountdown: 'Save product countdown',
    shoppableTabSettings: 'Settings',
    shoppableTabVideos: 'Videos',
    shoppableCarousels: 'Shoppable video carousels',
    shoppableCarouselsDescription: 'Create and manage video carousels for specific storefront pages.',
    shoppableCreateCarousel: 'Create',
    shoppableNoCarousels: 'No shoppable video carousels yet.',
    shoppableEditCarousel: 'Edit',
    shoppableBackToCarousels: 'Back to list',
    shoppableDeleteCarousel: 'Delete carousel',
    shoppableDeleteConfirm: 'Delete this shoppable video carousel? This action cannot be undone.',
    shoppableDeleted: 'Shoppable video carousel deleted',
    shoppablePageUrl: 'Page URL',
    installationDescription:
      'Add the wheel to your Haravan storefront with one lightweight script tag.',
    settingsDescription: 'Manage merchant access and private Haravan API credentials.',
    connectionSettingsDescription: 'Review the Haravan OAuth connection for this store.',
    accountSettings: 'Account settings',
    accountSettingsDescription: 'Username is assigned to this store and cannot be changed here.',
    username: 'Username',
    passwordSettings: 'Password',
    passwordSettingsDescription: 'Change the dashboard password for this merchant user.',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    savePassword: 'Save password',
    cancel: 'Cancel',
    close: 'Close',
    loading: 'Loading...',
    passwordMismatch: 'New password and confirmation do not match.',
    apiSettings: 'Haravan API key',
    apiSettingsDescription: 'Store the private Haravan API key used by backend integrations.',
    haravanApiKey: 'Haravan API key',
    haravanApiKeyPlaceholder: 'Paste a new API key',
    apiKeySaved: 'API key saved',
    apiKeyNotSet: 'No API key saved',
    saveSettings: 'Save settings',
    settingsSaved: 'Settings saved',
    haravanConnection: 'Haravan connection',
    connectedViaHaravan: 'Connected via Haravan OAuth',
    connectedStore: 'Connected store',
    grantedScopes: 'Granted scopes',
    scriptTagStatus: 'Widget script',
    scriptTagInstalled: 'Installed automatically',
    scriptTagNotInstalled: 'Not installed automatically',
    cursorSettings: 'Cursor settings',
    cursorSettingsDescription:
      'Upload a small PNG, WebP, GIF, SVG, or JPG. If no file is selected, the store keeps the normal cursor.',
    activePlugin: 'Active plugin',
    cursorFile: 'Cursor file',
    currentCursorFile: 'Current file',
    noCursorFile: 'No cursor file selected',
    cursorFileSelected: 'Cursor selected',
    removeCursorFile: 'Remove file',
    saveCursor: 'Save cursor',
    cursorSaved: 'Cursor saved',
    cursorPreview: 'Cursor preview',
    shoppableSettings: 'Shoppable video settings',
    shoppableSettingsDescription:
      'Manage the plugin’s general settings, including appearance and storefront display.',
    shoppableTitle: 'Section title',
    shoppableCardStyle: 'Card style',
    shoppableCornerRadius: 'Corner radius',
    shoppableBorderSize: 'Border size',
    shoppableBorderColor: 'Border color',
    shoppablePlaceholderColor: 'Card placeholder color',
    shoppableProductBarStyle: 'Product bar colors',
    shoppableProductBarPrimary: 'Primary color',
    shoppableProductBarSecondary: 'Secondary color',
    shoppableProductBarBackground: 'Background color',
    shoppableVisibility: 'Visibility',
    shoppableShowHome: 'Show on home page',
    shoppableShowProducts: 'Show on products page',
    shoppableShowProductDetails: 'Show on product details page',
    shoppablePlacement: 'Placement',
    shoppablePlacementHome: 'Home page placement',
    shoppablePlacementProducts: 'Products page placement',
    shoppablePlacementProductDetails: 'Product detail placement',
    shoppablePlacementCustomSelector: 'Custom CSS selector',
    shoppableScanSections: 'Scan page',
    shoppableScanningSections: 'Scanning...',
    shoppableSectionPlacement: 'Detected page section',
    shoppableBeforeSection: 'Before',
    shoppableAfterSection: 'After',
    shoppableNoSectionsFound: 'No sections found. Use a custom selector.',
    shoppableSectionsError: 'Unable to scan this page.',
    shoppablePlacementAuto: 'Auto',
    shoppablePlacementTop: 'Top of page',
    shoppablePlacementAfterHero: 'After hero',
    shoppablePlacementBeforeFooter: 'Before footer',
    shoppablePlacementAfterProductGrid: 'After product grid',
    shoppablePlacementAfterProductImages: 'After product images',
    shoppablePlacementAfterProductInfo: 'After product info',
    shoppablePlacementBeforeRecommendations: 'Before recommendations',
    shoppablePlacementCustom: 'Custom selector',
    shoppableItems: 'Video items',
    shoppableVideoUrl: 'Video URL',
    shoppableItemTitle: 'Card title',
    shoppableProduct: 'Product',
    shoppableLoadProducts: 'Load products',
    shoppableAddItem: 'Add video',
    shoppableRemoveItem: 'Remove',
    shoppableNoProducts: 'Add and customize your video items.',
    shoppableSaved: 'Shoppable videos saved',
    saveShoppableVideos: 'Save shoppable videos',
    chooseCursorFile: 'Choose cursor file',
    currencySettings: 'Currency settings',
    currencySettingsDescription:
      'Select which currencies can replace VND prices rendered in the storefront.',
    currencyBase: 'Base currency',
    currencyTargets: 'Currencies to show',
    currencySwitcher: 'Show currency switcher',
    currencySwitcherOutsideVietnam: 'Show only outside Vietnam',
    currencySwitcherPosition: 'Switcher position',
    currencyPrimaryColor: 'Switcher primary color',
    currencyTopLeft: 'Top left',
    currencyTopRight: 'Top right',
    currencyBottomLeft: 'Bottom left',
    currencyBottomRight: 'Bottom right',
    currencyShowSymbol: 'Show currency symbol',
    currencyShowCode: 'Show currency code',
    currencyRatesUpdated: 'Exchange rates updated',
    saveCurrency: 'Save currency',
    currencySaved: 'Currency converter saved',
    announcementSettings: 'Announcement settings',
    announcementSettingsDescription:
      'Configure the message, location, colors, link, and countdown shown on the storefront.',
    announcementCampaigns: 'Announcement bars',
    announcementCampaignsDescription:
      'Create and manage the announcement bars available for this store.',
    announcementTabBars: 'Announcement bars',
    announcementTabSettings: 'Settings',
    announcementGlobalSettings: 'General settings',
    announcementGlobalSettingsDescription:
      'Control how multiple active announcement bars behave on the storefront.',
    announcementAllowMultiple: 'Allow multiple bars at the same time',
    announcementDisplayMode: 'Display mode',
    announcementDisplayStacked: 'Stacked',
    announcementDisplayAlternate: 'Alternate',
    announcementRotationSeconds: 'Display time per bar',
    announcementTransition: 'Bar change effect',
    announcementTransitionSlide: 'Slide',
    announcementTransitionNone: 'No effect',
    announcementTransitionFade: 'Fade',
    announcementGlobalSaved: 'Announcement settings saved',
    createAnnouncement: 'Create',
    editAnnouncement: 'Edit',
    backToAnnouncements: 'Back to list',
    noAnnouncementBars: 'No announcement bars yet.',
    announcementStatus: 'Status',
    announcementActive: 'Active',
    announcementInactive: 'Inactive',
    announcementSchedule: 'Schedule',
    announcementAlwaysActive: 'Always active',
    announcementFrom: 'From',
    announcementTo: 'To',
    announcementOpenInNewWindow: 'Open in new window',
    deleteAnnouncement: 'Delete bar',
    announcementDeleteConfirm: 'Delete this announcement bar? This action cannot be undone.',
    announcementDeleted: 'Announcement bar deleted',
    announcementTitle: 'Title',
    announcementSubtitle: 'Subtitle',
    announcementIcon: 'Icon',
    announcementIconSelected: 'Icon selected',
    announcementNoIcon: 'No icon selected',
    announcementChooseIcon: 'Choose icon',
    announcementRemoveIcon: 'Remove icon',
    announcementBackground: 'Background',
    announcementBackgroundEffect: 'Background effect',
    announcementBackgroundEffectSoft: 'Soft shine',
    announcementBackgroundEffectNone: 'No effect',
    announcementBackgroundEffectSpotlight: 'Spotlight',
    announcementTextColor: 'Text color',
    announcementAlign: 'Content alignment',
    announcementAlignLeft: 'Left',
    announcementAlignCenter: 'Center',
    announcementAlignRight: 'Right',
    announcementAlignSliding: 'Sliding',
    announcementBehavior: 'Bar behavior',
    announcementBehaviorFixed: 'Fixed bar',
    announcementBehaviorScrollable: 'Scrollable bar',
    announcementPlacement: 'Placement',
    announcementTop: 'Top',
    announcementBottom: 'Bottom',
    announcementLinkUrl: 'Link URL',
    announcementCta: 'CTA button',
    announcementCtaText: 'CTA text',
    announcementCtaTextColor: 'CTA text color',
    announcementCtaBackground: 'CTA background',
    announcementCtaRadius: 'CTA corner radius',
    announcementCtaBorderSize: 'CTA border size',
    announcementCtaBorderColor: 'CTA border color',
    announcementDismiss: 'Dismiss button',
    announcementDismissOnce: "Don't show again after dismiss",
    announcementCountdown: 'Schedule',
    announcementCountdownStartsAt: 'Schedule start',
    announcementCountdownEndsAt: 'Schedule end',
    announcementShowCountdown: 'Show countdown',
    announcementPreview: 'Announcement preview',
    announcementViews: 'Views',
    announcementClicks: 'Clicks',
    announcementDismisses: 'Dismiss',
    saveAnnouncement: 'Save',
    announcementSaved: 'Announcement saved',
    wheelCampaign: 'Wheel campaign',
    spinTheWheel: 'Spin the Wheel',
    merchantAccess: 'Merchant access',
    storeLogin: 'Store login',
    loginDescription: 'Use the merchant credentials for this Haravan store.',
    haravanSigningInTitle: 'Signing in...',
    haravanSigningInDescription: 'We are connecting your Haravan store. This may take a moment.',
    store: 'Store',
    userCode: 'User code',
    password: 'Password',
    login: 'Login',
    signingIn: 'Signing in...',
    impressions: 'Impressions',
    uniqueImpressions: 'Unique impressions',
    clicks: 'Clicks',
    uniqueClicks: 'Unique clicks',
    campaign: 'Campaign',
    campaignDescription: 'Name and public status.',
    live: 'Live',
    paused: 'Paused',
    campaignName: 'Campaign name',
    activeCampaign: 'Active campaign',
    copy: 'Copy',
    copyDescription: 'Text shown in the storefront popup.',
    title: 'Title',
    subtitle: 'Subtitle',
    successTitle: 'Success title',
    consentLabel: 'Consent label',
    submitLabel: 'Button text',
    spinningLabel: 'Loading button text',
    coupons: 'Coupons',
    couponsDescription: 'Each row becomes one wheel segment.',
    addCoupon: 'New coupon',
    editCoupon: 'Edit',
    deleteCoupon: 'Delete',
    couponEditorTitle: 'Coupon slice',
    createCouponInHaravan:
      'Create coupons from the Discounts section in your Haravan admin, then try again.',
    retryCoupons: 'Retry',
    colors: 'Colors',
    colorsDescription: 'Brand the wheel without touching store CSS.',
    halfWheel: 'Half wheel',
    halfWheelDescription: 'Edit the storefront launcher text and colors.',
    launcherText: 'Launcher text',
    launcherSlice1: 'Slice color 1',
    launcherSlice2: 'Slice color 2',
    launcherSlice3: 'Slice color 3',
    launcherPointerColor: 'Indicator color',
    launcherTextColor: 'Launcher text color',
    primary: 'Primary',
    accent: 'Accent',
    pointer: 'Pointer',
    surface: 'Surface',
    text: 'Text',
    buttonText: 'Button text',
    trigger: 'Trigger',
    triggerDescription: 'Control when the popup appears.',
    delayMs: 'Delay ms',
    showOnceDays: 'Show once days',
    noLeadsYet: 'No leads yet',
    noLeadsDescription: 'Contacts will appear here as soon as customers spin the wheel.',
    collectedContacts: 'Collected contacts',
    latestLeads: 'latest leads from this campaign.',
    total: 'total',
    contact: 'Contact',
    prize: 'Prize',
    coupon: 'Coupon',
    received: 'Received',
    notAssigned: 'Not assigned',
    noCoupon: 'No coupon',
    exportCsv: 'Export CSV',
    printPdf: 'Print PDF',
    checkOrderConversion: 'Check order conversion',
    checkingOrderConversion: 'Checking...',
    haravanApiKeyRequiredForOrders: 'Enter your Haravan API key to unlock this feature.',
    orderConversionChecked:
      'Order conversion checked: {matched} purchased leads from {orders} Haravan orders.',
    purchased: 'Purchased',
    purchaseAmount: 'Purchase amount',
    yes: 'Yes',
    no: 'No',
    itemsPerPage: 'Items per page',
    page: 'Page',
    previous: 'Previous',
    next: 'Next',
    haravanSetup: 'Haravan setup',
    installOnce: 'Install the widget once. Campaign and coupon changes are managed from here.',
    openHaravanAdmin: 'Open your Haravan admin',
    openHaravanAdminBody:
      'Go to Online Store, then open Themes and choose the theme currently published.',
    editThemeCode: 'Edit theme code',
    editThemeCodeBody:
      'Open the theme editor and find the main layout file, usually theme.liquid or layout/theme.liquid.',
    pasteScript: 'Paste the script before </body>',
    pasteScriptBody:
      'Place the widget script near the end of the page, just before the closing body tag.',
    saveAndTest: 'Save and test storefront',
    saveAndTestBody:
      'Open the storefront in an incognito window. The half wheel appears after the configured delay.',
    widgetScript: 'Widget script',
    widgetScriptBody: 'Copy this simple line of code and paste it into the store theme.',
    copied: 'Copied',
    copyScript: 'Copy script',
    verifyInstallation: 'Verify installation',
    verifyingInstallation: 'Checking...',
    installationCheck: 'Installation check',
    installationCheckBody:
      'Check whether the widget script is present on the storefront homepage.',
    installationInstalled: 'Widget installed',
    installationMissing: 'Widget not found',
    installationPrivate: 'Cannot verify private storefront',
    installationCheckedUrl: 'Checked URL',
    installationCheckedAt: 'Checked at',
    installationCheckFailed: 'Unable to verify installation.',
    howItWorks: 'How it works',
    detectsDomain: 'The widget detects the storefront domain automatically.',
    noThemeEdit: 'Changing coupons or colors does not require editing Haravan again.',
    customDomain: 'If the store uses a custom domain, add it as a shop alias before launch.',
    wheelLabel: 'Wheel label',
    couponCode: 'Coupon code',
    selectCoupon: 'Select coupon',
    loadingCoupons: 'Loading Haravan coupons...',
    noHaravanCoupons:
      'No Haravan coupons found. Create coupons in Haravan, then refresh this dashboard.',
    slice: 'Slice',
    weight: 'Weight',
    newCoupon: 'New coupon',
    keepTwoSegments: 'Keep at least two wheel segments.',
    needTwoRewardsToActivate: 'Add at least two coupons before activating Spin the Wheel.',
    emptyCouponCode: 'Each coupon must have a coupon code before saving.',
    saved: 'Saved',
    loginFailed: 'Login failed.',
    unableLoad: 'Unable to load dashboard.',
    unableSave: 'Unable to save.',
    unableCopy: 'Unable to copy automatically. Select and copy the script.',
    unablePrint: 'Unable to open the print window. Allow popups and try again.',
  },
  vi: {
    merchantDashboard: 'Bảng điều khiển',
    access: 'Truy cập',
    landingEyebrow: 'Plugin tăng trưởng cho Haravan',
    landingTitle: 'Dada Plugins',
    landingSubtitle:
      'Chúng tôi xây dựng plugin Haravan giúp merchant tăng doanh thu, thu thập lead chất lượng, tối ưu bán hàng và cải thiện quy trình vận hành.',
    landingHeroCta: 'Mở dashboard',
    requestAccount: 'Yêu cầu tài khoản',
    startNow: 'Bắt đầu ngay',
    requestAccountTitle: 'Yêu cầu tài khoản Dada Plugins',
    requestAccountBody:
      'Gửi thông tin cửa hàng. Chúng tôi sẽ liên hệ sớm trong khi kích hoạt hồ sơ của bạn.',
    storeUrl: 'URL cửa hàng',
    emailAddress: 'Địa chỉ email',
    phoneNumber: 'Số điện thoại',
    sendRequest: 'Gửi yêu cầu',
    accountRequestSuccess:
      'Cảm ơn bạn. Chúng tôi sẽ liên hệ sớm nhất có thể, hồ sơ của bạn đang được kích hoạt.',
    accountRequestFailed: 'Không thể gửi yêu cầu. Vui lòng thử lại.',
    localPhonePlaceholder: '901 234 567',
    landingSecondaryCta: 'Xem plugin',
    landingMetricRevenue: 'Công cụ doanh thu',
    landingMetricLeads: 'Thu thập lead',
    landingMetricOps: 'Quy trình vận hành',
    landingSectionTitle: 'Dành cho cửa hàng muốn tăng trưởng rõ ràng hơn.',
    landingSectionBody:
      'Dada Plugins tập trung vào các công cụ storefront thực tế, triển khai nhanh, phù hợp theme Haravan và giúp merchant hiểu rõ ý định khách hàng.',
    revenueGrowth: 'Tăng doanh thu',
    revenueGrowthBody:
      'Triển khai trải nghiệm storefront giúp chuyển đổi khách truy cập thành người mua bằng ưu đãi đúng thời điểm.',
    leadCapture: 'Thu thập lead',
    leadCaptureBody:
      'Thu thập thông tin khách hàng từ các widget tương tác và xuất dữ liệu để chăm sóc lại.',
    workflowOptimization: 'Tối ưu vận hành',
    workflowOptimizationBody:
      'Giảm thao tác thủ công bằng các công cụ tập trung cho khuyến mãi, phân tích và quy trình cửa hàng.',
    storefrontLayer: 'Lớp storefront',
    storefrontLayerBody: 'Widget tương tác hoạt động tự nhiên bên trong theme Haravan.',
    merchantControls: 'Điều khiển merchant',
    merchantControlsBody: 'Dashboard đơn giản cho chiến dịch, thiết kế, lead và cấu hình.',
    actionableData: 'Dữ liệu hành động',
    actionableDataBody: 'Chỉ số và export hữu ích để chăm sóc lại và tối ưu chiến dịch.',
    workflowTitle: 'Từ sự chú ý của khách đến hành động của merchant.',
    workflowStep1: 'Thu hút khách truy cập bằng trải nghiệm storefront tập trung.',
    workflowStep2: 'Thu thập lead và ghi nhận ý định mua hàng an toàn.',
    workflowStep3: 'Cải thiện chiến dịch bằng analytics và công cụ vận hành.',
    finalCtaTitle: 'Triển khai trải nghiệm Haravan thông minh hơn với Dada Plugins.',
    finalCtaBody: 'Vào dashboard merchant để cấu hình chiến dịch, coupon, màu sắc và lead.',
    landingCta: 'Vào dashboard',
    blog: 'Blog',
    blogEyebrow: 'Playbook tăng trưởng',
    blogSectionTitle: 'Tối ưu chuyển đổi Spin Wheel theo từng tầng phễu.',
    blogSectionBody:
      'Hướng dẫn thực tế để tối ưu impressions, clicks, captures và coupon dispatch đã xác minh cho store Haravan.',
    blogIndexTitle: 'Blog Dada Plugins',
    blogIndexBody:
      'Playbook cho merchant Haravan muốn tăng doanh thu, thu thập lead, tối ưu conversion storefront và quy trình vận hành.',
    readArticle: 'Đọc bài viết',
    backHome: 'Về trang chủ',
    readTime: '8 phút đọc',
    overview: 'Tổng quan',
    leads: 'Khách hàng',
    customCursor: 'Con trỏ tùy chỉnh',
    currencyConverter: 'Chuyển đổi tiền tệ',
    announcementBar: 'Thanh thông báo',
    shoppableVideos: 'Video mua sắm',
    installation: 'Cài đặt',
    settings: 'Thiết lập',
    dashboardSections: 'Các phần dashboard',
    language: 'Ngôn ngữ',
    logout: 'Đăng xuất',
    saveChanges: 'Lưu thay đổi',
    saving: 'Đang lưu...',
    previewWidget: 'Xem trước widget',
    hidePreview: 'Ẩn xem trước',
    collapseSidebar: 'Thu gọn menu',
    expandSidebar: 'Mở rộng menu',
    loadingDashboard: 'Đang tải bảng điều khiển...',
    analyticsPeriod: 'Khoảng thời gian analytics',
    period: 'Khoảng thời gian',
    today: 'Hôm nay',
    yesterday: 'Hôm qua',
    last7Days: '7 ngày qua',
    last30Days: '30 ngày qua',
    customRange: 'Tùy chỉnh',
    fromDate: 'Từ',
    toDate: 'Đến',
    overviewDescription:
      'Cấu hình nội dung, màu sắc, mã coupon, trọng số và trạng thái chiến dịch.',
    leadsDescription: 'Xem các liên hệ đã thu thập và coupon mà từng khách hàng nhận được.',
    customCursorDescription: 'Bật cursor thương hiệu trên storefront.',
    currencyConverterDescription:
      'Chuyển giá VND trên storefront sang các tiền tệ đã chọn với tùy chọn tự động theo IP.',
    announcementBarDescription: 'Hiển thị thanh thông báo có thể bấm với countdown tùy chọn.',
    shoppableVideosDescription:
      'Tạo khu vực xem video và mua hàng với sản phẩm được liên kết.',
    productCountdown: 'Đếm ngược sản phẩm',
    productCountdownDescription:
      'Hiển thị countdown riêng cho sản phẩm bên cạnh tên sản phẩm trên trang chi tiết.',
    productCountdownList: 'Countdown sản phẩm',
    productCountdownListDescription: 'Tạo countdown gắn với từng sản phẩm Haravan.',
    productCountdownCreate: 'Tạo',
    productCountdownEdit: 'Chỉnh sửa',
    productCountdownBack: 'Quay lại danh sách',
    productCountdownDelete: 'Xóa countdown',
    productCountdownDeleteConfirm: 'Xóa countdown sản phẩm này? Hành động này không thể hoàn tác.',
    productCountdownDeleted: 'Đã xóa countdown sản phẩm',
    productCountdownSaved: 'Đã lưu countdown sản phẩm',
    productCountdownNoItems: 'Chưa có countdown sản phẩm.',
    productCountdownSettings: 'Thiết lập countdown',
    productCountdownProduct: 'Sản phẩm',
    productCountdownStart: 'Ngày bắt đầu',
    productCountdownEnd: 'Ngày kết thúc',
    productCountdownAlwaysActive: 'Luôn bật cho đến ngày kết thúc',
    productCountdownSelectProduct: 'Chọn sản phẩm',
    productCountdownNoProduct: 'Chưa chọn sản phẩm',
    productCountdownSchedule: 'Lịch hiển thị',
    productCountdownPosition: 'Vị trí',
    productCountdownPositionRight: 'Bên phải tiêu đề',
    productCountdownPositionLeft: 'Bên trái tiêu đề',
    productCountdownPositionAbove: 'Phía trên tiêu đề',
    productCountdownPositionBelow: 'Phía dưới tiêu đề',
    productCountdownBackgroundColor: 'Màu nền',
    productCountdownTextColor: 'Màu chữ',
    productCountdownBorderRadius: 'Bo góc',
    productCountdownBorderWidth: 'Độ dày viền',
    productCountdownBorderColor: 'Màu viền',
    saveProductCountdown: 'Lưu countdown sản phẩm',
    shoppableTabSettings: 'Thiết lập',
    shoppableTabVideos: 'Video',
    shoppableCarousels: 'Carousel video mua sắm',
    shoppableCarouselsDescription: 'Tạo và quản lý carousel video cho từng trang storefront.',
    shoppableCreateCarousel: 'Tạo',
    shoppableNoCarousels: 'Chưa có carousel video mua sắm.',
    shoppableEditCarousel: 'Chỉnh sửa',
    shoppableBackToCarousels: 'Quay lại danh sách',
    shoppableDeleteCarousel: 'Xóa carousel',
    shoppableDeleteConfirm: 'Xóa carousel video mua sắm này? Hành động này không thể hoàn tác.',
    shoppableDeleted: 'Đã xóa carousel video mua sắm',
    shoppablePageUrl: 'URL trang',
    installationDescription: 'Thêm vòng quay vào storefront Haravan bằng một dòng script nhẹ.',
    settingsDescription: 'Quản lý quyền truy cập merchant và API key Haravan riêng tư.',
    connectionSettingsDescription: 'Kiểm tra kết nối Haravan OAuth cho cửa hàng này.',
    accountSettings: 'Thiết lập tài khoản',
    accountSettingsDescription:
      'Username được gán cho cửa hàng này và không thể chỉnh sửa tại đây.',
    username: 'Tên người dùng',
    passwordSettings: 'Mật khẩu',
    passwordSettingsDescription: 'Đổi mật khẩu dashboard cho merchant user này.',
    changePassword: 'Đổi mật khẩu',
    currentPassword: 'Mật khẩu hiện tại',
    newPassword: 'Mật khẩu mới',
    confirmNewPassword: 'Xác nhận mật khẩu mới',
    savePassword: 'Lưu mật khẩu',
    cancel: 'Hủy',
    close: 'Đóng',
    loading: 'Đang tải...',
    passwordMismatch: 'Mật khẩu mới và xác nhận không khớp.',
    apiSettings: 'Haravan API key',
    apiSettingsDescription: 'Lưu API key Haravan riêng tư dùng cho tích hợp backend.',
    haravanApiKey: 'Haravan API key',
    haravanApiKeyPlaceholder: 'Dán API key mới',
    apiKeySaved: 'Đã lưu API key',
    apiKeyNotSet: 'Chưa có API key',
    saveSettings: 'Lưu thiết lập',
    settingsSaved: 'Đã lưu thiết lập',
    haravanConnection: 'Kết nối Haravan',
    connectedViaHaravan: 'Đã kết nối qua Haravan OAuth',
    connectedStore: 'Cửa hàng đã kết nối',
    grantedScopes: 'Scope đã cấp',
    scriptTagStatus: 'Script widget',
    scriptTagInstalled: 'Đã cài tự động',
    scriptTagNotInstalled: 'Chưa cài tự động',
    cursorSettings: 'Thiết lập cursor',
    cursorSettingsDescription:
      'Tải PNG, WebP, GIF, SVG hoặc JPG nhỏ. Nếu chưa chọn file, cửa hàng vẫn dùng cursor mặc định.',
    activePlugin: 'Plugin đang bật',
    cursorFile: 'File cursor',
    currentCursorFile: 'File hiện tại',
    noCursorFile: 'Chưa chọn file cursor',
    cursorFileSelected: 'Đã chọn cursor',
    removeCursorFile: 'Xóa file',
    saveCursor: 'Lưu cursor',
    cursorSaved: 'Đã lưu cursor',
    cursorPreview: 'Xem trước cursor',
    shoppableSettings: 'Thiết lập video mua sắm',
    shoppableSettingsDescription:
      'Quản lý thiết lập chung của plugin, bao gồm giao diện và cách hiển thị trên storefront.',
    shoppableTitle: 'Tiêu đề khu vực',
    shoppableCardStyle: 'Kiểu thẻ',
    shoppableCornerRadius: 'Bo góc',
    shoppableBorderSize: 'Độ dày viền',
    shoppableBorderColor: 'Màu viền',
    shoppablePlaceholderColor: 'Màu placeholder thẻ',
    shoppableProductBarStyle: 'Màu thanh sản phẩm',
    shoppableProductBarPrimary: 'Màu chính',
    shoppableProductBarSecondary: 'Màu phụ',
    shoppableProductBarBackground: 'Màu nền',
    shoppableVisibility: 'Hiển thị',
    shoppableShowHome: 'Hiển thị trên trang chủ',
    shoppableShowProducts: 'Hiển thị trên trang sản phẩm',
    shoppableShowProductDetails: 'Hiển thị trên trang chi tiết sản phẩm',
    shoppablePlacement: 'Vị trí hiển thị',
    shoppablePlacementHome: 'Vị trí trang chủ',
    shoppablePlacementProducts: 'Vị trí trang sản phẩm',
    shoppablePlacementProductDetails: 'Vị trí trang chi tiết sản phẩm',
    shoppablePlacementCustomSelector: 'CSS selector tùy chỉnh',
    shoppableScanSections: 'Quét trang',
    shoppableScanningSections: 'Đang quét...',
    shoppableSectionPlacement: 'Khu vực tìm thấy',
    shoppableBeforeSection: 'Trước',
    shoppableAfterSection: 'Sau',
    shoppableNoSectionsFound: 'Không tìm thấy khu vực. Hãy dùng selector tùy chỉnh.',
    shoppableSectionsError: 'Không thể quét trang này.',
    shoppablePlacementAuto: 'Tự động',
    shoppablePlacementTop: 'Đầu trang',
    shoppablePlacementAfterHero: 'Sau hero',
    shoppablePlacementBeforeFooter: 'Trước footer',
    shoppablePlacementAfterProductGrid: 'Sau lưới sản phẩm',
    shoppablePlacementAfterProductImages: 'Sau ảnh sản phẩm',
    shoppablePlacementAfterProductInfo: 'Sau thông tin sản phẩm',
    shoppablePlacementBeforeRecommendations: 'Trước gợi ý sản phẩm',
    shoppablePlacementCustom: 'Selector tùy chỉnh',
    shoppableItems: 'Video',
    shoppableVideoUrl: 'URL video',
    shoppableItemTitle: 'Tiêu đề thẻ',
    shoppableProduct: 'Sản phẩm',
    shoppableLoadProducts: 'Tải sản phẩm',
    shoppableAddItem: 'Thêm video',
    shoppableRemoveItem: 'Xóa',
    shoppableNoProducts: 'Thêm và tùy chỉnh các video.',
    shoppableSaved: 'Đã lưu video mua sắm',
    saveShoppableVideos: 'Lưu video mua sắm',
    chooseCursorFile: 'Chọn file cursor',
    currencySettings: 'Thiết lập tiền tệ',
    currencySettingsDescription:
      'Chọn tiền tệ có thể thay thế giá VND đang hiển thị trên storefront.',
    currencyBase: 'Tiền tệ gốc',
    currencyTargets: 'Tiền tệ hiển thị',
    currencySwitcher: 'Hiển thị bộ đổi tiền tệ',
    currencySwitcherOutsideVietnam: 'Chỉ hiển thị ngoài Việt Nam',
    currencySwitcherPosition: 'Vị trí bộ đổi tiền tệ',
    currencyPrimaryColor: 'Màu chính bộ đổi tiền tệ',
    currencyTopLeft: 'Trên trái',
    currencyTopRight: 'Trên phải',
    currencyBottomLeft: 'Dưới trái',
    currencyBottomRight: 'Dưới phải',
    currencyShowSymbol: 'Hiển thị ký hiệu tiền tệ',
    currencyShowCode: 'Hiển thị mã tiền tệ',
    currencyRatesUpdated: 'Tỷ giá cập nhật',
    saveCurrency: 'Lưu tiền tệ',
    currencySaved: 'Đã lưu currency converter',
    announcementSettings: 'Thiết lập thông báo',
    announcementSettingsDescription:
      'Cấu hình nội dung, vị trí, màu sắc, link và countdown hiển thị trên storefront.',
    announcementCampaigns: 'Thanh thông báo',
    announcementCampaignsDescription:
      'Tạo và quản lý các thanh thông báo có sẵn cho cửa hàng này.',
    announcementTabBars: 'Thanh thông báo',
    announcementTabSettings: 'Cài đặt',
    announcementGlobalSettings: 'Cài đặt chung',
    announcementGlobalSettingsDescription:
      'Điều khiển cách nhiều thanh thông báo đang bật hiển thị trên storefront.',
    announcementAllowMultiple: 'Cho phép nhiều thanh cùng lúc',
    announcementDisplayMode: 'Chế độ hiển thị',
    announcementDisplayStacked: 'Xếp chồng',
    announcementDisplayAlternate: 'Luân phiên',
    announcementRotationSeconds: 'Thời gian hiển thị mỗi thanh',
    announcementTransition: 'Hiệu ứng đổi thanh',
    announcementTransitionSlide: 'Trượt',
    announcementTransitionNone: 'Không hiệu ứng',
    announcementTransitionFade: 'Mờ dần',
    announcementGlobalSaved: 'Đã lưu cài đặt thông báo',
    createAnnouncement: 'Tạo',
    editAnnouncement: 'Chỉnh sửa',
    backToAnnouncements: 'Quay lại danh sách',
    noAnnouncementBars: 'Chưa có thanh thông báo.',
    announcementStatus: 'Trạng thái',
    announcementActive: 'Đang bật',
    announcementInactive: 'Đang tắt',
    announcementSchedule: 'Lịch chạy',
    announcementAlwaysActive: 'Luôn hoạt động',
    announcementFrom: 'Từ',
    announcementTo: 'Đến',
    announcementOpenInNewWindow: 'Mở trong cửa sổ mới',
    deleteAnnouncement: 'Xóa thanh',
    announcementDeleteConfirm: 'Xóa thanh thông báo này? Hành động này không thể hoàn tác.',
    announcementDeleted: 'Đã xóa thanh thông báo',
    announcementTitle: 'Tiêu đề',
    announcementSubtitle: 'Phụ đề',
    announcementIcon: 'Icon',
    announcementIconSelected: 'Đã chọn icon',
    announcementNoIcon: 'Chưa chọn icon',
    announcementChooseIcon: 'Chọn icon',
    announcementRemoveIcon: 'Xóa icon',
    announcementBackground: 'Nền',
    announcementBackgroundEffect: 'Hiệu ứng nền',
    announcementBackgroundEffectSoft: 'Ánh sáng mềm',
    announcementBackgroundEffectNone: 'Không hiệu ứng',
    announcementBackgroundEffectSpotlight: 'Spotlight',
    announcementTextColor: 'Màu chữ',
    announcementAlign: 'Căn nội dung',
    announcementAlignLeft: 'Trái',
    announcementAlignCenter: 'Giữa',
    announcementAlignRight: 'Phải',
    announcementAlignSliding: 'Chạy ngang',
    announcementBehavior: 'Kiểu thanh',
    announcementBehaviorFixed: 'Cố định',
    announcementBehaviorScrollable: 'Cuộn theo trang',
    announcementPlacement: 'Vị trí',
    announcementTop: 'Trên',
    announcementBottom: 'Dưới',
    announcementLinkUrl: 'Link URL',
    announcementCta: 'Nút CTA',
    announcementCtaText: 'Nội dung CTA',
    announcementCtaTextColor: 'Màu chữ CTA',
    announcementCtaBackground: 'Nền CTA',
    announcementCtaRadius: 'Bo góc CTA',
    announcementCtaBorderSize: 'Độ dày viền CTA',
    announcementCtaBorderColor: 'Màu viền CTA',
    announcementDismiss: 'Nút đóng',
    announcementDismissOnce: 'Không hiển thị lại sau khi đóng',
    announcementCountdown: 'Lịch hiển thị',
    announcementCountdownStartsAt: 'Thời gian bắt đầu',
    announcementCountdownEndsAt: 'Thời gian kết thúc',
    announcementShowCountdown: 'Hiển thị countdown',
    announcementPreview: 'Xem trước thông báo',
    announcementViews: 'Lượt xem',
    announcementClicks: 'Lượt click',
    announcementDismisses: 'Lượt đóng',
    saveAnnouncement: 'Lưu',
    announcementSaved: 'Đã lưu thông báo',
    wheelCampaign: 'Chiến dịch vòng quay',
    spinTheWheel: 'Vòng quay may mắn',
    merchantAccess: 'Truy cập merchant',
    storeLogin: 'Đăng nhập cửa hàng',
    loginDescription: 'Dùng thông tin đăng nhập merchant cho cửa hàng Haravan này.',
    haravanSigningInTitle: 'Đang đăng nhập...',
    haravanSigningInDescription: 'Chúng tôi đang kết nối cửa hàng Haravan của bạn.',
    store: 'Cửa hàng',
    userCode: 'Mã người dùng',
    password: 'Mật khẩu',
    login: 'Đăng nhập',
    signingIn: 'Đang đăng nhập...',
    impressions: 'Lượt hiển thị',
    uniqueImpressions: 'Hiển thị duy nhất',
    clicks: 'Lượt mở',
    uniqueClicks: 'Lượt mở duy nhất',
    campaign: 'Chiến dịch',
    campaignDescription: 'Tên và trạng thái hiển thị.',
    live: 'Đang chạy',
    paused: 'Tạm dừng',
    campaignName: 'Tên chiến dịch',
    activeCampaign: 'Chiến dịch đang bật',
    copy: 'Nội dung',
    copyDescription: 'Nội dung hiển thị trong popup storefront.',
    title: 'Tiêu đề',
    subtitle: 'Mô tả',
    successTitle: 'Tiêu đề khi trúng',
    consentLabel: 'Nội dung đồng ý',
    submitLabel: 'Chữ nút',
    spinningLabel: 'Chữ nút khi tải',
    coupons: 'Coupon',
    couponsDescription: 'Mỗi dòng là một phần trên vòng quay.',
    addCoupon: 'Thêm coupon',
    editCoupon: 'Chỉnh sửa',
    deleteCoupon: 'Xóa',
    couponEditorTitle: 'Lát coupon',
    createCouponInHaravan:
      'Hãy tạo coupon trong mục Discounts của Haravan admin, rồi thử lại.',
    retryCoupons: 'Thử lại',
    colors: 'Màu sắc',
    colorsDescription: 'Tùy chỉnh giao diện vòng quay mà không ảnh hưởng CSS của store.',
    halfWheel: 'Nửa vòng quay',
    halfWheelDescription: 'Chỉnh chữ và màu của nút launcher trên storefront.',
    launcherText: 'Chữ launcher',
    launcherSlice1: 'Màu spicchio 1',
    launcherSlice2: 'Màu spicchio 2',
    launcherSlice3: 'Màu spicchio 3',
    launcherPointerColor: 'Màu chỉ báo',
    launcherTextColor: 'Màu chữ launcher',
    primary: 'Chính',
    accent: 'Nhấn',
    pointer: 'Mũi tên',
    surface: 'Nền',
    text: 'Chữ',
    buttonText: 'Chữ nút',
    trigger: 'Hiển thị',
    triggerDescription: 'Kiểm soát thời điểm popup xuất hiện.',
    delayMs: 'Độ trễ ms',
    showOnceDays: 'Hiện lại sau ngày',
    noLeadsYet: 'Chưa có khách hàng',
    noLeadsDescription: 'Liên hệ sẽ xuất hiện ở đây khi khách hàng quay vòng quay.',
    collectedContacts: 'Liên hệ đã thu thập',
    latestLeads: 'liên hệ mới nhất từ chiến dịch này.',
    total: 'tổng',
    contact: 'Liên hệ',
    prize: 'Phần thưởng',
    coupon: 'Coupon',
    received: 'Thời gian',
    notAssigned: 'Chưa gán',
    noCoupon: 'Không có coupon',
    exportCsv: 'Xuất CSV',
    printPdf: 'In PDF',
    checkOrderConversion: 'Kiểm tra chuyển đổi đơn hàng',
    checkingOrderConversion: 'Đang kiểm tra...',
    haravanApiKeyRequiredForOrders: 'Nhập Haravan API key để mở khóa tính năng này.',
    orderConversionChecked:
      'Đã kiểm tra chuyển đổi: {matched} lead đã mua từ {orders} đơn hàng Haravan.',
    purchased: 'Đã mua',
    purchaseAmount: 'Giá trị mua',
    yes: 'Có',
    no: 'Không',
    itemsPerPage: 'Số dòng mỗi trang',
    page: 'Trang',
    previous: 'Trước',
    next: 'Tiếp',
    haravanSetup: 'Cài đặt Haravan',
    installOnce: 'Chỉ cần cài widget một lần. Campaign và coupon được quản lý tại đây.',
    openHaravanAdmin: 'Mở Haravan admin',
    openHaravanAdminBody: 'Vào Online Store, mở Themes và chọn theme đang được publish.',
    editThemeCode: 'Chỉnh sửa code theme',
    editThemeCodeBody:
      'Mở trình chỉnh sửa theme và tìm file layout chính, thường là theme.liquid hoặc layout/theme.liquid.',
    pasteScript: 'Dán script trước </body>',
    pasteScriptBody: 'Đặt script widget gần cuối trang, ngay trước thẻ đóng body.',
    saveAndTest: 'Lưu và kiểm tra storefront',
    saveAndTestBody:
      'Mở storefront bằng cửa sổ ẩn danh. Nửa vòng quay sẽ xuất hiện sau độ trễ đã cấu hình.',
    widgetScript: 'Script widget',
    widgetScriptBody: 'Sao chép dòng code đơn giản này và dán vào theme của cửa hàng.',
    copied: 'Đã sao chép',
    copyScript: 'Sao chép script',
    verifyInstallation: 'Kiểm tra cài đặt',
    verifyingInstallation: 'Đang kiểm tra...',
    installationCheck: 'Kiểm tra cài đặt',
    installationCheckBody:
      'Kiểm tra script widget có xuất hiện trên homepage storefront hay không.',
    installationInstalled: 'Widget đã được cài',
    installationMissing: 'Không tìm thấy widget',
    installationPrivate: 'Không thể kiểm tra storefront riêng tư',
    installationCheckedUrl: 'URL đã kiểm tra',
    installationCheckedAt: 'Thời gian kiểm tra',
    installationCheckFailed: 'Không thể kiểm tra cài đặt.',
    howItWorks: 'Cách hoạt động',
    detectsDomain: 'Widget tự động nhận diện domain storefront.',
    noThemeEdit: 'Thay đổi coupon hoặc màu sắc không cần chỉnh Haravan lại.',
    customDomain: 'Nếu store dùng custom domain, hãy thêm domain đó làm alias trước khi launch.',
    wheelLabel: 'Tên trên vòng quay',
    couponCode: 'Mã coupon',
    selectCoupon: 'Chọn coupon',
    loadingCoupons: 'Đang tải coupon Haravan...',
    noHaravanCoupons: 'Không tìm thấy coupon Haravan. Hãy tạo coupon trong Haravan rồi tải lại dashboard.',
    slice: 'Màu lát',
    weight: 'Trọng số',
    newCoupon: 'Coupon mới',
    keepTwoSegments: 'Cần giữ ít nhất hai phần trên vòng quay.',
    needTwoRewardsToActivate: 'Cần ít nhất hai coupon trước khi bật Spin the Wheel.',
    emptyCouponCode: 'Mỗi coupon cần có mã coupon trước khi lưu.',
    saved: 'Đã lưu',
    loginFailed: 'Đăng nhập thất bại.',
    unableLoad: 'Không thể tải dashboard.',
    unableSave: 'Không thể lưu.',
    unableCopy: 'Không thể tự động sao chép. Hãy chọn và sao chép script.',
    unablePrint: 'Không thể mở cửa sổ in. Hãy cho phép popup và thử lại.',
  },
} as const;

function createRewardDraft(discount?: HaravanDiscountOption): RewardDraft {
  return {
    label: discount?.label || '',
    code: discount?.code || '',
    color: '#ffffff',
    probability: 10,
    reusable: true,
    remainingUses: null,
  };
}

@customElement('spin-dashboard-app')
export class SpinDashboardApp extends LitElement {
  @state() private session: DashboardSession | null = readSession();
  @state() private data?: DashboardData;
  @state() private settings?: DashboardSettings;
  @state() private rewards: RewardDraft[] = [];
  @state() private rewardModalOpen = false;
  @state() private rewardModalIndex: number | null = null;
  @state() private rewardModalDraft: RewardDraft = createRewardDraft();
  @state() private haravanDiscounts: HaravanDiscountOption[] = [];
  @state() private loadingHaravanDiscounts = false;
  @state() private haravanDiscountsError = '';
  @state() private shopDomain = import.meta.env.VITE_DEFAULT_SHOP_DOMAIN || '';
  @state() private username = '';
  @state() private password = '';
  @state() private activeView: DashboardView = 'spinWheel';
  @state() private activeSpinView: SpinWheelView = 'overview';
  @state() private activeShoppableVideosView: ShoppableVideosView = 'settings';
  @state() private sidebarCollapsed = readSidebarCollapsed();
  @state() private language: DashboardLanguage = readLanguage();
  @state() private loading = false;
  @state() private saving = false;
  @state() private checkingOrderConversions = false;
  @state() private haravanAutoLoginLoading = false;
  @state() private error = '';
  @state() private saved = '';
  @state() private installCopied = false;
  @state() private checkingInstallation = false;
  @state() private installationCheck?: InstallationCheckResult;
  @state() private previewVisible = false;
  @state() private route: AppRoute = getCurrentRoute();
  @state() private analyticsPeriod: AnalyticsPeriod = 'today';
  @state() private activeAnnouncementView: AnnouncementBarView = 'bars';
  @state() private customAnalyticsFrom = toDateInputValue(new Date());
  @state() private customAnalyticsTo = toDateInputValue(new Date());
  @state() private leadsPage = 1;
  @state() private leadsPageSize = 25;
  @state() private leadsSortKey: LeadsSortKey = 'received';
  @state() private leadsSortDirection: SortDirection = 'desc';
  @state() private passwordModalOpen = false;
  @state() private currentPassword = '';
  @state() private newPassword = '';
  @state() private confirmPassword = '';
  @state() private haravanApiKey = '';
  @state() private apiKeyVisible = false;
  @state() private customCursor: CustomCursorSettings = {
    active: false,
    cursorUrl: '',
    fileName: '',
  };
  @state() private currencyConverter: CurrencyConverterSettings = {
    active: false,
    baseCurrency: 'VND',
    targetCurrencies: [...supportedDashboardCurrencies],
    autoConvertByIp: true,
    showCurrencySwitcher: false,
    showSwitcherOnlyOutsideVietnam: false,
    switcherPosition: 'bottom-right',
    switcherPrimaryColor: '#0f766e',
    showCurrencySymbol: true,
    showCurrencyCode: true,
    rates: {
      ...currencyRateFallbacks,
    },
  };
  @state() private announcementBar: AnnouncementBarSettings = {
    id: 'announcement-1',
    active: false,
    title: 'Limited offer',
    subtitle: 'Unlock a special reward today.',
    iconUrl: '',
    iconFileName: '',
    backgroundColor: '#111827',
    backgroundEffect: 'soft',
    textColor: '#ffffff',
    alignment: 'center',
    behavior: 'fixed',
    placement: 'top',
    linkUrl: '',
    openInNewWindow: true,
    ctaEnabled: false,
    ctaText: 'Shop now',
    ctaTextColor: '#111827',
    ctaBackgroundColor: '#ffffff',
    ctaCornerRadius: 50,
    ctaBorderSize: 0,
    ctaBorderColor: '#ffffff',
    dismissEnabled: false,
    dismissOnce: false,
    countdownEnabled: false,
    showCountdown: true,
    countdownStartsAt: '',
    countdownEndsAt: '',
  };
  @state() private announcementBars: AnnouncementBarSettings[] = [];
  @state() private announcementBarSettings: AnnouncementBarsGlobalSettings = {
    allowMultiple: false,
    displayMode: 'stacked',
    rotationSeconds: 6,
    transition: 'slide',
  };
  @state() private activeAnnouncementBarId = '';
  @state() private announcementDeleteModalOpen = false;
  @state() private shoppableVideos: ShoppableVideosSettings = {
    id: 'shoppable-1',
    active: false,
    title: 'Watch and buy',
    pageUrl: '/',
    cardCornerRadius: 20,
    cardBorderSize: 0,
    cardBorderColor: '#111827',
    cardPlaceholderColor: '#111827',
    productBarPrimaryColor: '#000000',
    productBarSecondaryColor: '#4b5563',
    productBarBackgroundColor: '#ffffff',
    placement: { preset: 'after-hero', customSelector: '', customMode: 'after' },
    items: [],
    carousels: [],
    activeCarouselId: '',
  };
  @state() private shoppableCarousels: ShoppableVideoCarousel[] = [];
  @state() private activeShoppableCarouselId = '';
  @state() private shoppableCarouselDraft = false;
  @state() private shoppableDeleteModalOpen = false;
  @state() private shoppableProducts: HaravanProductOption[] = [];
  @state() private loadingShoppableProducts = false;
  @state() private shoppablePageSections: ShoppablePageSection[] = [];
  @state() private loadingShoppableSections = false;
  @state() private shoppableSectionsError = '';
  @state() private openShoppableProductPickerIndex: number | null = null;
  @state() private productCountdown: ProductCountdownSettings = {
    items: [],
    activeItemId: '',
  };
  @state() private activeProductCountdownId = '';
  @state() private productCountdownDraft = false;
  @state() private productCountdownDeleteModalOpen = false;
  @state() private productCountdownPickerOpen = false;
  @state() private cursorFile?: { name: string; type: string; dataUrl: string };
  @state() private cursorClearFile = false;
  @state() private announcementIconFile?: { name: string; type: string; dataUrl: string };
  @state() private announcementClearIcon = false;
  @state() private accountModalOpen = false;
  @state() private accountStoreUrl = '';
  @state() private accountEmail = '';
  @state() private accountPhone = '';
  @state() private accountRequestLoading = false;
  @state() private accountRequestSuccess = false;
  @state() private accountRequestError = '';
  private sessionExpiryTimer?: number;
  private haravanAutoLoginAttempted = false;
  private announcementPreviewTimer?: number;

  static styles = css`
    :host {
      --brand: #2563eb;
      --brand-dark: #1d4ed8;
      --brand-soft: rgba(37, 99, 235, 0.1);
      --ink: #101828;
      --muted: #667085;
      --surface: rgba(255, 255, 255, 0.82);
      display: block;
      min-height: 100vh;
      color: var(--ink);
      background:
        radial-gradient(circle at 12% 8%, rgba(37, 99, 235, 0.13), transparent 32rem),
        radial-gradient(circle at 86% 14%, rgba(20, 184, 166, 0.12), transparent 30rem),
        linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%);
      font-family:
        Inter,
        ui-sans-serif,
        system-ui,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        sans-serif;
    }

    * {
      box-sizing: border-box;
    }

    button,
    input,
    select {
      font: inherit;
    }

    button:focus-visible,
    input:focus-visible,
    select:focus-visible {
      outline: 3px solid rgba(37, 99, 235, 0.2);
      outline-offset: 2px;
    }

    .login {
      min-height: 100vh;
      min-height: 100dvh;
      display: grid;
      place-items: center;
      padding: clamp(18px, 5vw, 32px);
    }

    .loginPanel,
    .panel,
    .metric {
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 18px;
      background: rgba(255, 255, 255, 0.84);
      box-shadow: 0 22px 70px rgba(18, 38, 32, 0.08);
      backdrop-filter: blur(18px);
    }

    .loginPanel {
      width: min(460px, 100%);
      padding: clamp(24px, 5vw, 34px);
    }

    .authSpinner {
      width: 34px;
      height: 34px;
      border-radius: 999px;
      border: 3px solid rgba(37, 99, 235, 0.16);
      border-top-color: var(--brand);
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    .landing {
      min-height: 100vh;
      min-height: 100dvh;
      display: grid;
      grid-template-rows: auto;
      overflow: hidden;
    }

    .landingNav {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      width: min(1180px, calc(100% - 40px));
      margin: 0 auto;
      padding: 24px 0;
      backdrop-filter: blur(18px);
    }

    .landingActions {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .landingNav .brand {
      margin-bottom: 0;
    }

    a.brand {
      color: inherit;
      text-decoration: none;
    }

    .accessButton {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 38px;
      border-radius: 999px;
      padding: 0 16px;
      font-size: 13px;
      background: var(--brand);
      color: #ffffff;
      font-weight: 850;
      text-decoration: none;
    }

    .navLink {
      display: inline-flex;
      align-items: center;
      min-height: 38px;
      border-radius: 999px;
      color: #344054;
      font-size: 13px;
      font-weight: 850;
      padding: 0 12px;
      text-decoration: none;
    }

    .navLink:hover {
      background: rgba(37, 99, 235, 0.08);
      color: var(--brand-dark);
    }

    .landingSection {
      display: grid;
      width: min(1180px, calc(100% - 40px));
      margin: 0 auto;
    }

    .landingHero {
      grid-template-columns: minmax(0, 1fr) minmax(360px, 0.82fr);
      align-items: center;
      gap: 56px;
      padding: 76px 0 88px;
    }

    .landingHero h1 {
      max-width: 760px;
      font-size: clamp(52px, 7vw, 96px);
      line-height: 0.94;
    }

    .landingHeroCopy > p:not(.eyebrow) {
      max-width: 690px;
      margin-top: 22px;
      font-size: clamp(18px, 2vw, 22px);
    }

    .landingCtas {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 30px;
    }

    .landingCtas button {
      min-height: 48px;
      padding: 0 20px;
      background: var(--brand);
    }

    .landingCtas .secondary {
      background: rgba(255, 255, 255, 0.8);
      color: var(--ink);
    }

    .heroMockup {
      position: relative;
      display: grid;
      gap: 14px;
      border: 1px solid rgba(37, 99, 235, 0.12);
      border-radius: 28px;
      background:
        linear-gradient(145deg, rgba(255, 255, 255, 0.92), rgba(239, 246, 255, 0.74)), #ffffff;
      padding: 22px;
      box-shadow: 0 34px 90px rgba(37, 99, 235, 0.18);
    }

    .heroMockup::before {
      position: absolute;
      inset: -26px -24px auto auto;
      width: 104px;
      height: 104px;
      border-radius: 32px;
      background: #14b8a6;
      opacity: 0.18;
      content: '';
      transform: rotate(12deg);
    }

    .mockupTop {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: center;
    }

    .mockupPill {
      display: inline-flex;
      align-items: center;
      min-height: 30px;
      border-radius: 999px;
      background: var(--brand-soft);
      color: var(--brand-dark);
      padding: 0 11px;
      font-size: 12px;
      font-weight: 850;
    }

    .mockupWheel {
      width: 154px;
      aspect-ratio: 1;
      place-self: center;
      border-radius: 999px;
      background: conic-gradient(
        from -35deg,
        #2563eb 0 30%,
        #14b8a6 0 56%,
        #f59e0b 0 74%,
        #ffffff 0
      );
      box-shadow:
        inset 0 0 0 10px #ffffff,
        inset 0 0 0 17px rgba(16, 24, 40, 0.08),
        0 24px 42px rgba(37, 99, 235, 0.18);
    }

    .mockupRows {
      display: grid;
      gap: 10px;
    }

    .mockupRow {
      display: grid;
      grid-template-columns: 1fr auto;
      gap: 12px;
      align-items: center;
      border-radius: 16px;
      background: rgba(248, 250, 252, 0.86);
      padding: 12px;
      color: var(--muted);
      font-size: 13px;
      font-weight: 760;
    }

    .mockupRow strong {
      color: var(--ink);
      font-size: 16px;
    }

    .landingMetrics {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 14px;
      padding: 0 0 72px;
    }

    .landingMetric {
      border-top: 1px solid rgba(16, 24, 40, 0.12);
      padding-top: 18px;
    }

    .landingMetric strong {
      display: block;
      color: var(--brand);
      font-size: 15px;
    }

    .landingMetric span {
      display: block;
      margin-top: 7px;
      color: var(--muted);
      font-size: 14px;
      line-height: 1.45;
    }

    .landingBand {
      background: rgba(255, 255, 255, 0.48);
      border-block: 1px solid rgba(16, 24, 40, 0.06);
    }

    .landingSplit {
      grid-template-columns: minmax(0, 0.82fr) minmax(0, 1fr);
      gap: 40px;
      padding: 76px 0;
      align-items: start;
    }

    .sectionIntro h2 {
      max-width: 520px;
      font-size: clamp(32px, 4vw, 52px);
      line-height: 1;
    }

    .sectionIntro p {
      max-width: 560px;
      margin-top: 18px;
      font-size: 17px;
    }

    .landingGrid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;
    }

    .landingCard {
      min-width: 0;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 18px;
      background: rgba(255, 255, 255, 0.76);
      padding: 22px;
      box-shadow: 0 16px 46px rgba(15, 23, 42, 0.05);
      backdrop-filter: blur(18px);
    }

    .landingCardMark {
      display: grid;
      place-items: center;
      width: 38px;
      height: 38px;
      border-radius: 14px;
      background: var(--brand-soft);
      color: var(--brand);
      font-size: 15px;
      font-weight: 900;
      margin-bottom: 18px;
    }

    .landingCard h2 {
      font-size: 19px;
    }

    .landingCard p {
      margin-top: 10px;
      font-size: 15px;
    }

    .workflow {
      padding: 82px 0;
    }

    .workflowPanel {
      display: grid;
      grid-template-columns: minmax(0, 0.9fr) minmax(0, 1fr);
      gap: 34px;
      align-items: center;
      border-radius: 30px;
      background: #101828;
      color: #ffffff;
      padding: clamp(28px, 5vw, 54px);
      box-shadow: 0 34px 92px rgba(16, 24, 40, 0.2);
    }

    .workflowPanel h2 {
      max-width: 500px;
      font-size: clamp(32px, 4vw, 56px);
      line-height: 1;
    }

    .workflowPanel p {
      color: rgba(255, 255, 255, 0.68);
      font-size: 17px;
    }

    .workflowSteps {
      display: grid;
      gap: 12px;
    }

    .workflowStep {
      display: grid;
      grid-template-columns: 42px minmax(0, 1fr);
      gap: 14px;
      align-items: center;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 18px;
      background: rgba(255, 255, 255, 0.06);
      padding: 14px;
      color: rgba(255, 255, 255, 0.82);
      font-weight: 720;
    }

    .workflowStep span {
      display: grid;
      place-items: center;
      width: 42px;
      height: 42px;
      border-radius: 999px;
      background: #ffffff;
      color: var(--brand);
      font-weight: 900;
    }

    .blogPreview {
      padding: 0 0 82px;
    }

    .blogPreviewCard {
      display: grid;
      grid-template-columns: minmax(0, 0.76fr) minmax(0, 1fr);
      gap: 30px;
      align-items: stretch;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 30px;
      background:
        radial-gradient(circle at 12% 20%, rgba(255, 56, 66, 0.12), transparent 18rem),
        radial-gradient(circle at 88% 10%, rgba(255, 139, 31, 0.16), transparent 18rem), #ffffff;
      padding: clamp(24px, 5vw, 44px);
      box-shadow: 0 28px 80px rgba(15, 23, 42, 0.08);
    }

    .blogPreviewVisual {
      display: grid;
      align-content: space-between;
      min-height: 320px;
      border-radius: 24px;
      background: #101828;
      color: #ffffff;
      padding: 24px;
      overflow: hidden;
      position: relative;
      isolation: isolate;
    }

    .blogPreviewVisual strong {
      position: relative;
      z-index: 1;
      max-width: 280px;
      font-size: clamp(34px, 5vw, 58px);
      line-height: 0.95;
    }

    .blogPreviewFunnel {
      position: relative;
      z-index: 1;
      display: grid;
      gap: 8px;
      max-width: 270px;
      color: rgba(255, 255, 255, 0.76);
      font-weight: 740;
    }

    .blogPreviewFunnel span {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.08);
      padding: 8px 12px;
      font-size: 12px;
      text-transform: uppercase;
    }

    .blogPreviewCopy {
      display: grid;
      align-content: center;
      gap: 16px;
      min-width: 0;
    }

    .blogPreviewCopy h2 {
      max-width: 720px;
      font-size: clamp(36px, 5vw, 68px);
      line-height: 0.96;
    }

    .blogPreviewCopy p:not(.eyebrow) {
      max-width: 620px;
      font-size: 17px;
    }

    .blogPreviewMeta,
    .blogMetaRow {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      color: #667085;
      font-size: 13px;
      font-weight: 720;
    }

    .blogPreviewMeta span,
    .blogMetaRow span {
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.72);
      padding: 7px 11px;
    }

    .landingCtaBand {
      padding: 0 0 86px;
    }

    .finalCta {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 24px;
      align-items: center;
      border: 1px solid rgba(37, 99, 235, 0.12);
      border-radius: 28px;
      background: linear-gradient(135deg, rgba(37, 99, 235, 0.11), rgba(20, 184, 166, 0.1));
      padding: clamp(24px, 5vw, 42px);
    }

    .finalCta h2 {
      max-width: 720px;
      font-size: clamp(30px, 4vw, 48px);
      line-height: 1.02;
    }

    .finalCta p {
      max-width: 650px;
      font-size: 16px;
    }

    .blogPage {
      min-height: 100vh;
      color: var(--ink);
      background:
        radial-gradient(circle at 16% 6%, rgba(255, 56, 66, 0.11), transparent 28rem),
        radial-gradient(circle at 84% 2%, rgba(255, 139, 31, 0.13), transparent 30rem),
        linear-gradient(180deg, #fffaf6 0%, #f8fbff 38%, #ffffff 100%);
    }

    .blogHero {
      display: grid;
      gap: 22px;
      width: min(100% - 48px, 1120px);
      margin: 0 auto;
      padding: 82px 0 48px;
    }

    .blogHero h1 {
      max-width: 960px;
      font-size: clamp(48px, 7vw, 92px);
      line-height: 0.94;
      letter-spacing: 0;
    }

    .blogHero > p {
      max-width: 780px;
      color: #536173;
      font-size: clamp(18px, 2.2vw, 24px);
      line-height: 1.35;
    }

    .blogHeroActions {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-top: 4px;
    }

    .blogFunnel {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 12px;
      width: min(100% - 48px, 1120px);
      margin: 0 auto 38px;
    }

    .blogFunnelStep {
      min-width: 0;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 20px;
      background: rgba(255, 255, 255, 0.78);
      padding: 18px;
      box-shadow: 0 18px 48px rgba(15, 23, 42, 0.06);
    }

    .blogFunnelStep span {
      display: block;
      color: #667085;
      font-size: 12px;
      font-weight: 800;
      text-transform: uppercase;
    }

    .blogFunnelStep strong {
      display: block;
      margin-top: 8px;
      font-size: 22px;
    }

    .blogFunnelStep small {
      display: block;
      margin-top: 6px;
      color: #667085;
      font-size: 13px;
      line-height: 1.4;
    }

    .blogArticle {
      width: min(100% - 48px, 1040px);
      margin: 0 auto;
      padding-bottom: 86px;
    }

    .blogArticle > p {
      max-width: 820px;
      color: #536173;
      font-size: 18px;
      line-height: 1.65;
    }

    .blogArticle > p + p {
      margin-top: 14px;
    }

    .blogBenchmark,
    .blogLayer,
    .blogTableWrap,
    .blogCampaigns,
    .blogArticleCta {
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 28px;
      background: rgba(255, 255, 255, 0.82);
      box-shadow: 0 22px 64px rgba(15, 23, 42, 0.07);
    }

    .blogBenchmark {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
      gap: 18px;
      align-items: center;
      margin-bottom: 24px;
      padding: clamp(22px, 4vw, 34px);
    }

    .blogBenchmark strong {
      display: block;
      font-size: clamp(34px, 5vw, 58px);
      line-height: 1;
    }

    .blogBenchmark span {
      color: #667085;
      font-weight: 740;
    }

    .blogBenchmarkArrow {
      display: grid;
      place-items: center;
      width: 44px;
      height: 44px;
      border-radius: 999px;
      background: #101828;
      color: #ffffff;
      font-weight: 900;
    }

    .blogLayer {
      display: grid;
      grid-template-columns: 116px minmax(0, 1fr);
      gap: 24px;
      margin: 18px 0;
      padding: clamp(22px, 4vw, 34px);
    }

    .blogLayerNumber {
      display: grid;
      place-items: center;
      align-self: start;
      width: 86px;
      height: 86px;
      border-radius: 28px;
      background: linear-gradient(135deg, #ff3842, #ff8b1f);
      color: #ffffff;
      font-size: 26px;
      font-weight: 900;
    }

    .blogLayer h2 {
      font-size: clamp(28px, 4vw, 46px);
      line-height: 1;
    }

    .blogLayerMetric {
      margin: 8px 0 16px;
      color: #667085;
      font-weight: 780;
    }

    .blogQuestion {
      border-left: 4px solid #ff8b1f;
      margin: 0 0 16px;
      padding-left: 14px;
      color: #344054;
      font-size: 17px;
      font-weight: 760;
    }

    .blogLayer p {
      color: #536173;
      font-size: 16px;
      line-height: 1.65;
    }

    .blogLayer ul {
      display: grid;
      gap: 10px;
      margin: 18px 0;
      padding-left: 20px;
      color: #344054;
      line-height: 1.55;
    }

    .blogCallout {
      border-radius: 18px;
      background: linear-gradient(135deg, rgba(255, 56, 66, 0.1), rgba(255, 139, 31, 0.11));
      color: #172026;
      font-weight: 760;
      padding: 16px;
    }

    .blogTableWrap,
    .blogCampaigns,
    .blogArticleCta {
      margin-top: 22px;
      padding: clamp(20px, 4vw, 32px);
    }

    .blogTableWrap h2,
    .blogCampaigns h2,
    .blogArticleCta h2 {
      font-size: clamp(30px, 4vw, 52px);
      line-height: 1;
    }

    .blogTableScroller {
      overflow-x: auto;
      margin-top: 18px;
    }

    .blogTable {
      width: 100%;
      min-width: 760px;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 14px;
    }

    .blogTable th,
    .blogTable td {
      border-bottom: 1px solid rgba(15, 23, 42, 0.08);
      padding: 14px 12px;
      text-align: left;
      vertical-align: top;
    }

    .blogTable th {
      color: #667085;
      font-size: 12px;
      text-transform: uppercase;
    }

    .blogTable td:nth-child(3) {
      color: #0f766e;
      font-weight: 850;
    }

    .blogCampaignGrid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      margin-top: 18px;
    }

    .blogCampaignCard {
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 18px;
      background: #ffffff;
      padding: 16px;
    }

    .blogCampaignCard strong {
      display: block;
      font-size: 18px;
    }

    .blogCampaignCard span {
      display: block;
      margin-top: 8px;
      color: #667085;
      line-height: 1.45;
    }

    .blogIndexGrid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 18px;
      width: min(100% - 48px, 1120px);
      margin: 0 auto;
      padding: 0 0 86px;
    }

    .blogIndexCard {
      display: grid;
      grid-template-rows: auto auto minmax(96px, 1fr) auto;
      gap: 16px;
      min-width: 0;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 28px;
      background:
        radial-gradient(circle at 12% 16%, rgba(255, 56, 66, 0.1), transparent 18rem),
        radial-gradient(circle at 88% 4%, rgba(255, 139, 31, 0.12), transparent 16rem),
        rgba(255, 255, 255, 0.86);
      padding: clamp(22px, 4vw, 34px);
      box-shadow: 0 22px 64px rgba(15, 23, 42, 0.07);
      text-decoration: none;
      color: inherit;
    }

    .blogIndexCard:hover {
      transform: translateY(-2px);
      box-shadow: 0 28px 80px rgba(15, 23, 42, 0.1);
    }

    .blogIndexCard h2 {
      font-size: clamp(30px, 4vw, 48px);
      line-height: 1;
    }

    .blogIndexCard p {
      color: #536173;
      font-size: 16px;
      line-height: 1.55;
    }

    .blogIndexCard .accessButton {
      align-self: end;
      justify-self: start;
      width: 132px;
      text-align: center;
    }

    .blogArticleCta {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 18px;
      align-items: center;
      background: #101828;
      color: #ffffff;
    }

    .blogArticleCta p {
      max-width: 680px;
      color: rgba(255, 255, 255, 0.72);
      font-size: 16px;
    }

    .shell {
      display: grid;
      grid-template-columns: 248px minmax(0, 1fr);
      min-height: 100vh;
      transition: padding-top 180ms ease;
    }

    .shell.announcementPreviewTopActive {
      padding-top: 52px;
    }

    .shell.collapsed {
      grid-template-columns: 72px minmax(0, 1fr);
    }

    aside {
      position: sticky;
      top: 0;
      align-self: start;
      min-height: 100vh;
      border-right: 1px solid rgba(15, 23, 42, 0.08);
      background: rgba(255, 255, 255, 0.68);
      padding: 28px 22px;
      backdrop-filter: blur(20px);
      overflow: hidden;
      transition:
        padding 180ms ease,
        background 180ms ease;
    }

    .shell.collapsed aside {
      padding-inline: 14px;
    }

    .shell.collapsed aside > .stack {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-items: center;
      width: 44px;
    }

    main {
      min-width: 0;
      padding: 32px;
    }

    h1,
    h2,
    h3 {
      margin: 0;
      letter-spacing: 0;
    }

    h1 {
      max-width: 820px;
      font-size: clamp(28px, 4vw, 44px);
      line-height: 1.1;
    }

    .titleWithBadge,
    .labelWithBadge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    .betaBadge {
      display: inline-grid;
      place-items: center;
      flex: 0 0 auto;
      min-height: 20px;
      border-radius: 999px;
      background: rgba(245, 158, 11, 0.14);
      color: #b45309;
      font-size: 11px;
      font-weight: 800;
      line-height: 1;
      padding: 0 8px;
      text-transform: uppercase;
    }

    h2 {
      margin-bottom: 2px;
      font-size: 17px;
      line-height: 1.2;
    }

    p {
      margin: 8px 0 0;
      color: #667085;
      line-height: 1.45;
    }

    label {
      display: grid;
      gap: 8px;
      color: #536173;
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0;
    }

    input {
      width: 100%;
      min-height: 44px;
      border: 1px solid rgba(15, 23, 42, 0.1);
      border-radius: 12px;
      color: #172026;
      background: rgba(255, 255, 255, 0.88);
      padding: 0 13px;
      font-size: 13px;
      transition:
        border-color 160ms ease,
        box-shadow 160ms ease,
        background 160ms ease;
    }

    input:hover,
    input:focus {
      border-color: rgba(37, 99, 235, 0.36);
      background: #ffffff;
      box-shadow: 0 8px 24px rgba(37, 99, 235, 0.08);
    }

    select {
      appearance: none;
      -webkit-appearance: none;
      min-height: 40px;
      border: 1px solid rgba(15, 23, 42, 0.1);
      border-radius: 999px;
      color: var(--ink);
      background:
        linear-gradient(45deg, transparent 50%, #667085 50%) calc(100% - 19px) 17px / 6px 6px
          no-repeat,
        linear-gradient(135deg, #667085 50%, transparent 50%) calc(100% - 15px) 17px / 6px 6px
          no-repeat,
        rgba(255, 255, 255, 0.88);
      padding: 0 36px 0 14px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
    }

    .productPicker {
      position: relative;
      display: grid;
      gap: 8px;
      color: #536173;
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0;
    }

    .productPickerButton {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      width: 100%;
      min-height: 40px;
      border: 1px solid rgba(15, 23, 42, 0.1);
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.88);
      color: var(--ink);
      padding: 0 14px;
      box-shadow: none;
      font-size: 13px;
      font-weight: 500;
      text-align: left;
    }

    .productPickerButton:hover:not(:disabled) {
      border-color: rgba(37, 99, 235, 0.36);
      background: #ffffff;
      box-shadow: 0 8px 24px rgba(37, 99, 235, 0.08);
      transform: none;
    }

    .productPickerButton span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .productPickerButton svg {
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      stroke: currentColor;
      stroke-width: 2.2;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .productPickerMenu {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      z-index: 30;
      display: grid;
      width: 100%;
      max-height: 260px;
      overflow: auto;
      border: 1px solid rgba(15, 23, 42, 0.1);
      border-radius: 18px;
      background: rgba(255, 255, 255, 0.98);
      padding: 6px;
      box-shadow: 0 22px 54px rgba(15, 23, 42, 0.16);
    }

    .productPickerOption {
      display: block;
      width: 100%;
      min-height: 36px;
      border: 0;
      border-radius: 12px;
      background: transparent;
      color: var(--ink);
      padding: 8px 10px;
      box-shadow: none;
      font-size: 13px;
      font-weight: 500;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .productPickerOption:hover:not(:disabled),
    .productPickerOption.active {
      background: rgba(37, 99, 235, 0.08);
      color: var(--brand);
      box-shadow: none;
      transform: none;
    }

    .productPickerEmpty {
      padding: 10px;
      color: #667085;
      font-size: 13px;
      font-weight: 500;
    }

    .loginPanel input {
      font-size: 16px;
    }

    .metricsHeader {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 12px;
    }

    .periodControl {
      display: grid;
      gap: 6px;
      color: #667085;
      font-size: 12px;
      font-weight: 500;
      text-transform: uppercase;
    }

    .periodControlGroup {
      display: flex;
      align-items: end;
      justify-content: flex-end;
      gap: 14px;
      flex-wrap: wrap;
    }

    .customRangeControl {
      display: flex;
      align-items: end;
      gap: 14px;
      color: #667085;
      font-size: 12px;
      font-weight: 500;
      text-transform: uppercase;
    }

    .customRangeControl label {
      display: grid;
      gap: 6px;
    }

    .customRangeControl input {
      width: 140px;
      min-height: 40px;
      border-radius: 999px;
      font-size: 13px;
      font-weight: 500;
    }

    button {
      min-height: 44px;
      border: 0;
      border-radius: 999px;
      background: var(--brand);
      color: #ffffff;
      cursor: pointer;
      font-weight: 850;
      padding: 0 18px;
      transition:
        transform 160ms ease,
        box-shadow 160ms ease,
        background 160ms ease;
    }

    button:hover:not(:disabled) {
      background: var(--brand-dark);
      box-shadow: 0 12px 26px rgba(37, 99, 235, 0.2);
      transform: translateY(-1px);
    }

    button:disabled {
      cursor: wait;
      opacity: 0.64;
    }

    button.secondary {
      border: 1px solid rgba(15, 23, 42, 0.12);
      background: rgba(255, 255, 255, 0.72);
      color: #172026;
    }

    button.secondary:hover:not(:disabled) {
      background: #ffffff;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
    }

    .button {
      min-height: 44px;
      width: fit-content;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 999px;
      padding: 0 18px;
      text-decoration: none;
      font-size: 14px;
      font-weight: 600;
      transition:
        transform 160ms ease,
        box-shadow 160ms ease,
        background 160ms ease;
    }

    .button.secondary {
      border: 1px solid rgba(15, 23, 42, 0.12);
      background: rgba(255, 255, 255, 0.72);
      color: #172026;
    }

    .button.secondary:hover {
      background: #ffffff;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
      transform: translateY(-1px);
    }

    .rowActions {
      display: inline-grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 4px;
      width: fit-content;
      justify-self: end;
      border: 1px solid rgba(15, 23, 42, 0.1);
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.74);
      padding: 4px;
    }

    .rowActions button {
      min-height: 34px;
      min-width: 108px;
      border: 0;
      border-radius: 999px;
      padding: 0 14px;
      font-size: 13px;
      font-weight: 760;
      box-shadow: none;
    }

    .rowActions button.secondary {
      background: transparent;
      color: #667085;
    }

    .rowActions button.primary {
      background: var(--brand);
      color: #ffffff;
    }

    .rowActions button.secondary:hover:not(:disabled) {
      background: rgba(37, 99, 235, 0.1);
      color: var(--brand-dark);
      box-shadow: none;
      transform: none;
    }

    .rowActions button.primary:hover:not(:disabled) {
      background: var(--brand-dark);
      box-shadow: none;
      transform: none;
    }

    .rowActions.single {
      display: flex;
      grid-template-columns: none;
      border: 0;
      background: transparent;
      padding: 0;
    }

    .fieldHint {
      color: #667085;
      font-size: 12px;
      line-height: 1.35;
    }

    .switchRow {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      min-height: 46px;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.62);
      padding: 0 14px;
    }

    .switchRow input {
      width: 18px;
      min-height: 18px;
      accent-color: var(--brand);
    }

    .formGrid .inlineSwitch {
      align-self: end;
      min-height: 44px;
      height: 44px;
      border-radius: 12px;
      padding: 0 13px;
      background: rgba(255, 255, 255, 0.88);
      color: #536173;
      font-size: 12px;
      font-weight: 500;
    }

    .formGrid .inlineSwitch input {
      width: 16px;
      min-height: 16px;
    }

    .checkboxGrid,
    .miniStats {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 10px;
    }

    .currencyControls {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      max-width: 720px;
    }

    .currencyBaseRow {
      grid-template-columns: minmax(0, 1fr);
      max-width: 320px;
    }

    .currencySwitcherRow {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      max-width: 720px;
    }

    .currencyPositionGrid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      max-width: 720px;
    }

    .currencyFieldBox {
      display: grid;
      align-content: center;
      gap: 6px;
      min-height: 46px;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.62);
      padding: 8px 14px;
      color: #667085;
      font-size: 12px;
      font-weight: 500;
    }

    .currencyFieldBox strong {
      color: var(--ink);
      font-size: 14px;
      font-weight: 560;
    }

    .compactSwitch {
      min-height: 42px;
    }

    .miniStats div {
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.62);
      padding: 12px;
    }

    .miniStats span {
      display: block;
      color: #667085;
      font-size: 11px;
      font-weight: 760;
      text-transform: uppercase;
    }

    .miniStats strong {
      display: block;
      margin-top: 6px;
      font-size: 20px;
      line-height: 1.1;
    }

    .filePicker {
      display: grid;
      gap: 8px;
    }

    .filePicker > span {
      color: #536173;
      font-size: 12px;
      font-weight: 500;
    }

    .filePicker input {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      white-space: nowrap;
    }

    .filePickerButton {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-height: 52px;
      border: 1px solid rgba(15, 23, 42, 0.1);
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.74);
      color: #172026;
      padding: 0 14px 0 16px;
      cursor: pointer;
      transition:
        border-color 160ms ease,
        box-shadow 160ms ease,
        background 160ms ease;
    }

    .filePickerButton:hover {
      border-color: rgba(37, 99, 235, 0.32);
      background: #ffffff;
      box-shadow: 0 10px 24px rgba(37, 99, 235, 0.08);
    }

    .announcementIconPicker .filePickerButton:hover {
      border-color: rgba(15, 23, 42, 0.14);
      background: rgba(255, 255, 255, 0.9);
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
    }

    .filePickerButton span:first-child {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .cursorStatusGroup {
      display: flex;
      align-items: center;
      min-width: 0;
      gap: 8px;
    }

    .cursorStatusGroup span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 12px;
      font-weight: 500;
      color: #667085;
    }

    .cursorStatusGroup .removeCursorIcon {
      color: #b42318;
      overflow: visible;
    }

    .cursorStatusGroup .removeCursorIcon:hover {
      color: #7f1d1d;
    }

    .cursorStatusThumb {
      width: 28px;
      min-width: 28px;
      height: 28px;
      border-radius: 8px;
      object-fit: contain;
      background: rgba(15, 23, 42, 0.04);
    }

    .removeCursorIcon {
      position: relative;
      display: grid;
      place-items: center;
      width: 22px;
      min-width: 22px;
      height: 22px;
      border: 0;
      border-radius: 999px;
      background: transparent;
      color: #b42318;
      font-size: 0;
      line-height: 1;
      padding: 0;
      transition:
        color 160ms ease,
        transform 160ms ease;
    }

    .removeCursorIcon::before,
    .removeCursorIcon::after {
      content: '';
      position: absolute;
      width: 11px;
      height: 1.7px;
      border-radius: 999px;
      background: currentColor;
    }

    .removeCursorIcon::before {
      transform: rotate(45deg);
    }

    .removeCursorIcon::after {
      transform: rotate(-45deg);
    }

    .removeCursorIcon:hover:not(:disabled) {
      background: transparent;
      color: #7f1d1d;
      box-shadow: none;
      transform: scale(1.08);
    }

    .filePickerCta {
      flex: 0 0 auto;
      border-radius: 999px;
      background: var(--brand-soft);
      color: var(--brand-dark);
      font-size: 12px;
      font-weight: 780;
      padding: 8px 12px;
    }

    .cursorPreview {
      display: grid;
      place-items: center;
      min-height: 220px;
      border: 1px dashed rgba(15, 23, 42, 0.18);
      border-radius: 18px;
      background:
        linear-gradient(45deg, rgba(37, 99, 235, 0.06) 25%, transparent 25%),
        linear-gradient(-45deg, rgba(37, 99, 235, 0.06) 25%, transparent 25%), #ffffff;
      color: #667085;
      overflow: hidden;
    }

    .cursorPreview img {
      width: min(96px, 50%);
      height: min(96px, 50%);
      object-fit: contain;
    }

    .cursorPreviewActive,
    .cursorPreviewActive * {
      cursor: var(--dashboard-cursor-preview), auto !important;
    }

    .videoItemCard {
      display: grid;
      gap: 14px;
      border: 1px solid rgba(15, 23, 42, 0.09);
      border-radius: 18px;
      background: rgba(255, 255, 255, 0.66);
      padding: 14px;
    }

    .videoItemHeader {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .videoItemHeader strong {
      font-size: 13px;
      color: #172026;
    }

    button.secondary.shoppableButton {
      min-height: 34px;
      border-color: rgba(15, 23, 42, 0.1);
      color: #475467;
      font-size: 12px;
      font-weight: 400;
      padding: 0 12px;
      box-shadow: none;
    }

    button.secondary.shoppableButton:hover:not(:disabled) {
      color: #172026;
      box-shadow: none;
      transform: none;
    }

    .videoItemCard label {
      min-width: 0;
    }

    .videoItemCard select {
      width: 100%;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .selectedProduct {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
      min-height: 64px;
      border-radius: 16px;
      background: rgba(15, 23, 42, 0.04);
      padding: 10px;
      color: #667085;
      font-size: 13px;
    }

    .selectedProduct.hasProduct {
      background: rgba(255, 255, 255, 0.78);
      box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
    }

    .selectedProduct span {
      min-width: 0;
      flex: 1 1 auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .selectedProduct img {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      object-fit: cover;
      background: #fff;
    }

    .removeSelectedProduct {
      position: relative;
      display: grid;
      place-items: center;
      width: 28px;
      min-width: 28px;
      height: 28px;
      border: 0;
      border-radius: 999px;
      background: transparent;
      color: #b42318;
      font-size: 0;
      padding: 0;
      box-shadow: none;
      transition:
        color 160ms ease,
        transform 160ms ease;
    }

    .removeSelectedProduct::before,
    .removeSelectedProduct::after {
      content: '';
      position: absolute;
      width: 13px;
      height: 1.8px;
      border-radius: 999px;
      background: currentColor;
    }

    .removeSelectedProduct::before {
      transform: rotate(45deg);
    }

    .removeSelectedProduct::after {
      transform: rotate(-45deg);
    }

    .removeSelectedProduct:hover:not(:disabled) {
      background: transparent;
      color: #7f1d1d;
      box-shadow: none;
      transform: scale(1.08);
    }

    .announcementPreview {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      align-content: center;
      min-height: 58px;
      border-radius: 16px;
      color: var(--announcement-text, #ffffff);
      background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.18), transparent 34%),
        radial-gradient(circle at 18% 50%, rgba(255, 255, 255, 0.18), transparent 20%),
        var(--announcement-bg, #111827);
      box-shadow: 0 16px 36px rgba(15, 23, 42, 0.18);
      padding: 9px 14px;
      overflow: hidden;
    }

    .announcementSitePreview {
      position: fixed;
      left: 0;
      z-index: 2147482998;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      width: 100%;
      min-height: 52px;
      padding-right: 34px;
      color: var(--announcement-text, #ffffff);
      background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.2), transparent 34%),
        radial-gradient(circle at 18% 50%, rgba(255, 255, 255, 0.2), transparent 20%),
        var(--announcement-bg, #111827);
      box-shadow: 0 16px 36px rgba(15, 23, 42, 0.2);
      padding: 8px 16px;
      pointer-events: none;
    }

    .announcementSitePreview.scrollable {
      position: static;
      margin: 12px 0 0;
    }

    .announcementSitePreview.scrollable.top {
      position: static;
      top: auto;
      bottom: auto;
      margin: 0 0 12px;
    }

    .announcementSitePreview.scrollable.bottom {
      position: static;
      top: auto;
      bottom: auto;
      margin: 12px 0 0;
    }

    .announcementSitePreview.left {
      justify-content: flex-start;
    }

    .announcementSitePreview.right {
      justify-content: flex-end;
    }

    .announcementSitePreview.sliding .announcementPreviewCopy {
      animation: announcementPreviewSlide 16s linear infinite;
      animation-delay: 0s;
      min-width: max-content;
    }

    .announcementPreviewIcon {
      width: 20px;
      min-width: 20px;
      height: 20px;
      border-radius: 6px;
      object-fit: contain;
    }

    .announcementCtaPreview {
      flex: 0 0 auto;
      border: var(--announcement-cta-border-size, 0) solid
        var(--announcement-cta-border-color, #ffffff);
      border-radius: var(--announcement-cta-radius, 999px);
      background: var(--announcement-cta-bg, #ffffff);
      color: var(--announcement-cta-text, #111827);
      font-size: 12px;
      font-weight: 760;
      line-height: 1;
      padding: 8px 12px;
    }

    @keyframes announcementPreviewSlide {
      from {
        transform: translateX(0);
      }

      to {
        transform: translateX(-100%);
      }
    }

    .announcementSitePreview.top {
      top: 0;
    }

    .announcementSitePreview.bottom {
      bottom: 0;
    }

    .announcementPreviewCopy {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }

    .announcementDismissPreview {
      position: absolute;
      top: 7px;
      right: 9px;
      width: 18px;
      height: 18px;
      color: var(--announcement-dismiss, currentColor);
      opacity: 0.78;
    }

    .announcementDismissPreview::before,
    .announcementDismissPreview::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 10px;
      height: 1.5px;
      border-radius: 999px;
      background: currentColor;
    }

    .announcementDismissPreview::before {
      transform: translate(-50%, -50%) rotate(45deg);
    }

    .announcementDismissPreview::after {
      transform: translate(-50%, -50%) rotate(-45deg);
    }

    .announcementPreview strong,
    .announcementSitePreview strong {
      flex: 0 0 auto;
      font-size: 15px;
      line-height: 1.2;
    }

    .announcementPreview span,
    .announcementSitePreview span {
      min-width: 0;
      opacity: 0.82;
      overflow: hidden;
      font-size: 13px;
      line-height: 1.4;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .announcementCountdown {
      flex: 0 0 auto;
      width: fit-content;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.16);
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.16);
      font-size: 12px;
      font-weight: 800;
      line-height: 1;
      padding: 7px 10px;
    }

    @media (max-width: 720px) {
      .announcementPreview,
      .announcementSitePreview {
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        min-height: 54px;
        padding: 7px 10px;
      }

      .announcementPreviewCopy {
        display: grid;
        gap: 2px;
      }

      .announcementPreview strong,
      .announcementSitePreview strong {
        font-size: 13px;
      }

      .announcementPreview span,
      .announcementSitePreview span {
        font-size: 12px;
      }
    }

    button.danger {
      display: grid;
      place-items: center;
      min-width: 42px;
      padding: 0;
      background: #fff1f1;
      color: #b42318;
      box-shadow: none;
    }

    button.danger:hover:not(:disabled) {
      background: #fee4e2;
      box-shadow: none;
    }

    .stack {
      display: grid;
      gap: 14px;
      min-width: 0;
    }

    .brand {
      display: flex;
      align-items: center;
      gap: 10px;
      min-height: 34px;
      margin: 0;
    }

    .brandMark {
      flex: 0 0 34px;
      width: 34px;
      height: 34px;
      object-fit: contain;
    }

    .brand h2 {
      overflow: hidden;
      max-width: 140px;
      margin: 0;
      font-size: 16px;
      white-space: nowrap;
    }

    .sidebarHeader {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 18px;
      min-width: 204px;
      transition: min-width 180ms ease;
    }

    .sidebarToggle {
      display: grid;
      place-items: center;
      flex: 0 0 34px;
      width: 34px;
      min-width: 34px;
      min-height: 34px;
      padding: 0;
      border: 1px solid rgba(15, 23, 42, 0.1);
      background: rgba(255, 255, 255, 0.72);
      color: #536173;
      box-shadow: none;
    }

    .sidebarToggle span {
      display: grid;
      place-items: center;
      width: 100%;
      height: 100%;
      line-height: 0;
    }

    .sidebarToggle svg,
    .navIcon svg {
      width: 17px;
      height: 17px;
      stroke: currentColor;
      stroke-width: 2.2;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .sidebarToggle .mobileIcon {
      display: none;
    }

    .sidebarToggle:hover:not(:disabled) {
      background: #ffffff;
      box-shadow: none;
      transform: none;
    }

    .shell.collapsed .sidebarHeader {
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      width: 44px;
      min-width: 44px;
      margin-bottom: 8px;
    }

    .shell.collapsed .brand {
      justify-content: center;
      flex: 0 0 34px;
      width: 34px;
      overflow: hidden;
    }

    .announcementPreview.effect-none,
    .announcementSitePreview.effect-none {
      background: var(--announcement-bg, #111827);
    }

    .announcementPreview.effect-spotlight,
    .announcementSitePreview.effect-spotlight {
      background:
        radial-gradient(circle at 12% 50%, rgba(255, 255, 255, 0.3), transparent 18%),
        linear-gradient(90deg, rgba(255, 255, 255, 0.2), transparent 34%, rgba(255, 255, 255, 0.12)),
        var(--announcement-bg, #111827);
    }

    .shell.collapsed .brandMark {
      flex-basis: 34px;
      width: 34px;
      height: 34px;
    }

    .brand h2,
    .storeBadge,
    .languageSwitch,
    .logoutLabel,
    .navLabel,
    .sidebarLogout {
      opacity: 1;
      transition:
        opacity 120ms ease,
        visibility 120ms ease;
    }

    .shell.collapsed .brand h2,
    .shell.collapsed .storeBadge,
    .shell.collapsed .languageSwitch,
    .shell.collapsed .logoutLabel,
    .shell.collapsed .navLabel,
    .shell.collapsed .sidebarLogout {
      visibility: hidden;
      opacity: 0;
      pointer-events: none;
    }

    .shell.collapsed .brand h2,
    .shell.collapsed .navLabel,
    .shell.collapsed .betaBadge {
      display: none;
      max-width: 0;
      width: 0;
      overflow: hidden;
    }

    .shell.collapsed .sidebarToggle {
      flex-basis: 30px;
      width: 30px;
      min-width: 30px;
      min-height: 30px;
      margin: 0;
    }

    .shell.collapsed .sidebarToggle svg {
      width: 15px;
      height: 15px;
    }

    .storeBadge,
    .statusPill {
      display: inline-flex;
      align-items: center;
      width: fit-content;
      min-height: 34px;
      border-radius: 999px;
      color: #536173;
      background: var(--brand-soft);
      padding: 0 12px;
      font-size: 13px;
      font-weight: 760;
    }

    .statusPill.live {
      background: rgba(22, 163, 74, 0.12);
      color: #15803d;
    }

    .statusPill.paused {
      background: rgba(245, 158, 11, 0.16);
      color: #b45309;
    }

    .navList {
      display: grid;
      gap: 8px;
      margin-top: 8px;
      min-width: 204px;
      color: #667085;
      font-size: 14px;
      transition: min-width 180ms ease;
    }

    .shell.collapsed .navList {
      justify-items: center;
      width: 44px;
      min-width: 44px;
      margin-top: 0;
    }

    .navItem {
      display: flex;
      align-items: center;
      gap: 10px;
      border: 0;
      border-radius: 12px;
      min-height: 40px;
      background: transparent;
      color: inherit;
      text-align: left;
      padding: 10px 12px;
      font-weight: 650;
      box-shadow: none;
    }

    .shell.collapsed .navItem {
      width: 40px;
      height: 40px;
      min-height: 40px;
      justify-content: center;
      gap: 0;
      padding: 0;
    }

    .navIcon {
      display: grid;
      place-items: center;
      flex: 0 0 auto;
      width: 20px;
      height: 20px;
    }

    .sidebarLogout {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-height: 40px;
      font-size: 13px;
      font-weight: 650;
    }

    .sidebarLogout svg {
      display: block;
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      stroke: currentColor;
      stroke-width: 2.1;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .shell.collapsed .sidebarLogout {
      display: none;
      width: 40px;
      min-height: 40px;
      padding: 0;
    }

    .navItem.active {
      color: #172026;
      background: var(--brand-soft);
      font-weight: 820;
    }

    .navItem:hover:not(:disabled) {
      background: var(--brand-soft);
      box-shadow: none;
      transform: none;
    }

    .viewSelector {
      display: inline-grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 4px;
      width: fit-content;
      margin-bottom: 18px;
      border: 1px solid rgba(15, 23, 42, 0.1);
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.74);
      padding: 4px;
    }

    .viewSelector button {
      min-height: 34px;
      min-width: 106px;
      border-radius: 999px;
      background: transparent;
      color: #667085;
      padding: 0 14px;
      font-size: 13px;
      font-weight: 760;
      box-shadow: none;
    }

    .viewSelector button.active {
      background: var(--brand);
      color: #ffffff;
    }

    .viewSelector button:hover:not(:disabled) {
      background: rgba(37, 99, 235, 0.1);
      color: var(--brand-dark);
      box-shadow: none;
      transform: none;
    }

    .viewSelector button.active:hover:not(:disabled) {
      background: var(--brand-dark);
      color: #ffffff;
    }

    a.storeBadge {
      text-decoration: none;
    }

    a.storeBadge:hover {
      background: rgba(37, 99, 235, 0.14);
      color: var(--brand-dark);
    }

    .languageSwitch {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 4px;
      border: 1px solid rgba(15, 23, 42, 0.1);
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.72);
      padding: 4px;
    }

    .languageSwitch button {
      min-height: 32px;
      border-radius: 999px;
      background: transparent;
      color: #667085;
      padding: 0 10px;
      box-shadow: none;
      font-size: 16px;
      font-weight: 850;
    }

    .languageSwitch button.active {
      background: var(--brand);
      color: #ffffff;
    }

    .languageSwitch button:hover:not(:disabled) {
      box-shadow: none;
      transform: none;
    }

    .topbar {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 24px;
      margin-bottom: 28px;
    }

    .topActions {
      display: flex;
      align-items: center;
      gap: 8px;
      justify-content: flex-end;
    }

    .iconButton {
      display: grid;
      place-items: center;
      width: 44px;
      min-width: 44px;
      padding: 0;
      border: 1px solid rgba(15, 23, 42, 0.12);
      background: rgba(255, 255, 255, 0.76);
      color: #172026;
      box-shadow: none;
    }

    .iconButton svg {
      width: 19px;
      height: 19px;
      stroke: currentColor;
      stroke-width: 2.2;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .iconButton.primary {
      border-color: transparent;
      background: var(--brand);
      color: #ffffff;
    }

    .iconButton:hover:not(:disabled) {
      background: #ffffff;
      box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
    }

    .iconButton.primary:hover:not(:disabled) {
      background: var(--brand-dark);
      box-shadow: 0 12px 26px rgba(37, 99, 235, 0.2);
    }

    .previewButton:disabled {
      cursor: default;
    }

    .eyebrow {
      margin: 0 0 8px;
      color: var(--brand);
      font-size: 12px;
      font-weight: 850;
      text-transform: uppercase;
    }

    .grid {
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 14px;
      margin-bottom: 20px;
    }

    .announcementGrid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      max-width: 660px;
      margin-left: auto;
    }

    .announcementGrid .metric {
      grid-template-rows: minmax(22px, auto) auto;
      gap: 8px;
      padding: 12px;
    }

    .announcementGrid .metricValueRow {
      min-height: 34px;
    }

    .announcementGrid .metricIcon {
      width: 34px;
      height: 34px;
    }

    .announcementGrid .metricIcon svg {
      width: 17px;
      height: 17px;
    }

    .announcementGrid .metric strong {
      font-size: clamp(22px, 1.8vw, 26px);
    }

    .announcementList {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
      gap: 12px;
    }

    .announcementCard {
      display: grid;
      align-content: space-between;
      gap: 18px;
      width: 100%;
      min-height: 188px;
      padding: 18px;
      border: 1px solid rgba(148, 163, 184, 0.22);
      border-radius: 22px;
      background: rgba(255, 255, 255, 0.76);
      color: inherit;
      text-align: left;
      cursor: pointer;
      transition:
        border-color 0.18s ease,
        background 0.18s ease,
        transform 0.18s ease;
    }

    button.announcementCard:hover:not(:disabled),
    button.announcementCard:focus:not(:disabled),
    button.announcementCard:focus-visible:not(:disabled) {
      border-color: rgba(148, 163, 184, 0.22);
      background: rgba(255, 255, 255, 0.76);
      color: inherit;
      outline: none;
      box-shadow: none;
      transform: none;
    }

    .announcementCardTop,
    .announcementCardSchedule {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-width: 0;
    }

    .announcementCardCopy {
      display: grid;
      min-width: 0;
      gap: 8px;
    }

    .announcementCardCopy strong,
    .announcementCardCopy span,
    .announcementCardSchedule strong {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .announcementCardCopy strong {
      font-size: 18px;
      font-weight: 700;
      color: #172026;
    }

    .announcementCardCopy span,
    .announcementCardSchedule {
      color: #667085;
      font-size: 13px;
      font-weight: 500;
    }

    .announcementCardAction {
      color: #667085;
      font-size: 12px;
      font-weight: 700;
    }

    .announcementDetailTitle {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }

    .announcementBackButton {
      display: inline-grid;
      place-items: center;
      width: 36px;
      height: 36px;
      min-width: 36px;
      max-width: 36px;
      min-height: 36px;
      max-height: 36px;
      aspect-ratio: 1 / 1;
      padding: 0;
      border: 1px solid rgba(148, 163, 184, 0.22);
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.78);
      color: #172026;
      box-shadow: none;
    }

    button.announcementBackButton:hover:not(:disabled),
    button.announcementBackButton:focus:not(:disabled),
    button.announcementBackButton:focus-visible:not(:disabled) {
      background: rgba(255, 255, 255, 0.78);
      border-color: rgba(148, 163, 184, 0.22);
      color: #172026;
      outline: none;
      box-shadow: none;
    }

    .announcementBackButton svg {
      width: 17px;
      height: 17px;
      stroke: currentColor;
      stroke-width: 2.2;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .announcementDeleteButton {
      display: inline-grid;
      place-items: center;
      width: 38px;
      height: 38px;
      min-width: 38px;
      max-width: 38px;
      min-height: 38px;
      max-height: 38px;
      aspect-ratio: 1 / 1;
      padding: 0;
      border: 1px solid rgba(180, 35, 24, 0.14);
      border-radius: 50%;
      background: #fff1f1;
      color: #b42318;
      box-shadow: none;
    }

    button.announcementDeleteButton:hover:not(:disabled),
    button.announcementDeleteButton:focus:not(:disabled),
    button.announcementDeleteButton:focus-visible:not(:disabled) {
      background: #fff1f1;
      border-color: rgba(180, 35, 24, 0.2);
      color: #b42318;
      outline: none;
      box-shadow: none;
    }

    .announcementDeleteButton svg {
      width: 18px;
      height: 18px;
      stroke: currentColor;
      stroke-width: 2.1;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .announcementCardSchedule {
      padding-top: 14px;
      border-top: 1px solid rgba(148, 163, 184, 0.18);
    }

    .announcementCardSchedule strong {
      display: grid;
      gap: 2px;
      max-width: 68%;
      color: #172026;
      font-size: 13px;
      font-weight: 650;
      text-align: right;
      line-height: 1.35;
      white-space: normal;
      overflow-wrap: anywhere;
    }

    .announcementCardSchedule strong span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .statusPill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 86px;
      padding: 7px 11px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 700;
    }

    .statusPill.active {
      background: rgba(16, 185, 129, 0.14);
      color: #047857;
    }

    .statusPill.inactive {
      background: rgba(148, 163, 184, 0.16);
      color: #475569;
    }

    .compactButton {
      min-height: 38px;
      padding: 0 14px;
      border-radius: 999px;
      font-size: 13px;
      font-weight: 550;
    }

    .metric {
      display: grid;
      grid-template-rows: minmax(30px, auto) auto;
      gap: 12px;
      overflow: hidden;
      padding: 18px;
      box-shadow: none;
    }

    .metricValueRow {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      min-height: 42px;
      min-width: 0;
    }

    .metricIcon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background: var(--brand-soft);
      color: var(--brand);
      line-height: 0;
    }

    .metricIcon svg {
      display: block;
      flex: 0 0 auto;
      width: 20px;
      height: 20px;
      stroke: currentColor;
      stroke-width: 2.2;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .metric > span:not(.metricIcon) {
      display: block;
      color: #667085;
      font-size: 11px;
      font-weight: 850;
      text-transform: uppercase;
    }

    .metric strong {
      display: block;
      min-width: 0;
      font-size: clamp(28px, 2.4vw, 34px);
      line-height: 1;
      white-space: nowrap;
    }

    .compactMetrics {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    .compactMetrics .metric {
      padding: 14px;
    }

    .compactMetrics .metric strong {
      font-size: clamp(24px, 2vw, 30px);
    }

    .layout {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(280px, 0.62fr);
      align-items: start;
      gap: 20px;
      min-width: 0;
    }

    .panel {
      min-width: 0;
      padding: 20px;
      box-shadow: 0 16px 46px rgba(15, 23, 42, 0.06);
    }

    .panelHeader {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 14px;
    }

    .panelHeader p {
      margin-top: 4px;
      font-size: 13px;
    }

    .panelActions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      flex-wrap: wrap;
    }

    .exportButton {
      min-height: 34px;
      padding: 0 12px;
      font-size: 12px;
    }

    .paginationBar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
    }

    .pageSizeControl {
      display: flex;
      align-items: center;
      gap: 10px;
      color: #667085;
      font-size: 12px;
      font-weight: 500;
    }

    .pageControls {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #667085;
      font-size: 13px;
    }

    .pageButton {
      min-height: 34px;
      padding: 0 12px;
      font-size: 12px;
    }

    .formGrid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(min(100%, 210px), 1fr));
      gap: 14px;
      min-width: 0;
    }

    .shoppablePlacementGrid {
      align-items: start;
    }

    .shoppablePlacementControl {
      align-self: start;
      align-content: start;
    }

    .shoppablePlacementControl select {
      height: 44px;
      min-height: 44px;
      align-self: start;
    }

    .shoppablePlacementRow {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 12px;
      align-items: end;
      width: 100%;
    }

    .shoppablePlacementScan {
      min-height: 44px;
      white-space: nowrap;
    }

    .shoppablePlacementMeta {
      display: flex;
      flex-direction: column;
      gap: 6px;
      grid-column: 1 / -1;
    }

    @media (max-width: 640px) {
      .shoppablePlacementRow {
        grid-template-columns: 1fr;
      }

      .shoppablePlacementScan {
        width: 100%;
      }
    }

    .settingsGrid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 14px;
      min-width: 0;
    }

    .settingsGrid .check {
      grid-column: 1 / -1;
    }

    .secretField {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 44px;
      gap: 8px;
      align-items: end;
    }

    .secretField label {
      min-width: 0;
    }

    .secretInput.masked {
      -webkit-text-security: disc;
      text-security: disc;
    }

    .secretToggle {
      display: grid;
      place-items: center;
      min-width: 44px;
      width: 44px;
      padding: 0;
      border: 1px solid rgba(15, 23, 42, 0.1);
      background: rgba(255, 255, 255, 0.78);
      color: var(--brand);
      box-shadow: none;
    }

    .secretToggle svg {
      width: 18px;
      height: 18px;
      stroke: currentColor;
      stroke-width: 2.2;
      stroke-linecap: round;
      stroke-linejoin: round;
      fill: none;
    }

    .modalOverlay {
      position: fixed;
      inset: 0;
      z-index: 20;
      display: grid;
      place-items: center;
      background: rgba(16, 24, 40, 0.42);
      padding: 20px;
      backdrop-filter: blur(10px);
    }

    .modalPanel {
      width: min(440px, 100%);
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 20px;
      background: rgba(255, 255, 255, 0.96);
      padding: 22px;
      box-shadow: 0 30px 80px rgba(16, 24, 40, 0.22);
    }

    .modalActions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      flex-wrap: wrap;
    }

    .deleteModal {
      width: min(420px, 100%);
      gap: 18px;
      padding: 24px;
    }

    .deleteModal .panelHeader h2 {
      font-size: 22px;
      line-height: 1.1;
    }

    .deleteModal .panelHeader p {
      max-width: 340px;
      line-height: 1.45;
    }

    .deleteModal .modalActions button {
      min-height: 40px;
      border-radius: 999px;
      padding: 0 16px;
      font-size: 12px;
      font-weight: 500;
      letter-spacing: 0;
    }

    button.deleteConfirmButton {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: max-content;
      padding-inline: 18px;
      border: 1px solid rgba(180, 35, 24, 0.16);
      background: #fff1f1;
      color: #b42318;
      box-shadow: none;
      font-weight: 700;
    }

    button.deleteConfirmButton:hover:not(:disabled),
    button.deleteConfirmButton:focus-visible:not(:disabled) {
      background: #fee4e2;
      border-color: rgba(180, 35, 24, 0.22);
      color: #b42318;
      box-shadow: none;
    }

    .phoneField {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      gap: 8px;
      align-items: center;
    }

    .phonePrefix {
      display: inline-flex;
      align-items: center;
      min-height: 44px;
      border: 1px solid rgba(15, 23, 42, 0.1);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.88);
      padding: 0 12px;
      color: var(--ink);
      font-size: 13px;
    }

    .sideStack .formGrid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }

    .reward {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto 28px auto;
      gap: 12px;
      align-items: center;
      min-width: 0;
      padding: 10px 12px;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 14px;
      background: rgba(248, 250, 252, 0.78);
      transition:
        border-color 160ms ease,
        transform 160ms ease,
        background 160ms ease;
    }

    .reward:hover {
      border-color: rgba(37, 99, 235, 0.22);
      background: #ffffff;
      transform: translateY(-1px);
    }

    .rewardInfo {
      display: grid;
      gap: 3px;
      min-width: 0;
    }

    .rewardInfo strong,
    .rewardInfo span,
    .rewardWeight {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rewardInfo strong {
      color: var(--ink);
      font-size: 14px;
      font-weight: 650;
    }

    .rewardInfo span,
    .rewardWeight {
      color: #667085;
      font-size: 12px;
      font-weight: 500;
    }

    .rewardSwatch {
      width: 22px;
      height: 22px;
      border: 1px solid rgba(15, 23, 42, 0.14);
      border-radius: 999px;
      box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.72);
    }

    .rewardActions {
      display: flex;
      gap: 6px;
      justify-content: flex-end;
      min-width: max-content;
    }

    .rewardActions .compactButton {
      min-height: 32px;
      padding: 0 11px;
      font-size: 12px;
    }

    .rewardModal {
      width: min(500px, 100%);
    }

    .rewardModal input,
    .rewardModal select {
      min-height: 40px;
      font-size: 14px;
    }

    .rewardModal input[readonly] {
      color: #667085;
      background: rgba(248, 250, 252, 0.92);
      cursor: default;
    }

    .compactFields {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 12px;
    }

    .compactStack {
      gap: 12px;
    }

    .plainNotice {
      margin: 0;
      background: transparent;
      padding: 0;
    }

    .check {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 44px;
      font-size: 13px;
      font-weight: 500;
    }

    .check input {
      width: 18px;
      min-height: 18px;
      accent-color: var(--brand);
    }

    input[type='color'] {
      height: 44px;
      padding: 5px;
      cursor: pointer;
    }

    .notice {
      display: inline-flex;
      align-items: center;
      width: fit-content;
      min-height: 34px;
      border-radius: 999px;
      margin-bottom: 14px;
      padding: 0 12px;
      font-size: 13px;
      font-weight: 760;
    }

    .error {
      color: #b42318;
      background: #fff1f1;
    }

    .saved {
      color: #067647;
      background: #ecfdf3;
    }

    .loading {
      display: grid;
      place-items: center;
      min-height: 280px;
      color: #667085;
      font-weight: 760;
    }

    .tableShell {
      overflow: hidden;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.72);
    }

    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 14px;
    }

    th,
    td {
      border-bottom: 1px solid rgba(15, 23, 42, 0.07);
      padding: 14px 16px;
      text-align: left;
      vertical-align: middle;
    }

    th {
      color: #667085;
      background: rgba(248, 250, 252, 0.72);
      font-size: 11px;
      font-weight: 850;
      text-transform: uppercase;
    }

    .sortHeader {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      min-height: 28px;
      border: 0;
      border-radius: 999px;
      background: transparent;
      color: inherit;
      padding: 0;
      font: inherit;
      text-transform: inherit;
      box-shadow: none;
    }

    .sortHeader:hover:not(:disabled) {
      background: transparent;
      color: var(--brand-dark);
      box-shadow: none;
      transform: none;
    }

    .sortIndicator {
      color: var(--brand);
      font-size: 12px;
      line-height: 1;
    }

    tr:last-child td {
      border-bottom: 0;
    }

    .contact {
      display: grid;
      gap: 3px;
      min-width: 190px;
    }

    .contact strong {
      font-size: 14px;
    }

    .contact span,
    .muted {
      color: #667085;
      font-size: 13px;
    }

    .mutedText {
      color: #667085;
      font-size: 13px;
      line-height: 1.45;
      margin: 0;
    }

    .couponPill {
      display: inline-flex;
      align-items: center;
      min-height: 32px;
      border-radius: 999px;
      background: var(--brand-soft);
      color: var(--brand-dark);
      padding: 0 11px;
      font-weight: 850;
    }

    .statusPill {
      display: inline-flex;
      align-items: center;
      min-height: 30px;
      border-radius: 999px;
      background: #f2f4f7;
      color: #667085;
      padding: 0 10px;
      font-size: 12px;
      font-weight: 850;
    }

    .statusPill.success {
      background: #ecfdf3;
      color: #067647;
    }

    .emptyState {
      display: grid;
      place-items: center;
      min-height: 260px;
      border: 1px dashed rgba(15, 23, 42, 0.16);
      border-radius: 16px;
      color: #667085;
      text-align: center;
      padding: 28px;
    }

    .installGrid {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(320px, 0.72fr);
      align-items: start;
      gap: 20px;
    }

    .steps {
      display: grid;
      gap: 12px;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    .step {
      display: grid;
      grid-template-columns: 36px minmax(0, 1fr);
      gap: 12px;
      padding: 14px;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 16px;
      background: rgba(248, 250, 252, 0.72);
    }

    .stepNumber {
      display: grid;
      place-items: center;
      width: 36px;
      height: 36px;
      border-radius: 999px;
      background: var(--brand-soft);
      color: var(--brand-dark);
      font-weight: 900;
    }

    .step strong {
      display: block;
      margin-bottom: 4px;
    }

    .codeBlock {
      display: block;
      overflow-x: auto;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 16px;
      background: #172026;
      color: #f8fafc;
      font:
        760 13px/1.55 ui-monospace,
        SFMono-Regular,
        Menlo,
        Monaco,
        Consolas,
        'Liberation Mono',
        monospace;
      padding: 16px;
      white-space: pre-wrap;
      word-break: break-word;
    }

    .hintList {
      display: grid;
      gap: 10px;
      margin: 0;
      padding-left: 18px;
      color: #667085;
      line-height: 1.45;
    }

    .installationStatus {
      display: grid;
      gap: 10px;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 16px;
      background: rgba(248, 250, 252, 0.76);
      padding: 14px;
    }

    .installationStatus.success {
      border-color: rgba(6, 118, 71, 0.18);
      background: rgba(236, 253, 243, 0.72);
    }

    .installationStatus.error {
      border-color: rgba(180, 35, 24, 0.18);
      background: rgba(255, 240, 237, 0.72);
    }

    .installationStatus strong {
      color: #172026;
    }

    .installationStatus.success strong {
      color: #067647;
    }

    .installationStatus.error strong {
      color: #b42318;
    }

    .installationMeta {
      display: grid;
      gap: 6px;
      color: #667085;
      font-size: 12px;
      line-height: 1.4;
      word-break: break-word;
    }

    .tagList {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .tagList span {
      border: 1px solid rgba(37, 99, 235, 0.12);
      border-radius: 999px;
      background: rgba(37, 99, 235, 0.08);
      color: #1d4ed8;
      font-size: 12px;
      font-weight: 600;
      padding: 6px 10px;
    }

    @media (max-width: 1120px) {
      .layout {
        grid-template-columns: 1fr;
      }

      .shell.collapsed .layout {
        grid-template-columns: minmax(0, 1fr) minmax(260px, 0.56fr);
      }
    }

    @media (max-width: 1180px) {
      .reward {
        grid-template-columns: minmax(0, 1fr) auto;
      }

      .rewardWeight,
      .rewardSwatch {
        justify-self: start;
      }
    }

    @media (max-width: 980px) {
      .shell,
      .layout,
      .shell.collapsed .layout,
      .installGrid {
        grid-template-columns: 1fr;
      }

      .landingHero,
      .landingSplit,
      .blogPreviewCard,
      .blogBenchmark,
      .blogLayer,
      .blogArticleCta,
      .workflowPanel,
      .finalCta {
        grid-template-columns: 1fr;
      }

      .landingHero {
        gap: 34px;
        padding-top: 42px;
      }

      .landingMetrics,
      .landingGrid,
      .blogFunnel,
      .blogIndexGrid,
      .blogCampaignGrid {
        grid-template-columns: 1fr;
      }

      .grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .shell.collapsed {
        grid-template-columns: 1fr;
      }

      aside {
        position: static;
        min-height: auto;
        border-right: 0;
        border-bottom: 1px solid rgba(15, 23, 42, 0.08);
      }

      .shell.collapsed aside {
        padding: 16px 20px;
      }

      .shell.collapsed aside > .stack {
        align-items: stretch;
        width: 100%;
      }

      .shell.collapsed .sidebarHeader {
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
        min-width: 0;
      }

      .sidebarToggle .desktopIcon {
        display: none;
      }

      .sidebarToggle .mobileIcon {
        display: grid;
      }

      .shell.collapsed .brand {
        flex: 0 1 auto;
        width: auto;
        margin-bottom: 0;
        overflow: visible;
      }

      .shell.collapsed .sidebarToggle {
        margin-left: auto;
      }

      .shell.collapsed .brand h2 {
        display: block;
        width: auto;
        max-width: 140px;
        visibility: visible;
        opacity: 1;
        overflow: hidden;
        pointer-events: auto;
      }

      .shell.collapsed .storeBadge,
      .shell.collapsed .languageSwitch,
      .shell.collapsed .navList,
      .shell.collapsed .sidebarLogout {
        display: none;
      }

      .shell.collapsed .navLabel {
        visibility: visible;
        opacity: 1;
      }

      .shell.collapsed .navItem {
        justify-content: flex-start;
        padding: 10px 12px;
      }

      main {
        padding: 22px;
      }

      .topbar {
        display: grid;
      }

      .topActions {
        justify-content: flex-start;
      }

      .panelHeader {
        display: grid;
      }

      .panelActions {
        justify-content: flex-start;
      }

      .paginationBar {
        align-items: flex-start;
      }

      .metricsHeader {
        justify-content: flex-start;
      }

      .periodControlGroup {
        justify-content: flex-start;
      }

      .announcementGrid {
        margin-left: 0;
      }

      input,
      select {
        font-size: 16px;
      }

      .grid,
      .formGrid,
      .settingsGrid,
      .sideStack .formGrid,
      .reward {
        grid-template-columns: 1fr;
      }

      .tableShell {
        overflow-x: auto;
      }
    }

    @media (max-width: 620px) {
      .landingNav {
        width: min(100% - 32px, 420px);
        padding: 18px 0;
      }

      .landingNav .brand {
        margin-bottom: 0;
      }

      .landingActions .languageSwitch {
        width: 92px;
      }

      .landingHero {
        width: min(100% - 32px, 420px);
        padding: 34px 0 52px;
      }

      .landingHero h1 {
        font-size: 54px;
      }

      .landingHero p {
        font-size: 17px;
      }

      .landingMetrics,
      .landingSplit,
      .blogPreview,
      .workflow,
      .landingCtaBand {
        padding-bottom: 48px;
      }

      .heroMockup {
        border-radius: 22px;
        padding: 18px;
      }

      .mockupWheel {
        width: 132px;
      }

      .finalCta {
        justify-items: start;
      }

      .blogHero,
      .blogArticle,
      .blogIndexGrid,
      .blogFunnel {
        width: min(100% - 32px, 420px);
      }

      .blogHero {
        padding-top: 42px;
      }

      .blogHero h1 {
        font-size: 48px;
      }

      .blogPreviewVisual {
        min-height: 260px;
      }

      .blogLayer {
        gap: 16px;
      }

      .blogLayerNumber {
        width: 64px;
        height: 64px;
        border-radius: 20px;
        font-size: 22px;
      }

      .login {
        position: fixed;
        inset: 0;
        align-items: center;
        justify-items: center;
        overflow-y: auto;
        padding: 18px;
      }

      .loginPanel {
        width: min(100%, 390px);
        gap: 16px;
        border-radius: 20px;
        padding: 26px 20px;
      }

      .loginPanel h1 {
        font-size: 34px;
      }

      .loginPanel p {
        font-size: 15px;
      }

      .loginPanel label {
        gap: 9px;
        font-size: 13px;
      }

      .loginPanel input {
        min-height: 50px;
        border-radius: 14px;
        font-size: 16px;
      }

      .loginPanel button {
        min-height: 50px;
        font-size: 16px;
      }

      .grid,
      .layout,
      .shell.collapsed .layout,
      .installGrid,
      .formGrid,
      .settingsGrid,
      .sideStack .formGrid,
      .reward {
        grid-template-columns: 1fr;
      }
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    void getFirebaseAnalytics();
    this.syncDocumentMetadata();
    void this.loadWidgetElement();
    window.addEventListener('popstate', this.handlePopState);
    this.announcementPreviewTimer = window.setInterval(() => {
      if (this.activeView === 'announcementBar' && this.announcementBar.countdownEnabled) {
        this.requestUpdate();
      }
    }, 1000);
    this.syncEmbeddedHaravanSession();
    if (this.session && this.scheduleSessionLogout(this.session.token)) {
      void this.loadData();
      return;
    }
    void this.tryHaravanAutoLogin();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.handlePopState);
    if (this.announcementPreviewTimer) window.clearInterval(this.announcementPreviewTimer);
    this.clearSessionExpiryTimer();
  }

  render() {
    if (this.route === 'blogIndex') return this.renderBlogIndex();
    if (this.route === 'blog') return this.renderBlogArticle();
    if (this.route === 'home') return this.renderLanding();
    if (!this.session) return this.renderLogin();
    if (!this.data) return this.renderDashboardLoading();
    const text = this.text;
    const cursorPreviewUrl = this.getCustomCursorPreviewUrl();
    const cursorPreviewActive =
      this.activeView === 'customCursor' && this.previewVisible && Boolean(cursorPreviewUrl);

    return html`
      ${this.renderAnnouncementScrollableTopPreview()}
      <div
        class=${`shell ${this.sidebarCollapsed ? 'collapsed' : ''} ${
          this.isAnnouncementTopPreviewVisible() ? 'announcementPreviewTopActive' : ''
        } ${cursorPreviewActive ? 'cursorPreviewActive' : ''}`}
        style=${cursorPreviewActive
          ? `--dashboard-cursor-preview: url('${cursorPreviewUrl}') 1 1`
          : ''}
      >
        <aside>
          <div class="stack">
            <div class="sidebarHeader">
              <div class="brand">
                <img class="brandMark" src="/dada-logo.png" alt="" />
                <h2>Dada Plugins</h2>
              </div>
              <button
                class="sidebarToggle"
                type="button"
                title=${this.sidebarCollapsed ? text.expandSidebar : text.collapseSidebar}
                aria-label=${this.sidebarCollapsed ? text.expandSidebar : text.collapseSidebar}
                @click=${this.toggleSidebar}
              >
                <span class="desktopIcon"
                  >${this.sidebarCollapsed ? this.iconPanelOpen() : this.iconPanelClose()}</span
                >
                <span class="mobileIcon"
                  >${this.sidebarCollapsed ? this.iconChevronDown() : this.iconChevronUp()}</span
                >
              </button>
            </div>
            <a
              class="storeBadge"
              href=${storefrontUrl(this.session.shopDomain)}
              target="_blank"
              rel="noreferrer"
            >
              ${this.session.shopDomain}
            </a>
            <div class="navList" aria-label=${text.dashboardSections}>
              ${this.navItem('spinWheel', text.spinTheWheel)}
              ${this.navItem('customCursor', text.customCursor)}
              ${this.navItem('currencyConverter', text.currencyConverter)}
              ${this.navItem('announcementBar', text.announcementBar)}
              ${this.navItem('shoppableVideos', text.shoppableVideos)}
              ${this.navItem('productCountdown', text.productCountdown)}
              ${this.shouldShowInstallationSection()
                ? this.navItem('installation', text.installation)
                : nothing}
              ${this.navItem('settings', text.settings)}
            </div>
            ${this.renderLanguageSwitch()}
            ${this.isHaravanSession()
              ? nothing
              : html`<button class="secondary sidebarLogout" type="button" @click=${this.logout}>
                  ${this.iconLogout()} <span class="logoutLabel">${text.logout}</span>
                </button>`}
          </div>
        </aside>
        <main>
          <div class="topbar">
            <div>
              <p class="eyebrow">${text.merchantDashboard}</p>
              <h1>${this.renderViewTitle()}</h1>
              <p>${this.getViewDescription()}</p>
            </div>
            ${this.activeView === 'spinWheel' && this.activeSpinView === 'overview'
              ? html`<div class="topActions">
                  <button
                    class="iconButton"
                    type="button"
                    title=${this.previewVisible ? text.hidePreview : text.previewWidget}
                    aria-label=${this.previewVisible ? text.hidePreview : text.previewWidget}
                    ?disabled=${!this.data}
                    @click=${this.toggleWidgetPreview}
                  >
                    ${this.previewVisible ? this.iconEyeOff() : this.iconEye()}
                  </button>
                  <button
                    class="iconButton primary"
                    type="button"
                    title=${this.saving ? text.saving : text.saveChanges}
                    aria-label=${this.saving ? text.saving : text.saveChanges}
                    ?disabled=${this.saving || !this.data}
                    @click=${this.save}
                  >
                    ${this.iconSave()}
                  </button>
                </div>`
              : this.activeView === 'announcementBar' && this.activeAnnouncementBarId
                ? html`<div class="topActions">
                    <button
                      class="iconButton"
                      type="button"
                      title=${this.previewVisible ? text.hidePreview : text.previewWidget}
                      aria-label=${this.previewVisible ? text.hidePreview : text.previewWidget}
                      ?disabled=${!this.data}
                      @click=${this.toggleWidgetPreview}
                    >
                      ${this.previewVisible ? this.iconEyeOff() : this.iconEye()}
                    </button>
                    <button
                      class="iconButton primary"
                      type="button"
                      title=${this.saving ? text.saving : text.saveAnnouncement}
                      aria-label=${this.saving ? text.saving : text.saveAnnouncement}
                      ?disabled=${this.saving || !this.data}
                      @click=${this.saveAnnouncementBarSettings}
                    >
                      ${this.iconSave()}
                    </button>
                  </div>`
              : this.activeView === 'shoppableVideos' && this.activeShoppableCarouselId
                ? html`<div class="topActions">
                    <button
                      class="iconButton primary"
                      type="button"
                      title=${this.saving ? text.saving : text.saveShoppableVideos}
                      aria-label=${this.saving ? text.saving : text.saveShoppableVideos}
                      ?disabled=${this.saving || !this.data}
                      @click=${this.saveShoppableVideoSettings}
                    >
                      ${this.iconSave()}
                    </button>
                  </div>`
              : this.activeView === 'productCountdown' && this.activeProductCountdownId
                ? html`<div class="topActions">
                    <button
                      class="iconButton primary"
                      type="button"
                      title=${this.saving ? text.saving : text.saveProductCountdown}
                      aria-label=${this.saving ? text.saving : text.saveProductCountdown}
                      ?disabled=${this.saving || !this.data}
                      @click=${this.saveProductCountdownSettings}
                    >
                      ${this.iconSave()}
                    </button>
                  </div>`
                : this.activeView === 'currencyConverter'
                  ? html`<div class="topActions">
                      <button
                        class="iconButton primary"
                        type="button"
                        title=${this.saving ? text.saving : text.saveCurrency}
                        aria-label=${this.saving ? text.saving : text.saveCurrency}
                        ?disabled=${this.saving || !this.data}
                        @click=${this.saveCurrencyConverterSettings}
                      >
                        ${this.iconSave()}
                      </button>
                    </div>`
                  : this.activeView === 'customCursor'
                    ? html`<div class="topActions">
                        <button
                          class="iconButton previewButton"
                          type="button"
                          title=${this.previewVisible ? text.hidePreview : text.previewWidget}
                          aria-label=${this.previewVisible ? text.hidePreview : text.previewWidget}
                          ?disabled=${!this.data || !this.getCustomCursorPreviewUrl()}
                          @click=${this.toggleWidgetPreview}
                        >
                          ${this.previewVisible ? this.iconEyeOff() : this.iconEye()}
                        </button>
                        <button
                          class="iconButton primary"
                          type="button"
                          title=${this.saving ? text.saving : text.saveCursor}
                          aria-label=${this.saving ? text.saving : text.saveCursor}
                          ?disabled=${this.saving || !this.data}
                          @click=${this.saveCustomCursorSettings}
                        >
                          ${this.iconSave()}
                        </button>
                      </div>`
                    : nothing}
          </div>
          ${this.error ? html`<p class="notice error">${this.error}</p>` : nothing}
          ${this.saved ? html`<p class="notice saved">${this.saved}</p>` : nothing}
          ${this.activeView === 'spinWheel' ? this.renderSpinWheelSelector() : nothing}
          ${this.activeView === 'shoppableVideos' && this.activeShoppableCarouselId
            ? this.renderShoppableVideosSelector()
            : nothing}
          ${this.loading
            ? html`<div class="loading">${text.loadingDashboard}</div>`
            : this.activeView === 'installation'
              ? this.renderInstallation()
              : this.activeView === 'settings'
                ? this.renderSettings()
                : this.activeView === 'customCursor'
                  ? this.renderCustomCursor()
                  : this.activeView === 'currencyConverter'
                    ? this.renderCurrencyConverter()
                  : this.activeView === 'announcementBar'
                    ? this.renderAnnouncementBar()
                  : this.activeView === 'shoppableVideos'
                    ? this.renderShoppableVideos()
                  : this.activeView === 'productCountdown'
                    ? this.renderProductCountdown()
                    : this.activeSpinView === 'leads'
                      ? this.renderLeads()
                      : this.renderDashboard()}
        </main>
      </div>
      ${this.renderWidgetPreview()} ${this.renderPasswordModal()} ${this.renderRewardModal()}
      ${this.renderAnnouncementDeleteModal()} ${this.renderShoppableDeleteModal()}
      ${this.renderProductCountdownDeleteModal()}
    `;
  }

  private renderLanding() {
    const text = this.text;

    return html`
      <div class="landing">
        <header class="landingNav">
          <div class="brand">
            <img class="brandMark" src="/dada-logo.png" alt="" />
            <h2>Dada Plugins</h2>
          </div>
          <div class="landingActions">
            <a class="navLink" href="/blog">${text.blog}</a>
            ${this.renderLanguageSwitch()}
            <button class="accessButton" type="button" @click=${this.openDashboard}>
              ${text.access}
            </button>
          </div>
        </header>

        <main>
          <section class="landingSection landingHero">
            <div class="landingHeroCopy">
              <p class="eyebrow">${text.landingEyebrow}</p>
              <h1>${text.landingTitle}</h1>
              <p>${text.landingSubtitle}</p>
              <div class="landingCtas">
                <button type="button" @click=${this.openAccountRequestModal}>
                  ${text.startNow}
                </button>
                <button class="secondary" type="button" @click=${this.scrollToPlugins}>
                  ${text.landingSecondaryCta}
                </button>
              </div>
            </div>
            <div class="heroMockup" aria-hidden="true">
              <div class="mockupTop">
                <span class="mockupPill">Spin Wheel</span>
                <span class="mockupPill">Live</span>
              </div>
              <div class="mockupWheel"></div>
              <div class="mockupRows">
                <div class="mockupRow"><span>${text.leads}</span><strong>1,248</strong></div>
                <div class="mockupRow"><span>${text.clicks}</span><strong>8,920</strong></div>
                <div class="mockupRow"><span>${text.coupon}</span><strong>24%</strong></div>
              </div>
            </div>
          </section>

          <section class="landingSection landingMetrics">
            ${this.landingMetric(text.landingMetricRevenue, text.revenueGrowthBody)}
            ${this.landingMetric(text.landingMetricLeads, text.leadCaptureBody)}
            ${this.landingMetric(text.landingMetricOps, text.workflowOptimizationBody)}
          </section>

          <section class="landingBand" id="plugins">
            <div class="landingSection landingSplit">
              <div class="sectionIntro">
                <p class="eyebrow">${text.landingEyebrow}</p>
                <h2>${text.landingSectionTitle}</h2>
                <p>${text.landingSectionBody}</p>
              </div>
              <div class="landingGrid">
                ${this.landingCard('01', text.revenueGrowth, text.revenueGrowthBody)}
                ${this.landingCard('02', text.leadCapture, text.leadCaptureBody)}
                ${this.landingCard('03', text.workflowOptimization, text.workflowOptimizationBody)}
                ${this.landingCard('04', text.storefrontLayer, text.storefrontLayerBody)}
                ${this.landingCard('05', text.merchantControls, text.merchantControlsBody)}
                ${this.landingCard('06', text.actionableData, text.actionableDataBody)}
              </div>
            </div>
          </section>

          <section class="landingSection workflow">
            <div class="workflowPanel">
              <div>
                <p class="eyebrow">${text.howItWorks}</p>
                <h2>${text.workflowTitle}</h2>
                <p>${text.landingSectionBody}</p>
              </div>
              <div class="workflowSteps">
                ${this.workflowStep('1', text.workflowStep1)}
                ${this.workflowStep('2', text.workflowStep2)}
                ${this.workflowStep('3', text.workflowStep3)}
              </div>
            </div>
          </section>

          <section class="landingSection blogPreview" id="blog">
            <article class="blogPreviewCard">
              <div class="blogPreviewVisual" aria-hidden="true">
                <strong>4 tầng chuyển đổi</strong>
                <div class="blogPreviewFunnel">
                  <span>Impressions <b>90%</b></span>
                  <span>Clicks <b>30%</b></span>
                  <span>Captures <b>65%</b></span>
                  <span>Verified <b>94%</b></span>
                </div>
              </div>
              <div class="blogPreviewCopy">
                <p class="eyebrow">${text.blogEyebrow}</p>
                <h2>${text.blogSectionTitle}</h2>
                <p>${text.blogSectionBody}</p>
                <div class="blogPreviewMeta">
                  <span>${text.readTime}</span>
                  <span>Spin Wheel CRO</span>
                  <span>Haravan</span>
                </div>
                <div class="landingCtas">
                  <a class="accessButton" href=${blogSlug}>${text.readArticle}</a>
                  <a class="navLink" href="/blog">${text.blog}</a>
                </div>
              </div>
            </article>
          </section>

          <section class="landingSection landingCtaBand">
            <div class="finalCta">
              <div>
                <h2>${text.finalCtaTitle}</h2>
                <p>${text.finalCtaBody}</p>
              </div>
              <button type="button" @click=${this.openAccountRequestModal}>
                ${text.requestAccount}
              </button>
            </div>
          </section>
        </main>
      </div>
      ${this.renderAccountRequestModal()}
    `;
  }

  private renderBlogIndex() {
    const text = this.text;

    return html`
      <div class="blogPage">
        <header class="landingNav">
          <a class="brand" href="/" aria-label="Dada Plugins">
            <img class="brandMark" src="/dada-logo.png" alt="" />
            <h2>Dada Plugins</h2>
          </a>
          <div class="landingActions">
            <a class="navLink" href="/blog">${text.blog}</a>
            ${this.renderLanguageSwitch()}
            <button class="accessButton" type="button" @click=${this.openDashboard}>
              ${text.access}
            </button>
          </div>
        </header>

        <main>
          <section class="blogHero">
            <p class="eyebrow">${text.blogEyebrow}</p>
            <h1>${text.blogIndexTitle}</h1>
            <p>${text.blogIndexBody}</p>
          </section>

          <section class="blogIndexGrid" aria-label=${text.blogIndexTitle}>
            ${blogPosts.map(
              (post) =>
                html`<a class="blogIndexCard" href=${post.slug}>
                  <div class="blogPreviewMeta">
                    <span>${text[post.readTimeKey]}</span>
                    <span>${post.category}</span>
                    <span>${post.date}</span>
                  </div>
                  <h2>${post.title}</h2>
                  <p>${post.excerpt}</p>
                  <span class="accessButton">${text.readArticle}</span>
                </a>`,
            )}
          </section>
        </main>
      </div>
    `;
  }

  private renderBlogArticle() {
    const text = this.text;
    const currentPost = getCurrentBlogPost();

    if (currentPost?.kind === 'customCursor') return this.renderCustomCursorBlogArticle();

    return html`
      <div class="blogPage">
        <header class="landingNav">
          <a class="brand" href="/" aria-label="Dada Plugins">
            <img class="brandMark" src="/dada-logo.png" alt="" />
            <h2>Dada Plugins</h2>
          </a>
          <div class="landingActions">
            <a class="navLink" href="/blog">${text.blog}</a>
            ${this.renderLanguageSwitch()}
            <button class="accessButton" type="button" @click=${this.openDashboard}>
              ${text.access}
            </button>
          </div>
        </header>

        <main>
          <section class="blogHero">
            <p class="eyebrow">${text.blogEyebrow}</p>
            <h1>${blogArticle.title}</h1>
            <p>${blogArticle.deck}</p>
            <div class="blogMetaRow">
              <span>${text.readTime}</span>
              <span>Spin Wheel CRO</span>
              <span>Haravan</span>
              <span>Dada Plugins</span>
            </div>
            <div class="blogHeroActions">
              <a class="accessButton" href="/">${text.backHome}</a>
              <button type="button" @click=${this.openAccountRequestModal}>
                ${text.requestAccount}
              </button>
            </div>
          </section>

          <section class="blogFunnel" aria-label="Spin Wheel conversion funnel">
            ${blogArticle.funnel.map(
              ([step, title, body]) =>
                html`<div class="blogFunnelStep">
                  <span>${step}</span>
                  <strong>${title}</strong>
                  <small>${body}</small>
                </div>`,
            )}
          </section>

          <article class="blogArticle">
            ${blogArticle.intro.map((paragraph) => html`<p>${paragraph}</p>`)}

            <section class="blogBenchmark">
              <div>
                <span>Store chưa tối ưu</span>
                <strong>3%</strong>
                <span>1,000 impressions → ~30 leads</span>
              </div>
              <div class="blogBenchmarkArrow">→</div>
              <div>
                <span>Đã tối ưu với Dada</span>
                <strong>12-15%</strong>
                <span>1,000 impressions → 120-150 leads</span>
              </div>
            </section>

            ${blogArticle.layers.map(
              (layer) =>
                html`<section class="blogLayer">
                  <div class="blogLayerNumber">${layer.number}</div>
                  <div>
                    <h2>${layer.title}</h2>
                    <div class="blogLayerMetric">${layer.metric}</div>
                    <div class="blogQuestion">Câu hỏi cần trả lời: ${layer.question}</div>
                    <p>${layer.body}</p>
                    <ul>
                      ${layer.bullets.map((item) => html`<li>${item}</li>`)}
                    </ul>
                    <div class="blogCallout">${layer.callout}</div>
                  </div>
                </section>`,
            )}

            <section class="blogTableWrap">
              <h2>Bức Tranh Toàn Cảnh: Từ 1,000 Impression Đến Verified Lead</h2>
              <div class="blogTableScroller">
                <table class="blogTable">
                  <thead>
                    <tr>
                      <th>Tầng</th>
                      <th>Chưa tối ưu</th>
                      <th>Đã tối ưu với Dada</th>
                      <th>Đòn bẩy chính</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${blogArticle.comparisonRows.map(
                      (row) =>
                        html`<tr>
                          ${row.map((cell) => html`<td>${cell}</td>`)}
                        </tr>`,
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section class="blogCampaigns">
              <p class="eyebrow">Campaign relevance</p>
              <h2>Spin Wheel Theo Dịp: Theme Đúng Là Conversion Tốt Hơn</h2>
              <div class="blogCampaignGrid">
                ${blogArticle.campaigns.map(
                  ([occasion, theme, headline, prize]) =>
                    html`<div class="blogCampaignCard">
                      <strong>${occasion}</strong>
                      <span>${theme}</span>
                      <span>${headline}</span>
                      <span>${prize}</span>
                    </div>`,
                )}
              </div>
            </section>

            <section class="blogArticleCta">
              <div>
                <h2>Mỗi tầng là một đòn bẩy độc lập.</h2>
                <p>
                  Dada Plugins đặt trigger timing, headline, màu sắc, prize structure, form design
                  và verified lead dispatch vào cùng một dashboard. Không cần kỹ thuật, không cần
                  developer.
                </p>
              </div>
              <button type="button" @click=${this.openAccountRequestModal}>
                ${text.requestAccount}
              </button>
            </section>
          </article>
        </main>
      </div>
      ${this.renderAccountRequestModal()}
    `;
  }

  private renderCustomCursorBlogArticle() {
    const text = this.text;

    return html`
      <div class="blogPage">
        <header class="landingNav">
          <a class="brand" href="/" aria-label="Dada Plugins">
            <img class="brandMark" src="/dada-logo.png" alt="" />
            <h2>Dada Plugins</h2>
          </a>
          <div class="landingActions">
            <a class="navLink" href="/blog">${text.blog}</a>
            ${this.renderLanguageSwitch()}
            <button class="accessButton" type="button" @click=${this.openDashboard}>
              ${text.access}
            </button>
          </div>
        </header>

        <main>
          <section class="blogHero">
            <p class="eyebrow">${text.blogEyebrow}</p>
            <h1>${customCursorArticle.title}</h1>
            <p>${customCursorArticle.deck}</p>
            <div class="blogMetaRow">
              <span>${text.readTime}</span>
              <span>${customCursorBlogMeta.category}</span>
              <span>Haravan</span>
              <span>Dada Plugins</span>
            </div>
            <div class="blogHeroActions">
              <a class="accessButton" href="/">${text.backHome}</a>
              <button type="button" @click=${this.openAccountRequestModal}>
                ${text.requestAccount}
              </button>
            </div>
          </section>

          <section class="blogFunnel" aria-label="Custom cursor impact metrics">
            ${customCursorArticle.stats.map(
              ([value, label, body]) =>
                html`<div class="blogFunnelStep">
                  <span>${value}</span>
                  <strong>${label}</strong>
                  <small>${body}</small>
                </div>`,
            )}
          </section>

          <article class="blogArticle">
            ${customCursorArticle.intro.map((paragraph) => html`<p>${paragraph}</p>`)}

            ${customCursorArticle.sections.map(
              (section) =>
                html`<section class="blogLayer">
                  <div class="blogLayerNumber">${section.number}</div>
                  <div>
                    <h2>${section.title}</h2>
                    <div class="blogLayerMetric">${section.metric}</div>
                    <div class="blogQuestion">Câu hỏi cần trả lời: ${section.question}</div>
                    <p>${section.body}</p>
                    <ul>
                      ${section.bullets.map((item) => html`<li>${item}</li>`)}
                    </ul>
                    <div class="blogCallout">${section.callout}</div>
                  </div>
                </section>`,
            )}

            <section class="blogTableWrap">
              <h2>5 Loại Custom Cursor Và Khi Nào Dùng</h2>
              <div class="blogTableScroller">
                <table class="blogTable">
                  <thead>
                    <tr>
                      <th>Loại cursor</th>
                      <th>Use case tốt nhất</th>
                      <th>Ví dụ cụ thể</th>
                      <th>Tránh</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${customCursorArticle.cursorTypes.map(
                      (row) =>
                        html`<tr>
                          ${row.map((cell) => html`<td>${cell}</td>`)}
                        </tr>`,
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section class="blogCampaigns">
              <p class="eyebrow">Campaign cursor</p>
              <h2>Biến Con Trỏ Thành Trang Trí Theo Mùa</h2>
              <div class="blogCampaignGrid">
                ${customCursorArticle.campaigns.map(
                  ([occasion, idea]) =>
                    html`<div class="blogCampaignCard">
                      <strong>${occasion}</strong>
                      <span>${idea}</span>
                    </div>`,
                )}
              </div>
            </section>

            <section class="blogArticleCta">
              <div>
                <h2>5 phút. 1 upload. 1 brand detail khác biệt.</h2>
                <p>
                  Mở Dada Dashboard, chọn Custom Cursor, upload brand icon của bạn. Khách hàng
                  desktop sẽ thấy sự khác biệt ngay lần đầu quay lại storefront.
                </p>
              </div>
              <button type="button" @click=${this.openAccountRequestModal}>
                ${text.requestAccount}
              </button>
            </section>
          </article>
        </main>
        ${this.renderAccountRequestModal()}
      </div>
    `;
  }

  private landingMetric(title: string, body: string) {
    return html`<div class="landingMetric">
      <strong>${title}</strong>
      <span>${body}</span>
    </div>`;
  }

  private landingCard(mark: string, title: string, body: string) {
    return html`<article class="landingCard">
      <span class="landingCardMark">${mark}</span>
      <h2>${title}</h2>
      <p>${body}</p>
    </article>`;
  }

  private workflowStep(mark: string, body: string) {
    return html`<div class="workflowStep"><span>${mark}</span>${body}</div>`;
  }

  private handleAnalyticsPeriodChange = (event: Event): void => {
    const value = (event.target as HTMLSelectElement).value;
    if (!isAnalyticsPeriod(value)) return;
    this.analyticsPeriod = value;
    if (this.activeView === 'spinWheel' && this.activeSpinView === 'leads') this.leadsPage = 1;
    if (this.session) void this.refreshAnalytics();
  };

  private handleCustomAnalyticsFromChange = (event: Event): void => {
    this.customAnalyticsFrom = (event.target as HTMLInputElement).value;
    if (this.activeView === 'spinWheel' && this.activeSpinView === 'leads') this.leadsPage = 1;
    if (this.analyticsPeriod === 'custom' && this.session) void this.refreshAnalytics();
  };

  private handleCustomAnalyticsToChange = (event: Event): void => {
    this.customAnalyticsTo = (event.target as HTMLInputElement).value;
    if (this.activeView === 'spinWheel' && this.activeSpinView === 'leads') this.leadsPage = 1;
    if (this.analyticsPeriod === 'custom' && this.session) void this.refreshAnalytics();
  };

  private handleLeadsPageSizeChange = (event: Event): void => {
    const value = Number((event.target as HTMLSelectElement).value);
    this.leadsPageSize = [25, 50, 100].includes(value) ? value : 25;
    this.leadsPage = 1;
  };

  private setLeadsPage(page: number): void {
    if (!this.data) return;
    const totalPages = Math.max(1, Math.ceil(this.getPeriodFilteredLeads().length / this.leadsPageSize));
    this.leadsPage = Math.min(Math.max(page, 1), totalPages);
  }

  private setLeadsSort(key: LeadsSortKey): void {
    if (this.leadsSortKey === key) {
      this.leadsSortDirection = this.leadsSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.leadsSortKey = key;
      this.leadsSortDirection = key === 'received' ? 'desc' : 'asc';
    }
    this.leadsPage = 1;
  }

  private openDashboard = (): void => {
    this.navigate('dashboard');
  };

  private scrollToPlugins = (): void => {
    this.renderRoot
      .querySelector('#plugins')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  private openAccountRequestModal = (): void => {
    this.accountModalOpen = true;
    this.accountRequestError = '';
    this.accountRequestSuccess = false;
  };

  private closeAccountRequestModal = (): void => {
    this.accountModalOpen = false;
    this.accountStoreUrl = '';
    this.accountEmail = '';
    this.accountPhone = '';
    this.accountRequestLoading = false;
    this.accountRequestSuccess = false;
    this.accountRequestError = '';
  };

  private submitAccountRequest = async (): Promise<void> => {
    this.accountRequestLoading = true;
    this.accountRequestError = '';
    try {
      await requestAccount({
        storeUrl: this.accountStoreUrl,
        email: this.accountEmail,
        phone: this.accountPhone,
      });
      this.accountStoreUrl = '';
      this.accountEmail = '';
      this.accountPhone = '';
      this.accountRequestSuccess = true;
    } catch (error) {
      this.accountRequestError =
        error instanceof Error ? error.message : this.text.accountRequestFailed;
    } finally {
      this.accountRequestLoading = false;
    }
  };

  private handlePopState = (): void => {
    this.route = getCurrentRoute();
    this.syncDocumentMetadata();
  };

  private navigate(route: AppRoute): void {
    const path = route === 'dashboard' ? '/dashboard' : '/';
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
    this.route = route;
    this.syncDocumentMetadata();
  }

  private navItem(view: DashboardView, label: string) {
    return html`<button
      class=${`navItem ${this.activeView === view ? 'active' : ''}`}
      type="button"
      title=${this.sidebarCollapsed ? label : ''}
      aria-label=${label}
      @click=${() => this.setActiveView(view)}
    >
      <span class="navIcon" aria-hidden="true">${this.navIcon(view)}</span>
      <span class="navLabel labelWithBadge">${label}</span>
    </button>`;
  }

  private navIcon(view: DashboardView) {
    const icons: Record<DashboardView, unknown> = {
      spinWheel: html`<svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="2" />
        <path d="M12 4v6M12 14v6M4 12h6M14 12h6" />
      </svg>`,
      customCursor: html`<svg viewBox="0 0 24 24">
        <path d="m5 3 14 7-6 2.2L10.8 19 5 3Z" />
        <path d="m13 12 4 7" />
      </svg>`,
      currencyConverter: html`<svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" />
        <path d="M8 8h8" />
        <path d="M9 12h6" />
        <path d="M12 5v14" />
      </svg>`,
      announcementBar: html`<svg viewBox="0 0 24 24">
        <path d="M4 6h16v9H7l-3 3V6Z" />
        <path d="M8 10h8" />
        <path d="M8 13h5" />
      </svg>`,
      shoppableVideos: html`<svg viewBox="0 0 24 24">
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="m10 9 5 3-5 3V9Z" />
        <path d="M7 19h10" />
      </svg>`,
      productCountdown: html`<svg viewBox="0 0 24 24">
        <path d="M6 7h12v13H6z" />
        <path d="M9 7V5h6v2" />
        <circle cx="12" cy="13" r="3" />
        <path d="M12 11v2l1.5 1" />
      </svg>`,
      installation: html`<svg viewBox="0 0 24 24">
        <path d="M8 6h12" />
        <path d="M8 12h12" />
        <path d="M8 18h12" />
        <path d="M4 6h.01" />
        <path d="M4 12h.01" />
        <path d="M4 18h.01" />
      </svg>`,
      settings: html`<svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v3" />
        <path d="M12 19v3" />
        <path d="M4.9 4.9 7 7" />
        <path d="M17 17l2.1 2.1" />
        <path d="M2 12h3" />
        <path d="M19 12h3" />
        <path d="M4.9 19.1 7 17" />
        <path d="M17 7l2.1-2.1" />
      </svg>`,
    };

    return icons[view];
  }

  private renderSpinWheelSelector() {
    const text = this.text;
    return html`<div class="viewSelector" aria-label=${text.spinTheWheel}>
      <button
        class=${this.activeSpinView === 'overview' ? 'active' : ''}
        type="button"
        @click=${() => this.setSpinView('overview')}
      >
        ${text.overview}
      </button>
      <button
        class=${this.activeSpinView === 'leads' ? 'active' : ''}
        type="button"
        @click=${() => this.setSpinView('leads')}
      >
        ${text.leads}
      </button>
    </div>`;
  }

  private renderShoppableVideosSelector() {
    const text = this.text;
    return html`<div class="viewSelector" aria-label=${text.shoppableVideos}>
      <button
        class=${this.activeShoppableVideosView === 'settings' ? 'active' : ''}
        type="button"
        @click=${() => this.setShoppableVideosView('settings')}
      >
        ${text.shoppableTabSettings}
      </button>
      <button
        class=${this.activeShoppableVideosView === 'videos' ? 'active' : ''}
        type="button"
        @click=${() => this.setShoppableVideosView('videos')}
      >
        ${text.shoppableTabVideos}
      </button>
    </div>`;
  }

  private renderAnnouncementBarSelector() {
    const text = this.text;
    return html`<div class="viewSelector" aria-label=${text.announcementBar}>
      <button
        class=${this.activeAnnouncementView === 'bars' ? 'active' : ''}
        type="button"
        @click=${() => this.setAnnouncementBarView('bars')}
      >
        ${text.announcementTabBars}
      </button>
      <button
        class=${this.activeAnnouncementView === 'settings' ? 'active' : ''}
        type="button"
        @click=${() => this.setAnnouncementBarView('settings')}
      >
        ${text.announcementTabSettings}
      </button>
    </div>`;
  }

  private setActiveView(view: DashboardView): void {
    if (this.activeView !== view) this.clearNotices();
    this.activeView = view;
  }

  private setSpinView(view: SpinWheelView): void {
    if (this.activeSpinView !== view) this.clearNotices();
    this.activeSpinView = view;
    if (view === 'leads') this.leadsPage = 1;
  }

  private setShoppableVideosView(view: ShoppableVideosView): void {
    if (this.activeShoppableVideosView !== view) this.clearNotices();
    this.activeShoppableVideosView = view;
  }

  private setAnnouncementBarView(view: AnnouncementBarView): void {
    if (this.activeAnnouncementView !== view) this.clearNotices();
    this.activeAnnouncementView = view;
  }

  private clearNotices(): void {
    this.error = '';
    this.saved = '';
  }

  private toggleSidebar = (): void => {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    localStorage.setItem(sidebarCollapsedKey, this.sidebarCollapsed ? '1' : '0');
  };

  private getViewTitle(): string {
    const text = this.text;
    if (this.activeView === 'announcementBar') return text.announcementBar;
    if (this.activeView === 'shoppableVideos') return text.shoppableVideos;
    if (this.activeView === 'productCountdown') return text.productCountdown;
    if (this.activeView === 'currencyConverter') return text.currencyConverter;
    if (this.activeView === 'customCursor') return text.customCursor;
    if (this.activeView === 'installation') return text.installation;
    if (this.activeView === 'settings') return text.settings;
    return text.spinTheWheel;
  }

  private renderViewTitle() {
    return this.getViewTitle();
  }

  private getViewDescription(): string {
    const text = this.text;
    if (this.activeView === 'announcementBar') return text.announcementBarDescription;
    if (this.activeView === 'shoppableVideos') return text.shoppableVideosDescription;
    if (this.activeView === 'productCountdown') return text.productCountdownDescription;
    if (this.activeView === 'currencyConverter') return text.currencyConverterDescription;
    if (this.activeView === 'customCursor') return text.customCursorDescription;
    if (this.activeView === 'installation') return text.installationDescription;
    if (this.activeView === 'settings') {
      return this.isHaravanSession() ? text.connectionSettingsDescription : text.settingsDescription;
    }
    if (this.activeSpinView === 'leads') return text.leadsDescription;
    return text.overviewDescription;
  }

  private renderLogin() {
    const text = this.text;
    if (this.shouldShowHaravanSigningIn()) return this.renderHaravanSigningIn();

    return html`
      <div class="login">
        <form class="loginPanel stack" @submit=${this.submitLogin}>
          <div>
            <p class="eyebrow">${text.merchantAccess}</p>
            <h1>${text.storeLogin}</h1>
            <p>${text.loginDescription}</p>
          </div>
          <label>
            ${text.store}
            <input
              .value=${this.shopDomain}
              @input=${(event: Event) =>
                (this.shopDomain = (event.target as HTMLInputElement).value)}
            />
          </label>
          <label>
            ${text.userCode}
            <input
              autocomplete="username"
              .value=${this.username}
              @input=${(event: Event) => (this.username = (event.target as HTMLInputElement).value)}
            />
          </label>
          <label>
            ${text.password}
            <input
              autocomplete="current-password"
              type="password"
              .value=${this.password}
              @input=${(event: Event) => (this.password = (event.target as HTMLInputElement).value)}
            />
          </label>
          ${this.error ? html`<p class="error">${this.error}</p>` : nothing}
          <button type="submit" ?disabled=${this.loading}>
            ${this.loading ? text.signingIn : text.login}
          </button>
          ${this.renderLanguageSwitch()}
        </form>
      </div>
    `;
  }

  private renderHaravanSigningIn() {
    const text = this.text;
    return html`
      <div class="login">
        <div class="loginPanel stack" role="status" aria-live="polite">
          <div class="authSpinner" aria-hidden="true"></div>
          <div>
            <p class="eyebrow">${text.access}</p>
            <h1>${text.haravanSigningInTitle}</h1>
            <p>${text.haravanSigningInDescription}</p>
          </div>
        </div>
      </div>
    `;
  }

  private renderDashboardLoading() {
    return html`
      <div class="login">
        <div class="loginPanel stack" role="status" aria-live="polite">
          <div class="authSpinner" aria-hidden="true"></div>
          <div>
            <p class="eyebrow">${this.text.merchantDashboard}</p>
            <h1>${this.text.loadingDashboard}</h1>
          </div>
        </div>
      </div>
    `;
  }

  private renderLanguageSwitch() {
    return html`<div class="languageSwitch" aria-label=${this.text.language}>
      <button
        class=${this.language === 'en' ? 'active' : ''}
        type="button"
        aria-label="English"
        title="English"
        @click=${() => this.setLanguage('en')}
      >
        🇬🇧
      </button>
      <button
        class=${this.language === 'vi' ? 'active' : ''}
        type="button"
        aria-label="Tiếng Việt"
        title="Tiếng Việt"
        @click=${() => this.setLanguage('vi')}
      >
        🇻🇳
      </button>
    </div>`;
  }

  private iconEye() {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
      <circle cx="12" cy="12" r="3" />
    </svg>`;
  }

  private iconEyeOff() {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3l18 18" />
      <path d="M10.6 10.6a3 3 0 0 0 3.8 3.8" />
      <path d="M7.1 7.6C4.2 9.3 2.5 12 2.5 12s3.5 6 9.5 6c1.5 0 2.8-.4 4-1" />
      <path d="M12 6c6 0 9.5 6 9.5 6a15.7 15.7 0 0 1-2.2 2.7" />
    </svg>`;
  }

  private iconSave() {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 3h12l2 2v16H5z" />
      <path d="M8 3v6h8V3" />
      <path d="M8 21v-7h8v7" />
    </svg>`;
  }

  private iconPanelClose() {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5h16v14H4z" />
      <path d="M9 5v14" />
      <path d="m16 9-3 3 3 3" />
    </svg>`;
  }

  private iconPanelOpen() {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5h16v14H4z" />
      <path d="M9 5v14" />
      <path d="m13 9 3 3-3 3" />
    </svg>`;
  }

  private iconChevronUp() {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m6 15 6-6 6 6" />
    </svg>`;
  }

  private iconChevronDown() {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>`;
  }

  private iconArrowLeft() {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </svg>`;
  }

  private iconTrash() {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </svg>`;
  }

  private iconLogout() {
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 17l5-5-5-5" />
      <path d="M15 12H3" />
      <path d="M21 5v14" />
    </svg>`;
  }

  private renderAnalyticsPeriodControl(label?: string) {
    const text = this.text;

    return html`
      <div class="periodControlGroup">
        <label class="periodControl">
          ${label || text.analyticsPeriod}
          <select .value=${this.analyticsPeriod} @change=${this.handleAnalyticsPeriodChange}>
            <option value="today">${text.today}</option>
            <option value="yesterday">${text.yesterday}</option>
            <option value="last7">${text.last7Days}</option>
            <option value="last30">${text.last30Days}</option>
            <option value="custom">${text.customRange}</option>
          </select>
        </label>
        ${this.analyticsPeriod === 'custom'
          ? html`<div class="customRangeControl">
              <label>
                ${text.fromDate}
                <input
                  type="date"
                  .value=${this.customAnalyticsFrom}
                  @change=${this.handleCustomAnalyticsFromChange}
                />
              </label>
              <label>
                ${text.toDate}
                <input
                  type="date"
                  .value=${this.customAnalyticsTo}
                  @change=${this.handleCustomAnalyticsToChange}
                />
              </label>
            </div>`
          : nothing}
      </div>
    `;
  }

  private renderDashboard() {
    if (!this.data) return nothing;
    const text = this.text;
    const analytics = this.data.analytics;
    const config = this.data.campaign.wheel_config || {};
    const theme = config.theme || {};
    const copy = config.copy || {};
    const trigger = config.trigger || {};
    const launcherColors = getLauncherColors(theme);

    return html`
      <div class="metricsHeader">${this.renderAnalyticsPeriodControl()}</div>
      <section class="grid">
        ${this.metric(text.impressions, analytics.impressions, 'impressions')}
        ${this.metric(text.uniqueImpressions, analytics.uniqueImpressions, 'uniqueImpressions')}
        ${this.metric(text.clicks, analytics.clicks, 'clicks')}
        ${this.metric(text.uniqueClicks, analytics.uniqueClicks, 'uniqueClicks')}
        ${this.metric(text.leads, analytics.leads, 'leads')}
      </section>

      <section class="layout">
        <div class="stack">
          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.settings}</h2>
                <p>${text.triggerDescription}</p>
              </div>
              <span class=${`statusPill ${this.data.campaign.active ? 'live' : 'paused'}`}
                >${this.data.campaign.active ? text.live : text.paused}</span
              >
            </div>
            <div class="settingsGrid">
              <label class="check">
                <input
                  type="checkbox"
                  .checked=${this.data.campaign.active}
                  @change=${(event: Event) =>
                    this.updateCampaignField('active', (event.target as HTMLInputElement).checked)}
                />
                ${text.activeCampaign}
              </label>
              <label>
                ${text.delayMs}
                <input
                  type="number"
                  .value=${String(trigger.delayMs ?? 2500)}
                  @input=${(event: Event) =>
                    this.updateConfigObject(
                      trigger,
                      'delayMs',
                      Number((event.target as HTMLInputElement).value),
                    )}
                />
              </label>
              <label>
                ${text.showOnceDays}
                <input
                  type="number"
                  .value=${String(trigger.showOncePerDays ?? 7)}
                  @input=${(event: Event) =>
                    this.updateConfigObject(
                      trigger,
                      'showOncePerDays',
                      Number((event.target as HTMLInputElement).value),
                    )}
                />
              </label>
            </div>
          </div>

          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.copy}</h2>
                <p>${text.copyDescription}</p>
              </div>
            </div>
            <div class="formGrid">
              ${this.textInput(copy, 'title', text.title)}
              ${this.textInput(copy, 'subtitle', text.subtitle)}
              ${this.textInput(copy, 'successTitle', text.successTitle)}
              ${this.textInput(copy, 'consentLabel', text.consentLabel)}
              ${this.textInput(copy, 'submitLabel', text.submitLabel)}
              ${this.textInput(copy, 'spinningLabel', text.spinningLabel)}
            </div>
          </div>

          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.coupons}</h2>
                <p>${text.couponsDescription}</p>
              </div>
            </div>
            ${this.loadingHaravanDiscounts
              ? html`<p class="mutedText">${text.loadingCoupons}</p>`
              : this.haravanDiscountsError
                ? html`<p class="notice error">${this.haravanDiscountsError}</p>`
                : !this.haravanDiscounts.length
                  ? html`<p class="mutedText">${text.noHaravanCoupons}</p>`
                  : nothing}
            ${this.rewards.map((reward, index) => this.rewardEditor(reward, index))}
            <button class="primary compactButton" type="button" @click=${this.addReward}>
              ${text.addCoupon}
            </button>
          </div>
        </div>

        <div class=${`stack ${this.isHaravanSession() ? '' : 'sideStack'}`}>
          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.colors}</h2>
                <p>${text.colorsDescription}</p>
              </div>
            </div>
            <div class="formGrid">
              ${this.colorInput(theme, 'primaryColor', text.primary)}
              ${this.colorInput(theme, 'accentColor', text.accent)}
              ${this.colorInput(theme, 'pointerColor', text.pointer)}
              ${this.colorInput(theme, 'surfaceColor', text.surface)}
              ${this.colorInput(theme, 'textColor', text.text)}
              ${this.colorInput(theme, 'buttonTextColor', text.buttonText)}
            </div>
          </div>

          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.halfWheel}</h2>
                <p>${text.halfWheelDescription}</p>
              </div>
            </div>
            <div class="formGrid">
              ${this.textInput(copy, 'triggerLabel', text.launcherText)}
              ${launcherColors.map((color, index) =>
                this.launcherColorInput(theme, index, color, text),
              )}
              ${this.colorInput(theme, 'launcherPointerColor', text.launcherPointerColor)}
              ${this.colorInput(theme, 'launcherTextColor', text.launcherTextColor)}
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private renderWidgetPreview() {
    if (!this.previewVisible || !this.data) {
      return nothing;
    }

    if (this.activeView === 'announcementBar' && this.activeAnnouncementBarId) {
      if (this.isAnnouncementScrollableTopPreviewVisible()) return nothing;
      return this.renderAnnouncementSitePreview();
    }

    if (this.activeView !== 'spinWheel' || this.activeSpinView !== 'overview') return nothing;

    return html`
      <haravan-spin-wheel
        preview
        .previewConfig=${this.getWidgetPreviewConfig()}
      ></haravan-spin-wheel>
    `;
  }

  private renderAnnouncementScrollableTopPreview() {
    if (!this.isAnnouncementScrollableTopPreviewVisible()) return nothing;
    return this.renderAnnouncementSitePreview();
  }

  private renderAnnouncementSitePreview() {
    const text = this.text;
    const countdownLabel = getCountdownPreview(this.announcementBar.countdownEndsAt);
    const iconUrl = this.getAnnouncementIconPreviewUrl();

    return html`<div
      class=${`announcementSitePreview ${this.announcementBar.placement} ${this.announcementBar.alignment} ${this.announcementBar.behavior} effect-${this.announcementBar.backgroundEffect}`}
      style=${`--announcement-bg: ${this.announcementBar.backgroundColor}; --announcement-text: ${this.announcementBar.textColor}; --announcement-cta-bg: ${this.announcementBar.ctaBackgroundColor}; --announcement-cta-text: ${this.announcementBar.ctaTextColor}; --announcement-cta-radius: ${this.announcementBar.ctaCornerRadius}px; --announcement-cta-border-size: ${this.announcementBar.ctaBorderSize}px; --announcement-cta-border-color: ${this.announcementBar.ctaBorderColor}; --announcement-dismiss: ${getReadableTextColor(this.announcementBar.backgroundColor)};`}
    >
      <div class="announcementPreviewCopy">
        ${iconUrl ? html`<img class="announcementPreviewIcon" src=${iconUrl} alt="" />` : nothing}
        <strong>${this.announcementBar.title || text.announcementTitle}</strong>
        <span>${this.announcementBar.subtitle || text.announcementSubtitle}</span>
      </div>
      ${this.announcementBar.countdownEnabled && this.announcementBar.showCountdown
        ? html`<div class="announcementCountdown">${countdownLabel}</div>`
        : nothing}
      ${this.announcementBar.ctaEnabled
        ? html`<span class="announcementCtaPreview">${this.announcementBar.ctaText}</span>`
        : nothing}
      ${this.announcementBar.dismissEnabled
        ? html`<span class="announcementDismissPreview" aria-hidden="true"></span>`
        : nothing}
    </div>`;
  }

  private isAnnouncementTopPreviewVisible(): boolean {
    return (
      this.previewVisible &&
      this.activeView === 'announcementBar' &&
      Boolean(this.activeAnnouncementBarId) &&
      this.announcementBar.behavior === 'fixed' &&
      this.announcementBar.placement === 'top'
    );
  }

  private isAnnouncementScrollableTopPreviewVisible(): boolean {
    return (
      this.previewVisible &&
      this.activeView === 'announcementBar' &&
      Boolean(this.activeAnnouncementBarId) &&
      this.announcementBar.behavior === 'scrollable' &&
      this.announcementBar.placement === 'top'
    );
  }

  private getWidgetPreviewConfig(): CampaignConfig {
    const campaign = this.data?.campaign;
    const config = campaign?.wheel_config || {};
    const theme = config.theme || {};
    const copy = config.copy || {};
    const trigger = config.trigger || {};
    const form = config.form || {};
    const segments: SpinWheelSegment[] = this.rewards.map((reward, index) => {
      const color = colorVar(reward.color, '#ffffff');
      return {
        id: reward.id || `preview-${index}`,
        label: reward.label,
        color,
        textColor: getReadableTextColor(color),
      };
    });

    return {
      campaignId: campaign?.id || 'dashboard-preview',
      shopDomain: this.session?.shopDomain || 'dashboard-preview',
      active: true,
      segments,
      theme: {
        primaryColor: colorVar(theme.primaryColor, '#106d63'),
        accentColor: colorVar(theme.accentColor, '#f59e0b'),
        textColor: colorVar(theme.textColor, '#172026'),
        mutedTextColor: colorVar(theme.mutedTextColor, '#64748b'),
        surfaceColor: colorVar(theme.surfaceColor, '#ffffff'),
        overlayColor:
          typeof theme.overlayColor === 'string' ? theme.overlayColor : 'rgba(15, 23, 42, 0.58)',
        borderColor: colorVar(theme.borderColor, '#d8e1e8'),
        pointerColor: colorVar(theme.pointerColor, '#ef4444'),
        buttonTextColor: colorVar(theme.buttonTextColor, '#ffffff'),
        launcherColors: getLauncherColors(theme),
        launcherPointerColor: colorVar(theme.launcherPointerColor, '#ef4444'),
        launcherDividerColor: colorVar(theme.launcherDividerColor, '#ffffff'),
        launcherTextColor: colorVar(theme.launcherTextColor, '#ffffff'),
      },
      copy: {
        title: stringConfig(copy.title, 'Spin for your coupon'),
        subtitle: stringConfig(copy.subtitle, 'Enter your email and phone to get one coupon.'),
        emailPlaceholder: stringConfig(copy.emailPlaceholder, 'Email address'),
        phonePlaceholder: stringConfig(copy.phonePlaceholder, 'Phone number'),
        consentLabel: stringConfig(copy.consentLabel, 'I agree to receive offers from this store.'),
        submitLabel: stringConfig(copy.submitLabel, 'Spin now'),
        spinningLabel: stringConfig(copy.spinningLabel, 'Finding your reward...'),
        successTitle: stringConfig(copy.successTitle, 'Your coupon is ready'),
        couponLabel: stringConfig(copy.couponLabel, 'Coupon code'),
        closeLabel: stringConfig(copy.closeLabel, 'Close'),
        triggerLabel: stringConfig(copy.triggerLabel, 'Spin to win'),
        unavailableTitle: stringConfig(copy.unavailableTitle, 'Campaign unavailable'),
        unavailableMessage: stringConfig(
          copy.unavailableMessage,
          'This promotion is not active right now.',
        ),
      },
      trigger: {
        mode: 'manual',
        delayMs: 0,
        exitIntentEnabled: Boolean(trigger.exitIntentEnabled ?? true),
        showOncePerDays: 0,
        mobileScrollPercent: numberConfig(trigger.mobileScrollPercent, 60),
      },
      form: {
        requiredFields: normalizeRequiredFields(form.requiredFields),
        consentRequired: Boolean(form.consentRequired ?? true),
      },
    };
  }

  private renderLeads() {
    if (!this.data) return nothing;
    const text = this.text;
    const filteredLeads = this.getPeriodFilteredLeads();
    const totalLeads = filteredLeads.length;
    const totalPages = Math.max(1, Math.ceil(totalLeads / this.leadsPageSize));
    const currentPage = Math.min(this.leadsPage, totalPages);
    const pageStart = (currentPage - 1) * this.leadsPageSize;
    const sortedLeads = this.getSortedLeads(filteredLeads);
    const visibleLeads = sortedLeads.slice(pageStart, pageStart + this.leadsPageSize);
    if (!this.data.leads.length) {
      return html`<div class="panel emptyState">
        <div>
          <h2>${text.noLeadsYet}</h2>
          <p>${text.noLeadsDescription}</p>
        </div>
      </div>`;
    }

    return html`
      <section class="panel stack">
        <div class="panelHeader">
          <div>
            <h2>${text.collectedContacts}</h2>
            <p>${totalLeads.toLocaleString()} ${text.latestLeads}</p>
          </div>
          <div class="panelActions">
            <button
              class="secondary exportButton"
              type="button"
              ?disabled=${this.checkingOrderConversions}
              @click=${this.checkLeadOrderConversions}
            >
              ${this.checkingOrderConversions
                ? text.checkingOrderConversion
                : text.checkOrderConversion}
            </button>
            <button class="secondary exportButton" type="button" @click=${this.exportLeadsCsv}>
              ${text.exportCsv}
            </button>
            <button class="secondary exportButton" type="button" @click=${this.printLeadsPdf}>
              ${text.printPdf}
            </button>
          </div>
        </div>
        ${this.renderAnalyticsPeriodControl(text.period)}
        ${!visibleLeads.length
          ? html`<div class="emptyState">
              <div>
                <h2>${text.noLeadsYet}</h2>
                <p>${text.noLeadsDescription}</p>
              </div>
            </div>`
          : html`
              <div class="tableShell">
                <table>
                  <thead>
                    <tr>
                      <th>${text.contact}</th>
                      <th>${text.prize}</th>
                      <th>${text.coupon}</th>
                      <th>${this.renderLeadsSortHeader('purchased', text.purchased)}</th>
                      <th>${this.renderLeadsSortHeader('purchaseAmount', text.purchaseAmount)}</th>
                      <th>${this.renderLeadsSortHeader('received', text.received)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${visibleLeads.map(
                      (lead) => html`
                        <tr>
                          <td>
                            <div class="contact">
                              <strong>${lead.email}</strong>
                              <span>${lead.phone}</span>
                            </div>
                          </td>
                          <td>
                            ${lead.reward_label || html`<span class="muted">${text.notAssigned}</span>`}
                          </td>
                          <td>
                            ${lead.coupon_code
                              ? html`<span class="couponPill">${lead.coupon_code}</span>`
                              : html`<span class="muted">${text.noCoupon}</span>`}
                          </td>
                          <td>
                            ${lead.purchased
                              ? html`<span class="statusPill success">${text.yes}</span>`
                              : html`<span class="statusPill">${text.no}</span>`}
                          </td>
                          <td>${formatCurrency(lead.purchase_amount || 0)}</td>
                          <td>${formatDate(lead.created_at, this.language)}</td>
                        </tr>
                      `,
                    )}
                  </tbody>
                </table>
              </div>
              <div class="paginationBar">
                <label class="pageSizeControl">
                  ${text.itemsPerPage}
                  <select .value=${String(this.leadsPageSize)} @change=${this.handleLeadsPageSizeChange}>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </label>
                <div class="pageControls">
                  <button
                    class="secondary pageButton"
                    type="button"
                    ?disabled=${currentPage <= 1}
                    @click=${() => this.setLeadsPage(currentPage - 1)}
                  >
                    ${text.previous}
                  </button>
                  <span>${text.page} ${currentPage} / ${totalPages}</span>
                  <button
                    class="secondary pageButton"
                    type="button"
                    ?disabled=${currentPage >= totalPages}
                    @click=${() => this.setLeadsPage(currentPage + 1)}
                  >
                    ${text.next}
                  </button>
                </div>
              </div>
            `}
      </section>
    `;
  }

  private renderLeadsSortHeader(key: LeadsSortKey, label: string) {
    const active = this.leadsSortKey === key;
    const indicator = this.leadsSortDirection === 'asc' ? '↑' : '↓';

    return html`<button class="sortHeader" type="button" @click=${() => this.setLeadsSort(key)}>
      <span>${label}</span>
      ${active ? html`<span class="sortIndicator" aria-hidden="true">${indicator}</span>` : nothing}
    </button>`;
  }

  private getSortedLeads(leads = this.getPeriodFilteredLeads()): LeadRow[] {
    const direction = this.leadsSortDirection === 'asc' ? 1 : -1;

    return [...leads].sort((left, right) => {
      const comparison = compareLeadValues(left, right, this.leadsSortKey);
      if (comparison !== 0) return comparison * direction;
      return compareLeadValues(left, right, 'received') * -1;
    });
  }

  private getPeriodFilteredLeads(): LeadRow[] {
    if (!this.data) return [];
    const window = getAnalyticsWindow(
      this.analyticsPeriod,
      this.customAnalyticsFrom,
      this.customAnalyticsTo,
    );
    const from = Date.parse(window.from);
    const to = Date.parse(window.to);
    if (!Number.isFinite(from) || !Number.isFinite(to)) return this.data.leads;
    return this.data.leads.filter((lead) => {
      const createdAt = Date.parse(lead.created_at);
      return Number.isFinite(createdAt) && createdAt >= from && createdAt < to;
    });
  }

  private renderProductCountdown() {
    const text = this.text;
    const activeItem = this.getActiveProductCountdownItem();
    if (!this.activeProductCountdownId || !activeItem) {
      return html`
        <section class="panel stack">
          <div class="panelHeader">
            <div>
              <h2>${text.productCountdownList}</h2>
              <p>${text.productCountdownListDescription}</p>
            </div>
            <button class="primary compactButton" type="button" @click=${this.createProductCountdown}>
              ${text.productCountdownCreate}
            </button>
          </div>
          <div class="announcementList">
            ${this.productCountdown.items.length
              ? this.productCountdown.items.map((item) => this.renderProductCountdownCard(item))
              : html`<p class="emptyState">${text.productCountdownNoItems}</p>`}
          </div>
        </section>
      `;
    }

    return html`
      <section class="stack">
        <div class="panel stack">
          <div class="panelHeader">
            <div class="announcementDetailTitle">
              <button
                class="announcementBackButton"
                type="button"
                title=${text.productCountdownBack}
                aria-label=${text.productCountdownBack}
                @click=${this.closeProductCountdownDetail}
              >
                ${this.iconArrowLeft()}
              </button>
              <div>
                <h2>${text.productCountdownSettings}</h2>
                <p>${text.productCountdownDescription}</p>
              </div>
            </div>
            <button
              class="announcementDeleteButton"
              type="button"
              title=${text.productCountdownDelete}
              aria-label=${text.productCountdownDelete}
              ?disabled=${this.saving}
              @click=${this.openProductCountdownDeleteModal}
            >
              ${this.iconTrash()}
            </button>
          </div>
          <label class="switchRow">
            <span>${text.activePlugin}</span>
            <input
              type="checkbox"
              .checked=${activeItem.active}
              @change=${(event: Event) =>
                this.updateProductCountdownItem({
                  active: (event.target as HTMLInputElement).checked,
                })}
            />
          </label>
          <div class="formGrid">
            ${this.renderProductCountdownProductPicker(activeItem)}
            <label>
              ${text.productCountdownStart}
              <input
                type="datetime-local"
                .value=${toDatetimeLocal(activeItem.startsAt)}
                @change=${(event: Event) =>
                  this.updateProductCountdownItem({
                    startsAt: fromDatetimeLocal((event.target as HTMLInputElement).value),
                  })}
              />
            </label>
            <label>
              ${text.productCountdownEnd}
              <input
                type="datetime-local"
                .value=${toDatetimeLocal(activeItem.endsAt)}
                @change=${(event: Event) =>
                  this.updateProductCountdownItem({
                    endsAt: fromDatetimeLocal((event.target as HTMLInputElement).value),
                  })}
              />
            </label>
            <label>
              ${text.productCountdownPosition}
              <select
                .value=${activeItem.position}
                @change=${(event: Event) =>
                  this.updateProductCountdownItem({
                    position: (event.target as HTMLSelectElement).value as ProductCountdownItem['position'],
                  })}
              >
                <option value="right">${text.productCountdownPositionRight}</option>
                <option value="left">${text.productCountdownPositionLeft}</option>
                <option value="above">${text.productCountdownPositionAbove}</option>
                <option value="below">${text.productCountdownPositionBelow}</option>
              </select>
            </label>
            <label>
              ${text.productCountdownBackgroundColor}
              <input
                type="color"
                .value=${activeItem.backgroundColor}
                @input=${(event: Event) =>
                  this.updateProductCountdownItem({
                    backgroundColor: (event.target as HTMLInputElement).value,
                  })}
              />
            </label>
            <label>
              ${text.productCountdownTextColor}
              <input
                type="color"
                .value=${activeItem.textColor}
                @input=${(event: Event) =>
                  this.updateProductCountdownItem({
                    textColor: (event.target as HTMLInputElement).value,
                  })}
              />
            </label>
            <label>
              ${text.productCountdownBorderRadius}
              <input
                type="number"
                min="0"
                max="999"
                .value=${String(activeItem.borderRadius)}
                @input=${(event: Event) =>
                  this.updateProductCountdownItem({
                    borderRadius: clampNumber((event.target as HTMLInputElement).value, 50, 0, 999),
                  })}
              />
            </label>
            <label>
              ${text.productCountdownBorderWidth}
              <input
                type="number"
                min="0"
                max="8"
                .value=${String(activeItem.borderWidth)}
                @input=${(event: Event) =>
                  this.updateProductCountdownItem({
                    borderWidth: clampNumber((event.target as HTMLInputElement).value, 0, 0, 8),
                  })}
              />
            </label>
            <label>
              ${text.productCountdownBorderColor}
              <input
                type="color"
                .value=${activeItem.borderColor}
                @input=${(event: Event) =>
                  this.updateProductCountdownItem({
                    borderColor: (event.target as HTMLInputElement).value,
                  })}
              />
            </label>
          </div>
          <div class="announcementCardSchedule">
            <span>${text.productCountdownProduct}</span>
            <strong>${activeItem.productTitle || text.productCountdownNoProduct}</strong>
          </div>
        </div>
      </section>
    `;
  }

  private renderProductCountdownCard(item: ProductCountdownItem) {
    const text = this.text;
    return html`<button
      class="announcementCard"
      type="button"
      @click=${() => this.openProductCountdown(item.id)}
    >
      <div class="announcementCardTop">
        <span class=${`statusPill ${item.active ? 'active' : 'inactive'}`}>
          ${item.active ? text.announcementActive : text.announcementInactive}
        </span>
        <span class="announcementCardAction">${text.productCountdownEdit}</span>
      </div>
      <div class="announcementCardCopy">
        <strong>${item.productTitle || text.productCountdownNoProduct}</strong>
        <span>${item.productUrl || text.productCountdownProduct}</span>
      </div>
      <div class="announcementCardSchedule">
        <span>${text.productCountdownSchedule}</span>
        <strong>
          ${item.startsAt ? `${text.announcementFrom}: ${formatCompactDateTime(item.startsAt)}` : text.productCountdownAlwaysActive}
          ${item.endsAt ? ` / ${text.announcementTo}: ${formatCompactDateTime(item.endsAt)}` : ''}
        </strong>
      </div>
    </button>`;
  }

  private renderProductCountdownProductPicker(activeItem: ProductCountdownItem) {
    const text = this.text;
    const buttonLabel = this.loadingShoppableProducts
      ? text.loading
      : activeItem.productTitle || text.productCountdownSelectProduct;

    return html`
      <div class="productPicker">
        <span>${text.productCountdownProduct}</span>
        <button
          class="productPickerButton"
          type="button"
          aria-haspopup="listbox"
          aria-expanded=${String(this.productCountdownPickerOpen)}
          @click=${this.toggleProductCountdownProductPicker}
        >
          <span>${buttonLabel}</span>
          ${this.iconChevronDown()}
        </button>
        ${this.productCountdownPickerOpen
          ? html`
              <div class="productPickerMenu" role="listbox">
                ${this.loadingShoppableProducts
                  ? html`<span class="productPickerEmpty">${text.loading}</span>`
                  : this.shoppableProducts.length
                    ? this.shoppableProducts.map(
                        (product) => html`
                          <button
                            class=${`productPickerOption ${
                              activeItem.productId === product.id ? 'active' : ''
                            }`}
                            type="button"
                            role="option"
                            aria-selected=${String(activeItem.productId === product.id)}
                            @click=${() => this.selectProductCountdownProduct(product.id)}
                          >
                            ${product.title}
                          </button>
                        `,
                      )
                    : html`
                        <button
                          class="productPickerOption"
                          type="button"
                          @click=${this.loadShoppableProducts}
                        >
                          ${text.productCountdownSelectProduct}
                        </button>
                      `}
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private renderShoppableProductPicker(item: ShoppableVideoItem, index: number) {
    const text = this.text;
    const isOpen = this.openShoppableProductPickerIndex === index;
    const buttonLabel = this.loadingShoppableProducts
      ? text.loading
      : item.productTitle || text.shoppableProduct;

    return html`
      <div class="productPicker">
        <span>${text.shoppableProduct}</span>
        <button
          class="productPickerButton"
          type="button"
          aria-haspopup="listbox"
          aria-expanded=${String(isOpen)}
          @click=${() => this.toggleShoppableProductPicker(index)}
        >
          <span>${buttonLabel}</span>
          ${this.iconChevronDown()}
        </button>
        ${isOpen
          ? html`
              <div class="productPickerMenu" role="listbox">
                ${this.loadingShoppableProducts
                  ? html`<span class="productPickerEmpty">${text.loading}</span>`
                  : this.shoppableProducts.length
                    ? this.shoppableProducts.map(
                        (product) => html`
                          <button
                            class=${`productPickerOption ${item.productId === product.id ? 'active' : ''}`}
                            type="button"
                            role="option"
                            aria-selected=${String(item.productId === product.id)}
                            @click=${() => this.selectShoppableProduct(index, product.id)}
                          >
                            ${product.title}
                          </button>
                        `,
                      )
                    : html`
                        <button
                          class="productPickerOption"
                          type="button"
                          @click=${this.loadShoppableProducts}
                        >
                          ${text.shoppableProduct}
                        </button>
                      `}
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private renderCustomCursor() {
    const text = this.text;
    const hasCursor = Boolean(this.cursorFile || (!this.cursorClearFile && this.customCursor.cursorUrl));
    const cursorStatus = hasCursor ? text.cursorFileSelected : text.noCursorFile;
    const cursorPreviewUrl = this.getCustomCursorPreviewUrl();

    return html`
      <section class="stack">
        <div class="stack">
          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.cursorSettings}</h2>
                <p>${text.cursorSettingsDescription}</p>
              </div>
            </div>
            <label class="switchRow">
              <span>${text.activePlugin}</span>
              <input
                type="checkbox"
                .checked=${this.customCursor.active}
                @change=${(event: Event) =>
                  (this.customCursor = {
                    ...this.customCursor,
                    active: (event.target as HTMLInputElement).checked,
                  })}
              />
            </label>
            <div class="filePicker">
              <span>${text.cursorFile}</span>
              <div
                class="filePickerButton"
                role="button"
                tabindex="0"
                @click=${this.openCursorFileDialog}
                @keydown=${this.handleCursorFilePickerKeydown}
              >
                <span class="cursorStatusGroup">
                  ${hasCursor
                    ? html`<button
                        class="removeCursorIcon"
                        type="button"
                        title=${text.removeCursorFile}
                        aria-label=${text.removeCursorFile}
                        @click=${this.clearCursorFileFromPicker}
                      >
                        ×
                      </button>`
                    : nothing}
                  ${cursorPreviewUrl
                    ? html`<img class="cursorStatusThumb" src=${cursorPreviewUrl} alt="" />`
                    : nothing}
                  <span>${cursorStatus}</span>
                </span>
                <span class="filePickerCta">${text.chooseCursorFile}</span>
              </div>
              <input
                id="cursor-file-input"
                type="file"
                accept="image/png,image/webp,image/gif,image/svg+xml,image/jpeg"
                @change=${this.handleCursorFileChange}
              />
            </div>
            <div class="fieldHint">${text.cursorSettingsDescription}</div>
          </div>
        </div>
      </section>
    `;
  }

  private renderCurrencyConverter() {
    const text = this.text;

    return html`
      <section class="stack">
        <div class="stack">
          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.currencySettings}</h2>
                <p>${text.currencySettingsDescription}</p>
              </div>
            </div>
            <label class="switchRow">
              <span>${text.activePlugin}</span>
              <input
                type="checkbox"
                .checked=${this.currencyConverter.active}
                @change=${(event: Event) =>
                  this.updateCurrencyConverter(
                    'active',
                    (event.target as HTMLInputElement).checked,
                  )}
              />
            </label>
            <div class="formGrid currencyBaseRow">
              <div class="currencyFieldBox">
                <span>${text.currencyBase}</span>
                <strong>VND</strong>
              </div>
            </div>
            <div class="formGrid currencySwitcherRow">
              <label class="switchRow">
                <span>${text.currencySwitcher}</span>
                <input
                  type="checkbox"
                  .checked=${this.currencyConverter.showCurrencySwitcher}
                  @change=${(event: Event) =>
                    this.updateCurrencyConverter(
                      'showCurrencySwitcher',
                      (event.target as HTMLInputElement).checked,
                  )}
                />
              </label>
              <label class="switchRow">
                <span>${text.currencySwitcherOutsideVietnam}</span>
                <input
                  type="checkbox"
                  .checked=${this.currencyConverter.showSwitcherOnlyOutsideVietnam}
                  @change=${(event: Event) =>
                    this.updateCurrencyConverter(
                      'showSwitcherOnlyOutsideVietnam',
                      (event.target as HTMLInputElement).checked,
                    )}
                />
              </label>
            </div>
            ${this.currencyConverter.showCurrencySwitcher
              ? html`<div class="formGrid currencyControls">
                  <label class="switchRow">
                    <span>${text.currencyShowSymbol}</span>
                    <input
                      type="checkbox"
                      .checked=${this.currencyConverter.showCurrencySymbol}
                      @change=${(event: Event) =>
                        this.updateCurrencyConverter(
                          'showCurrencySymbol',
                          (event.target as HTMLInputElement).checked,
                        )}
                    />
                  </label>
                  <label class="switchRow">
                    <span>${text.currencyShowCode}</span>
                    <input
                      type="checkbox"
                      .checked=${this.currencyConverter.showCurrencyCode}
                      @change=${(event: Event) =>
                        this.updateCurrencyConverter(
                          'showCurrencyCode',
                          (event.target as HTMLInputElement).checked,
                      )}
                    />
                  </label>
                </div>
                <div class="formGrid currencyPositionGrid">
                  <label>
                    ${text.currencySwitcherPosition}
                    <select
                      .value=${this.currencyConverter.switcherPosition}
                      @change=${(event: Event) =>
                        this.updateCurrencyConverter(
                          'switcherPosition',
                          (event.target as HTMLSelectElement).value as CurrencySwitcherPosition,
                        )}
                    >
                      <option value="top-left">${text.currencyTopLeft}</option>
                      <option value="top-right">${text.currencyTopRight}</option>
                      <option value="bottom-left">${text.currencyBottomLeft}</option>
                      <option value="bottom-right">${text.currencyBottomRight}</option>
                    </select>
                  </label>
                  <label>
                    ${text.currencyPrimaryColor}
                    <input
                      type="color"
                      .value=${this.currencyConverter.switcherPrimaryColor}
                      @input=${(event: Event) =>
                        this.updateCurrencyConverter(
                          'switcherPrimaryColor',
                          (event.target as HTMLInputElement).value,
                        )}
                    />
                  </label>
                </div>`
              : nothing}
            <div class="stack">
              <span class="fieldHint">${text.currencyTargets}</span>
              <div class="checkboxGrid">
                ${supportedDashboardCurrencies.map(
                  (currency) => html`<label class="switchRow compactSwitch">
                    <span>${currency}</span>
                    <input
                      type="checkbox"
                      .checked=${this.currencyConverter.targetCurrencies.includes(currency)}
                      @change=${(event: Event) =>
                        this.toggleCurrencyTarget(
                          currency,
                          (event.target as HTMLInputElement).checked,
                        )}
                    />
                  </label>`,
                )}
              </div>
              ${this.currencyConverter.ratesUpdatedAt
                ? html`<span class="fieldHint">
                    ${text.currencyRatesUpdated}:
                    ${formatDate(this.currencyConverter.ratesUpdatedAt, this.language)}
                  </span>`
                : nothing}
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private renderShoppableVideos() {
    const text = this.text;
    if (!this.activeShoppableCarouselId) return this.renderShoppableCarouselList();
    if (this.activeShoppableVideosView === 'videos') {
      return this.renderShoppableVideoItems();
    }

    return html`
      <section class="stack">
        <div class="panel stack">
          <div class="panelHeader">
            <div class="announcementDetailTitle">
              <button
                class="announcementBackButton"
                type="button"
                title=${text.shoppableBackToCarousels}
                aria-label=${text.shoppableBackToCarousels}
                @click=${this.closeShoppableCarouselDetail}
              >
                ${this.iconArrowLeft()}
              </button>
              <div>
                <h2>${text.shoppableSettings}</h2>
              <p>${text.shoppableSettingsDescription}</p>
              </div>
            </div>
            <button
              class="announcementDeleteButton"
              type="button"
              title=${text.shoppableDeleteCarousel}
              aria-label=${text.shoppableDeleteCarousel}
              ?disabled=${this.saving}
              @click=${this.openShoppableDeleteModal}
            >
              ${this.iconTrash()}
            </button>
          </div>
          <label class="switchRow">
            <span>${text.activePlugin}</span>
            <input
              type="checkbox"
              .checked=${this.shoppableVideos.active}
              @change=${(event: Event) =>
                this.updateShoppableVideos('active', (event.target as HTMLInputElement).checked)}
            />
          </label>
          <label>
            ${text.shoppableTitle}
            <input
              .value=${this.shoppableVideos.title}
              @input=${(event: Event) =>
                this.updateShoppableVideos('title', (event.target as HTMLInputElement).value)}
            />
          </label>
          <label>
            ${text.shoppablePageUrl}
            <input
              .value=${this.shoppableVideos.pageUrl}
              placeholder="https://store.myharavan.com/products/product-handle"
              @input=${(event: Event) =>
                this.updateShoppableVideos('pageUrl', (event.target as HTMLInputElement).value)}
              @change=${this.scanShoppableSections}
              @blur=${this.scanShoppableSections}
            />
          </label>
          <div class="stack">
            <div class="formGrid shoppablePlacementGrid">
              ${this.renderShoppablePlacementControl()}
            </div>
          </div>
          <div class="stack">
            <span class="fieldHint">${text.shoppableCardStyle}</span>
            <div class="formGrid">
              <label>
                ${text.shoppableCornerRadius}
                <input
                  type="number"
                  min="0"
                  max="48"
                  step="1"
                  .value=${String(this.shoppableVideos.cardCornerRadius)}
                  @input=${(event: Event) =>
                    this.updateShoppableVideos(
                      'cardCornerRadius',
                      Number((event.target as HTMLInputElement).value),
                    )}
                />
              </label>
              <label>
                ${text.shoppableBorderSize}
                <input
                  type="number"
                  min="0"
                  max="12"
                  step="1"
                  .value=${String(this.shoppableVideos.cardBorderSize)}
                  @input=${(event: Event) =>
                    this.updateShoppableVideos(
                      'cardBorderSize',
                      Number((event.target as HTMLInputElement).value),
                    )}
                />
              </label>
              <label>
                ${text.shoppableBorderColor}
                <input
                  type="color"
                  .value=${this.shoppableVideos.cardBorderColor}
                  @input=${(event: Event) =>
                    this.updateShoppableVideos(
                      'cardBorderColor',
                      (event.target as HTMLInputElement).value,
                    )}
                />
              </label>
              <label>
                ${text.shoppablePlaceholderColor}
                <input
                  type="color"
                  .value=${this.shoppableVideos.cardPlaceholderColor}
                  @input=${(event: Event) =>
                    this.updateShoppableVideos(
                      'cardPlaceholderColor',
                      (event.target as HTMLInputElement).value,
                    )}
                />
              </label>
            </div>
          </div>
          <div class="stack">
            <span class="fieldHint">${text.shoppableProductBarStyle}</span>
            <div class="formGrid">
              <label>
                ${text.shoppableProductBarPrimary}
                <input
                  type="color"
                  .value=${this.shoppableVideos.productBarPrimaryColor}
                  @input=${(event: Event) =>
                    this.updateShoppableVideos(
                      'productBarPrimaryColor',
                      (event.target as HTMLInputElement).value,
                    )}
                />
              </label>
              <label>
                ${text.shoppableProductBarSecondary}
                <input
                  type="color"
                  .value=${this.shoppableVideos.productBarSecondaryColor}
                  @input=${(event: Event) =>
                    this.updateShoppableVideos(
                      'productBarSecondaryColor',
                      (event.target as HTMLInputElement).value,
                    )}
                />
              </label>
              <label>
                ${text.shoppableProductBarBackground}
                <input
                  type="color"
                  .value=${this.shoppableVideos.productBarBackgroundColor}
                  @input=${(event: Event) =>
                    this.updateShoppableVideos(
                      'productBarBackgroundColor',
                      (event.target as HTMLInputElement).value,
                    )}
                />
              </label>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private renderShoppableCarouselList() {
    const text = this.text;
    return html`
      <section class="panel stack">
        <div class="panelHeader">
          <div>
            <h2>${text.shoppableCarousels}</h2>
            <p>${text.shoppableCarouselsDescription}</p>
          </div>
          <button class="primary compactButton" type="button" @click=${this.createShoppableCarousel}>
            ${text.shoppableCreateCarousel}
          </button>
        </div>
        <div class="announcementList">
          ${this.shoppableCarousels.length
            ? this.shoppableCarousels.map((carousel) => this.renderShoppableCarouselCard(carousel))
            : html`<p class="emptyState">${text.shoppableNoCarousels}</p>`}
        </div>
      </section>
    `;
  }

  private renderShoppableCarouselCard(carousel: ShoppableVideoCarousel) {
    const text = this.text;
    return html`<button
      class="announcementCard"
      type="button"
      @click=${() => this.openShoppableCarousel(carousel.id)}
    >
      <div class="announcementCardTop">
        <span class=${`statusPill ${carousel.active ? 'active' : 'inactive'}`}>
          ${carousel.active ? text.announcementActive : text.announcementInactive}
        </span>
        <span class="announcementCardAction">${text.shoppableEditCarousel}</span>
      </div>
      <div class="announcementCardCopy">
        <strong>${carousel.title || text.shoppableTitle}</strong>
        <span>${carousel.pageUrl || text.shoppablePageUrl}</span>
      </div>
      <div class="announcementCardSchedule">
        <span>${text.shoppableItems}</span>
        <strong>${carousel.items.length}</strong>
      </div>
    </button>`;
  }

  private renderShoppablePlacementControl() {
    const text = this.text;
    const placement = this.shoppableVideos.placement;
    const scannedValue =
      placement.preset === 'custom' && placement.customSelector
        ? `${placement.customMode === 'before' ? 'before' : 'after'}::${placement.customSelector}`
        : '';
    const options: ShoppablePlacementPreset[] = [
      'auto',
      'top',
      'after-hero',
      'after-product-grid',
      'after-product-images',
      'after-product-info',
      'before-recommendations',
      'before-footer',
      'custom',
    ];
    return html`
      <div class="shoppablePlacementRow">
        <label class="shoppablePlacementControl">
          ${text.shoppablePlacement}
          ${this.shoppablePageSections.length
            ? html`<select
                .value=${scannedValue}
                @change=${(event: Event) =>
                  this.selectScannedShoppableSection((event.target as HTMLSelectElement).value)}
              >
                <option value="">${text.shoppablePlacementAuto}</option>
                ${this.shoppablePageSections.flatMap((section) => [
                  html`<option
                    value=${`before::${section.selector}`}
                    ?selected=${scannedValue === `before::${section.selector}`}
                  >
                    ${text.shoppableBeforeSection} ${section.label}
                  </option>`,
                  html`<option
                    value=${`after::${section.selector}`}
                    ?selected=${scannedValue === `after::${section.selector}`}
                  >
                    ${text.shoppableAfterSection} ${section.label}
                  </option>`,
                ])}
              </select>`
            : html`<select
                .value=${placement.preset}
                @change=${(event: Event) =>
                  this.updateShoppablePlacement(
                    'preset',
                    (event.target as HTMLSelectElement).value as ShoppablePlacementPreset,
                  )}
              >
                ${options.map(
                  (option) => html`<option value=${option} ?selected=${placement.preset === option}>
                    ${this.getShoppablePlacementLabel(option)}
                  </option>`,
                )}
              </select>`}
        </label>
        <button
          class="secondary compactButton shoppablePlacementScan"
          type="button"
          ?disabled=${this.loadingShoppableSections || !this.shoppableVideos.pageUrl.trim()}
          @click=${this.scanShoppableSections}
        >
          ${this.loadingShoppableSections ? text.shoppableScanningSections : text.shoppableScanSections}
        </button>
        <div class="shoppablePlacementMeta">
          ${this.shoppableSectionsError
            ? html`<span class="errorText">${this.shoppableSectionsError}</span>`
            : this.shoppablePageSections.length
              ? html`<span class="fieldHint">${this.shoppablePageSections.length} ${text.shoppableSectionPlacement}</span>`
              : nothing}
        </div>
      </div>
      ${placement.preset === 'custom'
        ? html`<label class="shoppablePlacementControl">
            ${text.shoppablePlacementCustomSelector}
            <input
                .value=${placement.customSelector}
                placeholder=".section-selector"
                aria-label=${text.shoppablePlacementCustomSelector}
                @input=${(event: Event) =>
                  this.updateShoppablePlacement(
                    'customSelector',
                    (event.target as HTMLInputElement).value,
                  )}
              />
          </label>`
        : nothing}
    `;
  }

  private getShoppablePlacementLabel(preset: ShoppablePlacementPreset): string {
    const text = this.text;
    const labels: Record<ShoppablePlacementPreset, string> = {
      auto: text.shoppablePlacementAuto,
      top: text.shoppablePlacementTop,
      'after-hero': text.shoppablePlacementAfterHero,
      'before-footer': text.shoppablePlacementBeforeFooter,
      'after-product-grid': text.shoppablePlacementAfterProductGrid,
      'after-product-images': text.shoppablePlacementAfterProductImages,
      'after-product-info': text.shoppablePlacementAfterProductInfo,
      'before-recommendations': text.shoppablePlacementBeforeRecommendations,
      custom: text.shoppablePlacementCustom,
    };
    return labels[preset];
  }

  private renderShoppableVideoItems() {
    const text = this.text;

    return html`
      <section class="stack">
        <div class="panel stack">
          <div class="panelHeader">
            <div>
              <h2>${text.shoppableItems}</h2>
              <p>${text.shoppableNoProducts}</p>
            </div>
            <div class="topActions">
              <button class="secondary shoppableButton" type="button" @click=${this.addShoppableItem}>
                ${text.shoppableAddItem}
              </button>
            </div>
          </div>
          ${this.shoppableVideos.items.map((item, index) =>
            this.renderShoppableVideoItem(item, index),
          )}
        </div>
      </section>
    `;
  }

  private renderShoppableVideoItem(item: ShoppableVideoItem, index: number) {
    const text = this.text;
    const hasSelectedProduct = Boolean(item.productId && item.productTitle);
    return html`
      <div class="videoItemCard">
        <div class="videoItemHeader">
          <strong>${text.shoppableItems} ${index + 1}</strong>
          <button
            class="secondary shoppableButton"
            type="button"
            @click=${() => this.removeShoppableItem(index)}
          >
            ${text.shoppableRemoveItem}
          </button>
        </div>
        <div class="formGrid">
          <label>
            ${text.shoppableVideoUrl}
            <input
              .value=${item.videoUrl}
              placeholder="https://..."
              data-shoppable-video-url=${String(index)}
              @input=${(event: Event) =>
                this.updateShoppableItem(index, {
                  videoUrl: (event.target as HTMLInputElement).value,
                })}
            />
          </label>
          <label>
            ${text.shoppableItemTitle}
            <input
              .value=${item.title}
              @input=${(event: Event) =>
                this.updateShoppableItem(index, {
                  title: (event.target as HTMLInputElement).value,
                })}
            />
          </label>
          ${hasSelectedProduct ? nothing : this.renderShoppableProductPicker(item, index)}
        </div>
        ${hasSelectedProduct
          ? html`<div class="selectedProduct hasProduct">
              ${item.productImage ? html`<img src=${item.productImage} alt="" />` : nothing}
              <span>${item.productTitle}</span>
              <button
                class="removeSelectedProduct"
                type="button"
                title=${text.shoppableRemoveItem}
                aria-label=${text.shoppableRemoveItem}
                @click=${() => this.clearShoppableProduct(index)}
              >
                ×
              </button>
            </div>`
          : nothing}
      </div>
    `;
  }

  private renderAnnouncementBar() {
    if (this.activeAnnouncementBarId) return this.renderAnnouncementBarDetail();
    return html`<section class="stack">
      ${this.renderAnnouncementBarSelector()}
      ${this.activeAnnouncementView === 'settings'
        ? this.renderAnnouncementGlobalSettings()
        : this.renderAnnouncementBarList()}
    </section>`;
  }

  private renderAnnouncementGlobalSettings() {
    const text = this.text;
    return html`<section class="panel stack">
      <div class="panelHeader">
        <div>
          <h2>${text.announcementGlobalSettings}</h2>
          <p>${text.announcementGlobalSettingsDescription}</p>
        </div>
        <button
          class="primary compactButton"
          type="button"
          ?disabled=${this.saving || !this.data}
          @click=${this.saveAnnouncementGlobalSettings}
        >
          ${this.saving ? text.saving : text.saveAnnouncement}
        </button>
      </div>
      <label class="switchRow">
        <span>${text.announcementAllowMultiple}</span>
        <input
          type="checkbox"
          .checked=${this.announcementBarSettings.allowMultiple}
          @change=${(event: Event) =>
            this.updateAnnouncementBarSettings(
              'allowMultiple',
              (event.target as HTMLInputElement).checked,
            )}
        />
      </label>
      ${this.announcementBarSettings.allowMultiple
        ? html`<div class="formGrid">
            <label>
              ${text.announcementDisplayMode}
              <select
                .value=${this.announcementBarSettings.displayMode}
                @change=${(event: Event) =>
                  this.updateAnnouncementBarSettings(
                    'displayMode',
                    (event.target as HTMLSelectElement).value as AnnouncementBarsDisplayMode,
                  )}
              >
                <option value="stacked">${text.announcementDisplayStacked}</option>
                <option value="alternate">${text.announcementDisplayAlternate}</option>
              </select>
            </label>
            ${this.announcementBarSettings.displayMode === 'alternate'
              ? html`<label>
                    ${text.announcementRotationSeconds}
                    <input
                      type="number"
                      min="2"
                      max="120"
                      .value=${String(this.announcementBarSettings.rotationSeconds)}
                      @input=${(event: Event) =>
                        this.updateAnnouncementBarSettings(
                          'rotationSeconds',
                          clampNumber(
                            (event.target as HTMLInputElement).value,
                            this.announcementBarSettings.rotationSeconds,
                            2,
                            120,
                          ),
                        )}
                    />
                  </label>
                  <label>
                    ${text.announcementTransition}
                    <select
                      .value=${this.announcementBarSettings.transition}
                      @change=${(event: Event) =>
                        this.updateAnnouncementBarSettings(
                          'transition',
                          (event.target as HTMLSelectElement).value as AnnouncementBarsTransition,
                        )}
                    >
                      <option value="slide">${text.announcementTransitionSlide}</option>
                      <option value="none">${text.announcementTransitionNone}</option>
                      <option value="fade">${text.announcementTransitionFade}</option>
                    </select>
                  </label>`
              : nothing}
          </div>`
        : nothing}
    </section>`;
  }

  private renderAnnouncementBarList() {
    const text = this.text;
    const bars = this.announcementBars;

    return html`
      <section class="panel stack">
        <div class="panelHeader">
          <div>
            <h2>${text.announcementCampaigns}</h2>
            <p>${text.announcementCampaignsDescription}</p>
          </div>
          <button class="primary compactButton" type="button" @click=${this.createAnnouncementBar}>
            ${text.createAnnouncement}
          </button>
        </div>
        <div class="announcementList">
          ${bars.length
            ? bars.map((bar) => this.renderAnnouncementBarRow(bar))
            : html`<p class="emptyState">${text.noAnnouncementBars}</p>`}
        </div>
      </section>
    `;
  }

  private renderAnnouncementBarRow(bar: AnnouncementBarSettings) {
    const text = this.text;
    return html`<button class="announcementCard" type="button" @click=${() => this.openAnnouncementBar(bar.id)}>
      <div class="announcementCardTop">
        <span class=${`statusPill ${bar.active ? 'active' : 'inactive'}`}>
          ${bar.active ? text.announcementActive : text.announcementInactive}
        </span>
        <span class="announcementCardAction">${text.editAnnouncement}</span>
      </div>
      <div class="announcementCardCopy">
        <strong>${bar.title || text.announcementTitle}</strong>
        <span>${bar.subtitle || text.announcementSubtitle}</span>
      </div>
      <div class="announcementCardSchedule">
        <span>${text.announcementSchedule}</span>
        <strong>${this.renderAnnouncementSchedule(bar)}</strong>
      </div>
    </button>`;
  }

  private renderAnnouncementBarDetail() {
    const text = this.text;

    return html`
      <div class="metricsHeader">${this.renderAnalyticsPeriodControl()}</div>
      <div class="grid announcementGrid">
        ${this.metric(
          text.announcementViews,
          this.data?.announcementAnalytics?.views || 0,
          'views',
        )}
        ${this.metric(
          text.announcementClicks,
          this.data?.announcementAnalytics?.clicks || 0,
          'announcementClicks',
        )}
        ${this.metric(
          text.announcementDismisses,
          this.data?.announcementAnalytics?.dismisses || 0,
          'dismisses',
        )}
      </div>
      <section class="stack">
        <div class="stack">
          <div class="panel stack">
            <div class="panelHeader">
              <div class="announcementDetailTitle">
                <button
                  class="announcementBackButton"
                  type="button"
                  title=${text.backToAnnouncements}
                  aria-label=${text.backToAnnouncements}
                  @click=${this.closeAnnouncementBarDetail}
                >
                  ${this.iconArrowLeft()}
                </button>
                <div>
                  <h2>${text.announcementSettings}</h2>
                  <p>${text.announcementSettingsDescription}</p>
                </div>
              </div>
              <button
                class="announcementDeleteButton"
                type="button"
                title=${text.deleteAnnouncement}
                aria-label=${text.deleteAnnouncement}
                ?disabled=${this.saving}
                @click=${this.openAnnouncementDeleteModal}
              >
                ${this.iconTrash()}
              </button>
            </div>
            <label class="switchRow">
              <span>${text.activePlugin}</span>
              <input
                type="checkbox"
                .checked=${this.announcementBar.active}
                @change=${(event: Event) =>
                  this.updateAnnouncementBar('active', (event.target as HTMLInputElement).checked)}
              />
            </label>
            <div class="formGrid">
              ${this.announcementTextInput('title', text.announcementTitle)}
              ${this.announcementTextInput('subtitle', text.announcementSubtitle)}
              ${this.announcementTextInput('linkUrl', text.announcementLinkUrl)}
              <label class="switchRow inlineSwitch">
                <span>${text.announcementOpenInNewWindow}</span>
                <input
                  type="checkbox"
                  .checked=${this.announcementBar.openInNewWindow}
                  @change=${(event: Event) =>
                    this.updateAnnouncementBar(
                      'openInNewWindow',
                      (event.target as HTMLInputElement).checked,
                    )}
                />
              </label>
              <label>
                ${text.announcementAlign}
                <select
                  .value=${this.announcementBar.alignment}
                  @change=${(event: Event) =>
                    this.updateAnnouncementBar(
                      'alignment',
                      (event.target as HTMLSelectElement)
                        .value as AnnouncementBarSettings['alignment'],
                    )}
                >
                  <option value="left">${text.announcementAlignLeft}</option>
                  <option value="center">${text.announcementAlignCenter}</option>
                  <option value="right">${text.announcementAlignRight}</option>
                  <option value="sliding">${text.announcementAlignSliding}</option>
                </select>
              </label>
              <label>
                ${text.announcementPlacement}
                <select
                  .value=${this.announcementBar.placement}
                  @change=${(event: Event) =>
                    this.updateAnnouncementBar(
                      'placement',
                      (event.target as HTMLSelectElement)
                        .value as AnnouncementBarSettings['placement'],
                    )}
                >
                  <option value="top">${text.announcementTop}</option>
                  <option value="bottom">${text.announcementBottom}</option>
                </select>
              </label>
              <label>
                ${text.announcementBehavior}
                <select
                  .value=${this.announcementBar.behavior}
                  @change=${(event: Event) =>
                    this.updateAnnouncementBar(
                      'behavior',
                      (event.target as HTMLSelectElement)
                        .value as AnnouncementBarSettings['behavior'],
                    )}
                >
                  <option value="fixed">${text.announcementBehaviorFixed}</option>
                  <option value="scrollable">${text.announcementBehaviorScrollable}</option>
                </select>
              </label>
              ${this.announcementColorInput('backgroundColor', text.announcementBackground)}
              <label>
                ${text.announcementBackgroundEffect}
                <select
                  .value=${this.announcementBar.backgroundEffect}
                  @change=${(event: Event) =>
                    this.updateAnnouncementBar(
                      'backgroundEffect',
                      (event.target as HTMLSelectElement).value as AnnouncementBarSettings['backgroundEffect'],
                    )}
                >
                  <option value="soft">${text.announcementBackgroundEffectSoft}</option>
                  <option value="none">${text.announcementBackgroundEffectNone}</option>
                  <option value="spotlight">${text.announcementBackgroundEffectSpotlight}</option>
                </select>
              </label>
              ${this.announcementColorInput('textColor', text.announcementTextColor)}
            </div>
            ${this.renderAnnouncementIconPicker()}
            <div class="stack">
              <label class="switchRow">
                <span>${text.announcementCta}</span>
                <input
                  type="checkbox"
                  .checked=${this.announcementBar.ctaEnabled}
                  @change=${(event: Event) =>
                    this.updateAnnouncementBar(
                      'ctaEnabled',
                      (event.target as HTMLInputElement).checked,
                    )}
                />
              </label>
              ${this.announcementBar.ctaEnabled
                ? html`<div class="formGrid">
                    ${this.announcementTextInput('ctaText', text.announcementCtaText)}
                    ${this.announcementColorInput('ctaTextColor', text.announcementCtaTextColor)}
                    ${this.announcementColorInput(
                      'ctaBackgroundColor',
                      text.announcementCtaBackground,
                    )}
                    ${this.announcementNumberInput(
                      'ctaCornerRadius',
                      text.announcementCtaRadius,
                      0,
                      999,
                    )}
                    ${this.announcementNumberInput(
                      'ctaBorderSize',
                      text.announcementCtaBorderSize,
                      0,
                      8,
                    )}
                    ${this.announcementColorInput(
                      'ctaBorderColor',
                      text.announcementCtaBorderColor,
                    )}
                  </div>`
                : nothing}
            </div>
            <div class="stack">
              <label class="switchRow">
                <span>${text.announcementDismiss}</span>
                <input
                  type="checkbox"
                  .checked=${this.announcementBar.dismissEnabled}
                  @change=${(event: Event) =>
                    this.updateAnnouncementBar(
                      'dismissEnabled',
                      (event.target as HTMLInputElement).checked,
                    )}
                />
              </label>
              ${this.announcementBar.dismissEnabled
                ? html`<label class="switchRow">
                    <span>${text.announcementDismissOnce}</span>
                    <input
                      type="checkbox"
                      .checked=${this.announcementBar.dismissOnce}
                      @change=${(event: Event) =>
                        this.updateAnnouncementBar(
                          'dismissOnce',
                          (event.target as HTMLInputElement).checked,
                        )}
                    />
                  </label>`
                : nothing}
            </div>
            <label class="switchRow">
              <span>${text.announcementCountdown}</span>
              <input
                type="checkbox"
                .checked=${this.announcementBar.countdownEnabled}
                @change=${(event: Event) =>
                  this.updateAnnouncementBar(
                    'countdownEnabled',
                    (event.target as HTMLInputElement).checked,
                  )}
              />
            </label>
            ${this.announcementBar.countdownEnabled
              ? html`<label class="switchRow">
                    <span>${text.announcementShowCountdown}</span>
                    <input
                      type="checkbox"
                      .checked=${this.announcementBar.showCountdown}
                      @change=${(event: Event) =>
                        this.updateAnnouncementBar(
                          'showCountdown',
                          (event.target as HTMLInputElement).checked,
                        )}
                    />
                  </label>
                  <label>
                    ${text.announcementCountdownStartsAt}
                    <input
                      type="datetime-local"
                      .value=${toDatetimeLocal(this.announcementBar.countdownStartsAt)}
                      @input=${(event: Event) =>
                        this.updateAnnouncementBar(
                          'countdownStartsAt',
                          fromDatetimeLocal((event.target as HTMLInputElement).value),
                        )}
                    />
                  </label>
                  <label>
                    ${text.announcementCountdownEndsAt}
                    <input
                      type="datetime-local"
                      .value=${toDatetimeLocal(this.announcementBar.countdownEndsAt)}
                      @input=${(event: Event) =>
                        this.updateAnnouncementBar(
                          'countdownEndsAt',
                          fromDatetimeLocal((event.target as HTMLInputElement).value),
                        )}
                    />
                  </label>`
              : nothing}
          </div>
        </div>
      </section>
    `;
  }

  private renderAnnouncementIconPicker() {
    const text = this.text;
    const iconUrl = this.getAnnouncementIconPreviewUrl();
    const status = iconUrl ? text.announcementIconSelected : text.announcementNoIcon;

    return html`<div class="filePicker announcementIconPicker">
      <span>${text.announcementIcon}</span>
      <button class="filePickerButton" type="button" @click=${this.pickAnnouncementIconFile}>
        <span class="cursorStatusGroup">
          ${iconUrl
            ? html`<span
                class="removeCursorIcon"
                role="button"
                tabindex="0"
                title=${text.announcementRemoveIcon}
                aria-label=${text.announcementRemoveIcon}
                @click=${this.clearAnnouncementIcon}
                @keydown=${this.onAnnouncementIconRemoveKeydown}
              ></span>`
            : nothing}
          ${iconUrl ? html`<img class="cursorStatusThumb" src=${iconUrl} alt="" />` : nothing}
          <span>${status}</span>
        </span>
        <span class="filePickerCta">${text.announcementChooseIcon}</span>
      </button>
      <input
        id="announcement-icon-input"
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        @change=${this.onAnnouncementIconSelected}
      />
    </div>`;
  }

  private announcementTextInput<Key extends 'title' | 'subtitle' | 'linkUrl' | 'ctaText'>(
    key: Key,
    label: string,
  ) {
    return html`<label>
      ${label}
      <input
        .value=${String(this.announcementBar[key] || '')}
        @input=${(event: Event) =>
          this.updateAnnouncementBar(key, (event.target as HTMLInputElement).value)}
      />
    </label>`;
  }

  private announcementColorInput<
    Key extends
      | 'backgroundColor'
      | 'textColor'
      | 'ctaTextColor'
      | 'ctaBackgroundColor'
      | 'ctaBorderColor',
  >(
    key: Key,
    label: string,
  ) {
    return html`<label>
      ${label}
      <input
        type="color"
        .value=${this.announcementBar[key]}
        @input=${(event: Event) =>
          this.updateAnnouncementBar(key, (event.target as HTMLInputElement).value)}
      />
    </label>`;
  }

  private announcementNumberInput<
    Key extends 'ctaCornerRadius' | 'ctaBorderSize',
  >(key: Key, label: string, min: number, max: number) {
    return html`<label>
      ${label}
      <input
        type="number"
        min=${String(min)}
        max=${String(max)}
        .value=${String(this.announcementBar[key])}
        @input=${(event: Event) =>
          this.updateAnnouncementBar(
            key,
            clampNumber((event.target as HTMLInputElement).value, this.announcementBar[key], min, max),
          )}
      />
    </label>`;
  }

  private renderSettings() {
    if (!this.session) return nothing;
    if (this.isHaravanSession()) return this.renderHaravanConnectionSettings();
    const text = this.text;
    const username = this.settings?.username || this.session.username;

    return html`
      <section class="layout">
        <div class="stack">
          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.accountSettings}</h2>
                <p>${text.accountSettingsDescription}</p>
              </div>
            </div>
            <div class="formGrid">
              <label>
                ${text.username}
                <input .value=${username} readonly />
              </label>
            </div>
            <button class="secondary" type="button" @click=${this.openPasswordModal}>
              ${text.changePassword}
            </button>
          </div>
        </div>

        <div class="stack sideStack">
          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.apiSettings}</h2>
                <p>${text.apiSettingsDescription}</p>
              </div>
            </div>
            <div class="secretField">
              <label>
                ${text.haravanApiKey}
                <input
                  class=${`secretInput${this.apiKeyVisible ? '' : ' masked'}`}
                  type="text"
                  autocomplete="off"
                  autocapitalize="off"
                  autocorrect="off"
                  spellcheck="false"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  placeholder=${text.haravanApiKeyPlaceholder}
                  .value=${this.haravanApiKey}
                  @input=${(event: Event) =>
                    (this.haravanApiKey = (event.target as HTMLInputElement).value)}
                />
              </label>
              <button
                class="secretToggle"
                type="button"
                aria-label=${this.apiKeyVisible ? text.hidePreview : text.previewWidget}
                @click=${() => (this.apiKeyVisible = !this.apiKeyVisible)}
              >
                ${this.apiKeyVisible ? this.iconEyeOff() : this.iconEye()}
              </button>
            </div>
            <button type="button" ?disabled=${this.saving} @click=${this.saveSettings}>
              ${this.saving ? text.saving : text.saveSettings}
            </button>
          </div>
        </div>
      </section>
    `;
  }

  private renderHaravanConnectionSettings() {
    const text = this.text;
    const installation = this.data?.installation;
    const scopes = (installation?.scopes || '')
      .split(/\s+/)
      .map((scope) => scope.trim())
      .filter((scope) => !hiddenHaravanScopes.has(scope))
      .filter(Boolean);

    return html`
      <section class="layout">
        <div class="stack">
          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.haravanConnection}</h2>
                <p>${text.connectionSettingsDescription}</p>
              </div>
            </div>
            <div class="installationMeta">
              <span>${text.connectedViaHaravan}</span>
              <span>${text.connectedStore}: ${this.session?.shopDomain || '-'}</span>
              ${installation?.installedAt
                ? html`<span>
                    ${text.installationCheckedAt}: ${formatDate(installation.installedAt, this.language)}
                  </span>`
                : nothing}
              <span>
                ${text.scriptTagStatus}:
                ${installation?.scriptTagInstalled
                  ? text.scriptTagInstalled
                  : text.scriptTagNotInstalled}
              </span>
              ${installation?.scriptTagError ? html`<span>${installation.scriptTagError}</span>` : nothing}
            </div>
            ${scopes.length
              ? html`<div class="tagList" aria-label=${text.grantedScopes}>
                  ${scopes.map((scope) => html`<span>${scope}</span>`)}
                </div>`
              : nothing}
          </div>
        </div>
      </section>
    `;
  }

  private renderPasswordModal() {
    if (!this.passwordModalOpen) return nothing;
    const text = this.text;

    return html`
      <div class="modalOverlay" role="dialog" aria-modal="true" aria-label=${text.changePassword}>
        <div class="modalPanel stack">
          <div class="panelHeader">
            <div>
              <h2>${text.changePassword}</h2>
              <p>${text.passwordSettingsDescription}</p>
            </div>
          </div>
          <label>
            ${text.currentPassword}
            <input
              type="password"
              autocomplete="current-password"
              .value=${this.currentPassword}
              @input=${(event: Event) =>
                (this.currentPassword = (event.target as HTMLInputElement).value)}
            />
          </label>
          <label>
            ${text.newPassword}
            <input
              type="password"
              autocomplete="new-password"
              .value=${this.newPassword}
              @input=${(event: Event) =>
                (this.newPassword = (event.target as HTMLInputElement).value)}
            />
          </label>
          <label>
            ${text.confirmNewPassword}
            <input
              type="password"
              autocomplete="new-password"
              .value=${this.confirmPassword}
              @input=${(event: Event) =>
                (this.confirmPassword = (event.target as HTMLInputElement).value)}
            />
          </label>
          <div class="modalActions">
            <button class="secondary" type="button" @click=${this.closePasswordModal}>
              ${text.cancel}
            </button>
            <button type="button" ?disabled=${this.saving} @click=${this.savePassword}>
              ${this.saving ? text.saving : text.savePassword}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderRewardModal() {
    if (!this.rewardModalOpen) return nothing;
    const text = this.text;
    const hasCoupons = this.haravanDiscounts.length > 0;

    return html`
      <div class="modalOverlay" role="dialog" aria-modal="true" aria-label=${text.couponEditorTitle}>
        <div class="modalPanel rewardModal stack">
          <div class="panelHeader">
            <div>
              <h2>${text.couponEditorTitle}</h2>
              <p>${text.couponsDescription}</p>
            </div>
          </div>
          ${this.loadingHaravanDiscounts
            ? html`<p class="mutedText">${text.loadingCoupons}</p>`
            : !hasCoupons
              ? html`
                  <div class="stack compactStack">
                    <p class="notice error plainNotice">${text.createCouponInHaravan}</p>
                    ${this.haravanDiscountsError
                      ? html`<p class="mutedText">${this.haravanDiscountsError}</p>`
                      : nothing}
                    <button
                      class="secondary compactButton"
                      type="button"
                      @click=${this.loadHaravanDiscounts}
                    >
                      ${text.retryCoupons}
                    </button>
                  </div>
                `
              : html`
                  <label>
                    ${text.selectCoupon}
                    <select
                      .value=${this.rewardModalDraft.code}
                      @change=${(event: Event) =>
                        this.handleRewardModalCouponChange(
                          (event.target as HTMLSelectElement).value,
                        )}
                    >
                      <option value="">${text.selectCoupon}</option>
                      ${this.haravanDiscounts.map(
                        (discount) =>
                          html`<option value=${discount.code}>${discount.label}</option>`,
                      )}
                    </select>
                  </label>
                  <label>
                    ${text.couponCode}
                    <input readonly .value=${this.rewardModalDraft.code} />
                  </label>
                  <label>
                    ${text.wheelLabel}
                    <input
                      .value=${this.rewardModalDraft.label}
                      @input=${(event: Event) =>
                        this.updateRewardModalDraft(
                          'label',
                          (event.target as HTMLInputElement).value,
                        )}
                    />
                  </label>
                  <div class="formGrid compactFields">
                    <label>
                      ${text.slice}
                      <input
                        type="color"
                        .value=${this.rewardModalDraft.color}
                        @input=${(event: Event) =>
                          this.updateRewardModalDraft(
                            'color',
                            (event.target as HTMLInputElement).value,
                          )}
                      />
                    </label>
                    <label>
                      ${text.weight}
                      <input
                        type="number"
                        min="0"
                        .value=${String(this.rewardModalDraft.probability)}
                        @input=${(event: Event) =>
                          this.updateRewardModalDraft(
                            'probability',
                            Number((event.target as HTMLInputElement).value),
                          )}
                      />
                    </label>
                  </div>
                `}
          <div class="modalActions">
            <button class="secondary compactButton" type="button" @click=${this.closeRewardModal}>
              ${text.cancel}
            </button>
            ${hasCoupons
              ? html`
                  <button class="compactButton" type="button" @click=${this.saveRewardModal}>
                    ${text.saveChanges}
                  </button>
                `
              : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private renderAnnouncementDeleteModal() {
    if (!this.announcementDeleteModalOpen) return nothing;
    const text = this.text;

    return html`
      <div
        class="modalOverlay"
        role="dialog"
        aria-modal="true"
        aria-label=${text.deleteAnnouncement}
      >
        <div class="modalPanel stack deleteModal">
          <div class="panelHeader">
            <div>
              <h2>${text.deleteAnnouncement}</h2>
              <p>${text.announcementDeleteConfirm}</p>
            </div>
          </div>
          <div class="modalActions">
            <button class="secondary" type="button" @click=${this.closeAnnouncementDeleteModal}>
              ${text.cancel}
            </button>
            <button
              class="deleteConfirmButton"
              type="button"
              ?disabled=${this.saving}
              @click=${this.deleteAnnouncementBar}
            >
              ${this.saving ? text.saving : text.deleteAnnouncement}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderShoppableDeleteModal() {
    if (!this.shoppableDeleteModalOpen) return nothing;
    const text = this.text;

    return html`
      <div
        class="modalOverlay"
        role="dialog"
        aria-modal="true"
        aria-label=${text.shoppableDeleteCarousel}
      >
        <div class="modalPanel stack deleteModal">
          <div class="panelHeader">
            <div>
              <h2>${text.shoppableDeleteCarousel}</h2>
              <p>${text.shoppableDeleteConfirm}</p>
            </div>
          </div>
          <div class="modalActions">
            <button class="secondary" type="button" @click=${this.closeShoppableDeleteModal}>
              ${text.cancel}
            </button>
            <button
              class="deleteConfirmButton"
              type="button"
              ?disabled=${this.saving}
              @click=${this.deleteShoppableCarousel}
            >
              ${this.saving ? text.saving : text.shoppableDeleteCarousel}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderProductCountdownDeleteModal() {
    if (!this.productCountdownDeleteModalOpen) return nothing;
    const text = this.text;

    return html`
      <div
        class="modalOverlay"
        role="dialog"
        aria-modal="true"
        aria-label=${text.productCountdownDelete}
      >
        <div class="modalPanel stack deleteModal">
          <div class="panelHeader">
            <div>
              <h2>${text.productCountdownDelete}</h2>
              <p>${text.productCountdownDeleteConfirm}</p>
            </div>
          </div>
          <div class="modalActions">
            <button class="secondary" type="button" @click=${this.closeProductCountdownDeleteModal}>
              ${text.cancel}
            </button>
            <button
              class="deleteConfirmButton"
              type="button"
              ?disabled=${this.saving}
              @click=${this.deleteProductCountdown}
            >
              ${this.saving ? text.saving : text.productCountdownDelete}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderAccountRequestModal() {
    if (!this.accountModalOpen) return nothing;
    const text = this.text;

    return html`
      <div
        class="modalOverlay"
        role="dialog"
        aria-modal="true"
        aria-label=${text.requestAccountTitle}
      >
        <div class="modalPanel stack">
          <div class="panelHeader">
            <div>
              <h2>${text.requestAccountTitle}</h2>
              <p>
                ${this.accountRequestSuccess ? text.accountRequestSuccess : text.requestAccountBody}
              </p>
            </div>
          </div>

          ${this.accountRequestSuccess
            ? html`<div class="modalActions">
                <button type="button" @click=${this.closeAccountRequestModal}>${text.close}</button>
              </div>`
            : html`
                <label>
                  ${text.storeUrl}
                  <input
                    type="text"
                    autocomplete="url"
                    placeholder="store.myharavan.com"
                    .value=${this.accountStoreUrl}
                    @input=${(event: Event) =>
                      (this.accountStoreUrl = (event.target as HTMLInputElement).value)}
                  />
                </label>
                <label>
                  ${text.emailAddress}
                  <input
                    type="email"
                    autocomplete="email"
                    placeholder="merchant@example.com"
                    .value=${this.accountEmail}
                    @input=${(event: Event) =>
                      (this.accountEmail = (event.target as HTMLInputElement).value)}
                  />
                </label>
                <label>
                  ${text.phoneNumber}
                  <div class="phoneField">
                    <span class="phonePrefix">🇻🇳 +84</span>
                    <input
                      type="tel"
                      inputmode="tel"
                      autocomplete="tel"
                      placeholder=${text.localPhonePlaceholder}
                      .value=${this.accountPhone}
                      @input=${(event: Event) =>
                        (this.accountPhone = (event.target as HTMLInputElement).value)}
                    />
                  </div>
                </label>
                ${this.accountRequestError
                  ? html`<p class="notice error">${this.accountRequestError}</p>`
                  : nothing}
                <div class="modalActions">
                  <button
                    class="secondary"
                    type="button"
                    ?disabled=${this.accountRequestLoading}
                    @click=${this.closeAccountRequestModal}
                  >
                    ${text.cancel}
                  </button>
                  <button
                    type="button"
                    ?disabled=${this.accountRequestLoading}
                    @click=${this.submitAccountRequest}
                  >
                    ${this.accountRequestLoading ? text.loading : text.sendRequest}
                  </button>
                </div>
              `}
        </div>
      </div>
    `;
  }

  private renderInstallation() {
    const text = this.text;
    return html`
      <section class="installGrid">
        <div class="panel stack">
          <div class="panelHeader">
            <div>
              <h2>${text.haravanSetup}</h2>
              <p>${text.installOnce}</p>
            </div>
          </div>
          <ol class="steps">
            ${this.installStep(1, text.openHaravanAdmin, text.openHaravanAdminBody)}
            ${this.installStep(2, text.editThemeCode, text.editThemeCodeBody)}
            ${this.installStep(3, text.pasteScript, text.pasteScriptBody)}
            ${this.installStep(4, text.saveAndTest, text.saveAndTestBody)}
          </ol>
        </div>

        <div class="stack">
          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.widgetScript}</h2>
                <p>${text.widgetScriptBody}</p>
              </div>
            </div>
            <code class="codeBlock">${widgetSnippet}</code>
            <button class="secondary" type="button" @click=${this.copyInstallSnippet}>
              ${this.installCopied ? text.copied : text.copyScript}
            </button>
          </div>

          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.howItWorks}</h2>
                <p>${text.detectsDomain}</p>
              </div>
            </div>
            <ul class="hintList">
              <li>${text.noThemeEdit}</li>
              <li>${text.customDomain}</li>
            </ul>
          </div>

          <div class="panel stack">
            <div class="panelHeader">
              <div>
                <h2>${text.installationCheck}</h2>
                <p>${text.installationCheckBody}</p>
              </div>
            </div>
            ${this.installationCheck
              ? html`<div
                  class=${`installationStatus ${this.installationCheck.installed ? 'success' : 'error'}`}
                >
                  <strong>
                    ${this.installationCheck.installed
                      ? text.installationInstalled
                      : this.installationCheck.privateStorefront
                        ? text.installationPrivate
                        : text.installationMissing}
                  </strong>
                  <div class="installationMeta">
                    <span>${this.installationCheck.message}</span>
                    <span>${text.installationCheckedUrl}: ${this.installationCheck.checkedUrl}</span>
                    <span>${text.installationCheckedAt}: ${formatDate(this.installationCheck.checkedAt, this.language)}</span>
                  </div>
                </div>`
              : nothing}
            <button
              class="secondary"
              type="button"
              @click=${this.checkInstallation}
              ?disabled=${this.checkingInstallation}
            >
              ${this.checkingInstallation ? text.verifyingInstallation : text.verifyInstallation}
            </button>
          </div>
        </div>
      </section>
    `;
  }

  private installStep(number: number, title: string, body: string) {
    return html`<li class="step">
      <span class="stepNumber">${number}</span>
      <span>
        <strong>${title}</strong>
        <span class="muted">${body}</span>
      </span>
    </li>`;
  }

  private metric(label: string, value: number, icon: MetricIcon) {
    return html`<div class="metric">
      <span>${label}</span>
      <div class="metricValueRow">
        <strong>${this.formatMetricValue(value)}</strong>
        <span class="metricIcon" aria-hidden="true">${this.metricIcon(icon)}</span>
      </div>
    </div>`;
  }

  private formatMetricValue(value: number) {
    const absoluteValue = Math.abs(value);

    if (absoluteValue >= 1_000_000) {
      return `${this.formatCompactMetric(value / 1_000_000)}mln`;
    }

    if (absoluteValue >= 1_000) {
      return `${this.formatCompactMetric(value / 1_000)}k`;
    }

    return value.toLocaleString();
  }

  private formatCompactMetric(value: number) {
    const roundedValue = Math.round(value * 10) / 10;
    return Number.isInteger(roundedValue) ? `${roundedValue}` : roundedValue.toFixed(1);
  }

  private metricIcon(icon: MetricIcon) {
    const icons: Record<MetricIcon, unknown> = {
      impressions: html`<svg viewBox="0 0 24 24">
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
        <circle cx="12" cy="12" r="3" />
      </svg>`,
      uniqueImpressions: html`<svg viewBox="0 0 24 24">
        <path d="M16 21v-2a4 4 0 0 0-8 0v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>`,
      clicks: html`<svg viewBox="0 0 24 24">
        <path d="M5 3l14 8-6 2.2L10.8 19z" />
        <path d="M13 13l5 5" />
      </svg>`,
      views: html`<svg viewBox="0 0 24 24">
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
        <circle cx="12" cy="12" r="3" />
      </svg>`,
      announcementClicks: html`<svg viewBox="0 0 24 24">
        <path d="M5 3l14 8-6 2.2L10.8 19z" />
        <path d="M13 13l5 5" />
      </svg>`,
      dismisses: html`<svg viewBox="0 0 24 24">
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </svg>`,
      uniqueClicks: html`<svg viewBox="0 0 24 24">
        <path d="M4 4l8 16 2-7 6-2z" />
        <path d="M16 17h4" />
        <path d="M18 15v4" />
      </svg>`,
      leads: html`<svg viewBox="0 0 24 24">
        <path d="M4 6h16v12H4z" />
        <path d="M4 7l8 6 8-6" />
      </svg>`,
    };

    return icons[icon];
  }

  private textInput(object: Record<string, unknown>, key: string, label: string) {
    return html`<label>
      ${label}
      <input
        .value=${String(object[key] || '')}
        @input=${(event: Event) =>
          this.updateConfigObject(object, key, (event.target as HTMLInputElement).value)}
      />
    </label>`;
  }

  private colorInput(object: Record<string, unknown>, key: string, label: string) {
    return html`<label>
      ${label}
      <input
        type="color"
        .value=${colorVar(object[key], getColorFallback(key))}
        @input=${(event: Event) =>
          this.updateConfigObject(object, key, (event.target as HTMLInputElement).value)}
      />
    </label>`;
  }

  private launcherColorInput(
    theme: Record<string, unknown>,
    index: number,
    color: string,
    text: Record<'launcherSlice1' | 'launcherSlice2' | 'launcherSlice3', string>,
  ) {
    const labels = [text.launcherSlice1, text.launcherSlice2, text.launcherSlice3];

    return html`<label>
      ${labels[index]}
      <input
        type="color"
        .value=${color}
        @input=${(event: Event) =>
          this.updateLauncherColor(theme, index, (event.target as HTMLInputElement).value)}
      />
    </label>`;
  }

  private rewardEditor(reward: RewardDraft, index: number) {
    const text = this.text;
    const couponLabel =
      this.haravanDiscounts.find((discount) => discount.code === reward.code)?.label ||
      reward.code ||
      text.noCoupon;
    return html`
      <div class="reward">
        <div class="rewardInfo">
          <strong>${reward.label || couponLabel}</strong>
          <span>${couponLabel}</span>
        </div>
        <span class="rewardWeight">${text.weight}: ${reward.probability}</span>
        <span class="rewardSwatch" style=${`background:${reward.color || '#ffffff'}`}></span>
        <div class="rewardActions">
          <button class="secondary compactButton" type="button" @click=${() => this.openRewardModal(index)}>
            ${text.editCoupon}
          </button>
          <button class="danger compactButton" type="button" @click=${() => this.removeReward(index)}>
            ${text.deleteCoupon}
          </button>
        </div>
      </div>
    `;
  }

  private toggleWidgetPreview = (): void => {
    this.previewVisible = !this.previewVisible;
  };

  private getCustomCursorPreviewUrl(): string {
    return this.cursorFile?.dataUrl || (!this.cursorClearFile ? this.customCursor.cursorUrl : '');
  }

  private getAnnouncementIconPreviewUrl(): string {
    return (
      this.announcementIconFile?.dataUrl ||
      (!this.announcementClearIcon ? this.announcementBar.iconUrl || '' : '')
    );
  }

  private updateCampaignField<Key extends 'name' | 'active'>(
    key: Key,
    value: DashboardData['campaign'][Key],
  ): void {
    if (!this.data) return;
    if (key === 'active' && value === true && this.rewards.length < 2) {
      this.error = this.text.needTwoRewardsToActivate;
      this.requestUpdate();
      return;
    }
    this.data = {
      ...this.data,
      campaign: {
        ...this.data.campaign,
        [key]: value,
      },
    };
  }

  private updateConfigObject(
    object: Record<string, unknown>,
    key: string,
    value: string | number,
  ): void {
    object[key] = value;
    this.requestUpdate();
  }

  private updateLauncherColor(theme: Record<string, unknown>, index: number, value: string): void {
    const colors = getLauncherColors(theme);
    colors[index] = colorVar(value, colors[index]);
    theme.launcherColors = colors;
    this.requestUpdate();
  }

  private async loadWidgetElement(): Promise<void> {
    const widgetWindow = window as Window & {
      __HARAVAN_SPIN_WHEEL_DISABLE_AUTOMOUNT__?: boolean;
    };
    widgetWindow.__HARAVAN_SPIN_WHEEL_DISABLE_AUTOMOUNT__ = true;
    await import('../../frontend-widget/src/index');
  }

  private async submitLogin(event: Event): Promise<void> {
    event.preventDefault();
    this.loading = true;
    this.error = '';
    try {
      this.session = await login(this.shopDomain.trim(), this.username.trim(), this.password);
      localStorage.setItem(sessionKey, JSON.stringify(this.session));
      this.scheduleSessionLogout(this.session.token);
      await this.loadData();
    } catch (error) {
      this.error = error instanceof Error ? error.message : this.text.loginFailed;
    } finally {
      this.loading = false;
    }
  }

  private async tryHaravanAutoLogin(): Promise<void> {
    if (this.session || this.haravanAutoLoginAttempted || !isHaravanEmbeddedContext()) return;
    this.haravanAutoLoginAttempted = true;
    this.haravanAutoLoginLoading = true;
    this.loading = true;
    this.error = '';

    try {
      const sessionToken = await getHaravanSessionToken();
      this.session = await loginWithHaravanSession(sessionToken);
      localStorage.setItem(sessionKey, JSON.stringify(this.session));
      this.scheduleSessionLogout(this.session.token);
      await this.loadData();
    } catch (error) {
      this.session = null;
      localStorage.removeItem(sessionKey);
      this.error = error instanceof Error ? error.message : this.text.loginFailed;
    } finally {
      this.haravanAutoLoginLoading = false;
      this.loading = false;
    }
  }

  private syncEmbeddedHaravanSession(): void {
    if (!this.session || !isHaravanEmbeddedContext()) return;
    const shopOrigin = getHaravanShopOrigin();
    const sessionShop = this.session.shopDomain.toLowerCase();
    const shouldUseHaravanSession =
      this.session.authProvider === 'haravan' && (!shopOrigin || sessionShop === shopOrigin);

    if (shouldUseHaravanSession) return;
    this.session = null;
    localStorage.removeItem(sessionKey);
    this.clearSessionExpiryTimer();
  }

  private async loadData(): Promise<void> {
    if (!this.session) return;
    this.loading = true;
    this.error = '';
    try {
      this.data = await getDashboardData(
        this.session.token,
        getAnalyticsWindow(
          this.analyticsPeriod,
          this.customAnalyticsFrom,
          this.customAnalyticsTo,
        ),
        this.activeView === 'announcementBar' ? this.activeAnnouncementBarId : undefined,
      );
      void this.loadSettings();
      void this.loadHaravanDiscounts();
      this.data = {
        ...this.data,
        campaign: {
          ...this.data.campaign,
          wheel_config: withDashboardConfigDefaults(this.data.campaign.wheel_config),
        },
      };
      if (!this.shouldShowInstallationSection() && this.activeView === 'installation') {
        this.activeView = 'spinWheel';
      }
      this.customCursor = {
        active: Boolean(this.data.plugins?.customCursor?.active),
        cursorUrl: this.data.plugins?.customCursor?.cursorUrl || '',
        fileName: this.data.plugins?.customCursor?.fileName || '',
      };
      this.currencyConverter = normalizeCurrencyConverter(this.data.plugins?.currencyConverter);
      this.announcementBars = normalizeAnnouncementBars(this.data.plugins);
      this.announcementBarSettings = normalizeAnnouncementBarSettings(
        this.data.plugins?.announcementBarSettings,
      );
      this.announcementBar =
        this.announcementBars.find((bar) => bar.id === this.activeAnnouncementBarId) ||
        this.announcementBars[0] ||
        normalizeAnnouncementBar();
      if (!this.activeAnnouncementBarId && this.activeView !== 'announcementBar') {
        this.activeAnnouncementBarId = '';
      }
      this.shoppableVideos = normalizeShoppableVideos(this.data.plugins?.shoppableVideos);
      this.shoppableCarousels = this.shoppableVideos.carousels;
      if (
        this.activeShoppableCarouselId &&
        !this.shoppableCarousels.some((carousel) => carousel.id === this.activeShoppableCarouselId)
      ) {
        this.activeShoppableCarouselId = '';
      }
      if (this.activeShoppableCarouselId) {
        const selectedCarousel = this.shoppableCarousels.find(
          (carousel) => carousel.id === this.activeShoppableCarouselId,
        );
        if (selectedCarousel) {
          this.shoppableVideos = normalizeShoppableVideos({
            ...selectedCarousel,
            carousels: this.shoppableCarousels,
            activeCarouselId: this.activeShoppableCarouselId,
          });
        }
      }
      this.productCountdown = normalizeProductCountdown(this.data.plugins?.productCountdown);
      if (
        this.activeProductCountdownId &&
        !this.productCountdown.items.some((item) => item.id === this.activeProductCountdownId)
      ) {
        this.activeProductCountdownId = '';
      }
      this.cursorFile = undefined;
      this.cursorClearFile = false;
      this.announcementIconFile = undefined;
      this.announcementClearIcon = false;
      const couponsByReward = new Map(
        this.data.coupons.map((coupon) => [coupon.reward_id, coupon]),
      );
      const segmentColors = this.data.campaign.wheel_config.segmentColors || [];
      this.rewards = this.data.rewards.map((reward, index) => {
        const coupon = couponsByReward.get(reward.id);
        return {
          id: reward.id,
          label: reward.value,
          code: reward.coupon_code || coupon?.code || '',
          color: segmentColors[index] || '#ffffff',
          probability: reward.probability,
          reusable: true,
          remainingUses: null,
        };
      });
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error =
        localizedBackendMessage(error, this.text.haravanApiKeyRequiredForOrders) ||
        (error instanceof Error ? error.message : this.text.unableLoad);
    } finally {
      this.loading = false;
    }
  }

  private async refreshAnalytics(): Promise<void> {
    if (!this.session) return;
    if (!this.data) {
      await this.loadData();
      return;
    }

    this.error = '';
    try {
      const freshData = await getDashboardAnalytics(
        this.session.token,
        getAnalyticsWindow(
          this.analyticsPeriod,
          this.customAnalyticsFrom,
          this.customAnalyticsTo,
        ),
        this.activeView === 'announcementBar' ? this.activeAnnouncementBarId : undefined,
      );
      this.data = {
        ...this.data,
        analytics: freshData.analytics,
        announcementAnalytics: freshData.announcementAnalytics,
      };
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error =
        localizedBackendMessage(error, this.text.haravanApiKeyRequiredForOrders) ||
        (error instanceof Error ? error.message : this.text.unableLoad);
    }
  }

  private async loadSettings(): Promise<void> {
    if (!this.session) return;
    try {
      this.settings = await getDashboardSettings(this.session.token);
      this.haravanApiKey = this.settings.haravanApiKey || '';
    } catch {
      this.settings = {
        username: this.session.username,
        haravanApiKeyConfigured: false,
        haravanApiKey: '',
        haravanApiKeyPreview: '',
      };
      this.haravanApiKey = '';
    }
  }

  private async loadHaravanDiscounts(): Promise<void> {
    if (!this.session) return;
    this.loadingHaravanDiscounts = true;
    this.haravanDiscountsError = '';
    try {
      const response = await getHaravanDiscounts(this.session.token);
      this.haravanDiscounts = response.discounts;
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.haravanDiscounts = [];
      this.haravanDiscountsError =
        error instanceof Error ? error.message : this.text.noHaravanCoupons;
    } finally {
      this.loadingHaravanDiscounts = false;
    }
  }

  private checkLeadOrderConversions = async (): Promise<void> => {
    if (!this.session) return;
    this.error = '';
    this.saved = '';

    if (!this.settings) {
      await this.loadSettings();
    }

    if (!this.settings?.haravanApiKeyConfigured) {
      this.error = this.text.haravanApiKeyRequiredForOrders;
      return;
    }

    this.checkingOrderConversions = true;
    try {
      const result = await checkOrderConversions(this.session.token);
      this.saved = this.text.orderConversionChecked
        .replace('{matched}', String(result.matched))
        .replace('{orders}', String(result.ordersScanned));
      await this.loadData();
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error =
        localizedBackendMessage(error, this.text.haravanApiKeyRequiredForOrders) ||
        (error instanceof Error ? error.message : this.text.unableLoad);
    } finally {
      this.checkingOrderConversions = false;
    }
  };

  private async save(): Promise<void> {
    if (!this.session || !this.data) return;
    if (this.data.campaign.active && this.rewards.length < 2) {
      this.error = this.text.needTwoRewardsToActivate;
      return;
    }
    if (this.rewards.some((reward) => !reward.code.trim())) {
      this.error = this.text.emptyCouponCode;
      return;
    }
    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      await saveDashboardData(this.session.token, {
        campaignId: this.data.campaign.id,
        name: this.data.campaign.name,
        active: this.data.campaign.active,
        wheelConfig: {
          ...this.data.campaign.wheel_config,
          segmentColors: this.rewards.map((reward) => reward.color),
        },
        rewards: this.rewards,
      });
      this.saved = this.text.saved;
      await this.loadData();
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  }

  private saveSettings = async (): Promise<void> => {
    if (!this.session) return;
    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      this.settings = await saveDashboardSettings(this.session.token, {
        haravanApiKey: this.haravanApiKey.trim(),
      });
      this.haravanApiKey = this.settings.haravanApiKey || '';
      this.saved = this.text.settingsSaved;
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private handleCursorFileChange = async (event: Event): Promise<void> => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.error = '';
    this.saved = '';
    try {
      this.cursorFile = {
        name: 'custom-cursor.png',
        type: 'image/png',
        dataUrl: await readCursorFileAsPortablePng(file),
      };
      this.cursorClearFile = false;
    } catch (error) {
      this.error =
        localizedBackendMessage(error, this.text.haravanApiKeyRequiredForOrders) ||
        (error instanceof Error ? error.message : this.text.unableLoad);
    }
  };

  private openCursorFileDialog = (): void => {
    this.renderRoot.querySelector<HTMLInputElement>('#cursor-file-input')?.click();
  };

  private handleCursorFilePickerKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.openCursorFileDialog();
  };

  private clearCursorFile = (): void => {
    this.cursorFile = undefined;
    this.cursorClearFile = true;
    this.customCursor = {
      ...this.customCursor,
      cursorUrl: '',
      fileName: '',
    };
  };

  private clearCursorFileFromPicker = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
    this.clearCursorFile();
  };

  private saveCustomCursorSettings = async (): Promise<void> => {
    if (!this.session) return;
    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      this.customCursor = await saveCustomCursor(this.session.token, {
        active: this.customCursor.active,
        file: this.cursorFile,
        clearFile: this.cursorClearFile,
      });
      this.cursorFile = undefined;
      this.cursorClearFile = false;
      this.saved = this.text.cursorSaved;
      await this.loadData();
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private updateCurrencyConverter<Key extends keyof CurrencyConverterSettings>(
    key: Key,
    value: CurrencyConverterSettings[Key],
  ): void {
    this.currencyConverter = {
      ...this.currencyConverter,
      [key]: value,
    };
  }

  private toggleCurrencyTarget(currency: CurrencyCode, enabled: boolean): void {
    const current = this.currencyConverter.targetCurrencies;
    const next = enabled
      ? [...new Set([...current, currency])]
      : current.filter((item) => item !== currency);

    this.currencyConverter = {
      ...this.currencyConverter,
      targetCurrencies: next.length ? next : ['USD'],
    };
  }

  private saveCurrencyConverterSettings = async (): Promise<void> => {
    if (!this.session) return;
    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      this.currencyConverter = await saveCurrencyConverter(
        this.session.token,
        this.currencyConverter,
      );
      this.saved = this.text.currencySaved;
      await this.loadData();
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private updateAnnouncementBar<Key extends keyof AnnouncementBarSettings>(
    key: Key,
    value: AnnouncementBarSettings[Key],
  ): void {
    this.announcementBar = {
      ...this.announcementBar,
      [key]: value,
    };
  }

  private updateAnnouncementBarSettings<Key extends keyof AnnouncementBarsGlobalSettings>(
    key: Key,
    value: AnnouncementBarsGlobalSettings[Key],
  ): void {
    const next = {
      ...this.announcementBarSettings,
      [key]: value,
    };
    this.announcementBarSettings = normalizeAnnouncementBarSettings(next);
  }

  private openAnnouncementBar = (id: string): void => {
    const selected = this.announcementBars.find((bar) => bar.id === id);
    if (!selected) return;
    this.activeAnnouncementBarId = id;
    this.announcementBar = { ...selected };
    this.announcementIconFile = undefined;
    this.announcementClearIcon = false;
    this.previewVisible = false;
    void this.refreshAnalytics();
  };

  private closeAnnouncementBarDetail = (): void => {
    this.activeAnnouncementBarId = '';
    this.announcementDeleteModalOpen = false;
    this.announcementIconFile = undefined;
    this.announcementClearIcon = false;
    this.previewVisible = false;
    void this.refreshAnalytics();
  };

  private createAnnouncementBar = (): void => {
    const bar = normalizeAnnouncementBar(
      {
        title: 'New announcement',
        subtitle: 'Add your message here.',
      },
      createAnnouncementId(),
    );
    this.activeAnnouncementBarId = bar.id;
    this.announcementBar = { ...bar };
    this.announcementDeleteModalOpen = false;
    this.announcementIconFile = undefined;
    this.announcementClearIcon = false;
    this.previewVisible = false;
  };

  private openAnnouncementDeleteModal = (): void => {
    if (!this.activeAnnouncementBarId) return;
    this.announcementDeleteModalOpen = true;
  };

  private closeAnnouncementDeleteModal = (): void => {
    if (this.saving) return;
    this.announcementDeleteModalOpen = false;
  };

  private deleteAnnouncementBar = async (): Promise<void> => {
    if (!this.activeAnnouncementBarId) return;

    const existingBar = this.announcementBars.some(
      (bar) => bar.id === this.activeAnnouncementBarId,
    );
    if (!existingBar) {
      this.announcementDeleteModalOpen = false;
      this.closeAnnouncementBarDetail();
      return;
    }

    if (!this.session) return;

    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      const nextBars = this.announcementBars.filter(
        (bar) => bar.id !== this.activeAnnouncementBarId,
      );
      const response = await saveAnnouncementBar(this.session.token, {
        bars: nextBars,
        settings: this.announcementBarSettings,
        activeBarId: nextBars[0]?.id || '',
        iconBarId: '',
      });
      this.announcementBars = normalizeAnnouncementBars({
        announcementBars: response.bars,
        announcementBar: response.activeBar,
      });
      this.announcementBarSettings = normalizeAnnouncementBarSettings(response.settings);
      this.announcementBar = this.announcementBars[0] || normalizeAnnouncementBar();
      this.activeAnnouncementBarId = '';
      this.announcementDeleteModalOpen = false;
      this.announcementIconFile = undefined;
      this.announcementClearIcon = false;
      this.previewVisible = false;
      this.saved = this.text.announcementDeleted;
      await this.refreshAnalytics();
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private renderAnnouncementSchedule(bar: AnnouncementBarSettings) {
    const text = this.text;
    if (!bar.countdownEnabled || (!bar.countdownStartsAt && !bar.countdownEndsAt)) {
      return text.announcementAlwaysActive;
    }
    return html`
      ${bar.countdownStartsAt
        ? html`<span>${text.announcementFrom}: ${formatCompactDateTime(bar.countdownStartsAt)}</span>`
        : nothing}
      ${bar.countdownEndsAt
        ? html`<span>${text.announcementTo}: ${formatCompactDateTime(bar.countdownEndsAt)}</span>`
        : nothing}
    `;
  }

  private onAnnouncementIconSelected = async (event: Event): Promise<void> => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.error = '';
    this.saved = '';
    try {
      this.announcementIconFile = {
        name: 'announcement-icon.png',
        type: 'image/png',
        dataUrl: await readImageFileAsSquarePng(file, 40, 'Announcement icon'),
      };
      this.announcementClearIcon = false;
    } catch (error) {
      this.error = error instanceof Error ? error.message : this.text.unableLoad;
    } finally {
      input.value = '';
    }
  };

  private pickAnnouncementIconFile = (): void => {
    this.renderRoot.querySelector<HTMLInputElement>('#announcement-icon-input')?.click();
  };

  private clearAnnouncementIcon = (event?: Event): void => {
    event?.preventDefault();
    event?.stopPropagation();
    this.announcementIconFile = undefined;
    this.announcementClearIcon = true;
    this.announcementBar = {
      ...this.announcementBar,
      iconUrl: '',
      iconFileName: '',
    };
  };

  private onAnnouncementIconRemoveKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    this.clearAnnouncementIcon(event);
  };

  private saveAnnouncementBarSettings = async (): Promise<void> => {
    if (!this.session || !this.activeAnnouncementBarId) return;
    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      const bars = this.announcementBars.length ? this.announcementBars : [this.announcementBar];
      const existingBar = bars.some((bar) => bar.id === this.activeAnnouncementBarId);
      const nextBars = existingBar
        ? bars.map((bar) =>
            bar.id === this.activeAnnouncementBarId ? { ...this.announcementBar } : bar,
          )
        : [...bars, { ...this.announcementBar }];
      const response = await saveAnnouncementBar(this.session.token, {
        bars: nextBars,
        settings: this.announcementBarSettings,
        activeBarId: this.activeAnnouncementBarId,
        iconBarId: this.activeAnnouncementBarId,
        iconFile: this.announcementIconFile,
        clearIcon: this.announcementClearIcon,
      });
      this.announcementBarSettings = normalizeAnnouncementBarSettings(response.settings);
      this.announcementBars = normalizeAnnouncementBars({
        announcementBars: response.bars,
        announcementBar: response.activeBar,
      });
      this.announcementBar =
        this.announcementBars.find((bar) => bar.id === this.activeAnnouncementBarId) ||
        normalizeAnnouncementBar(response.activeBar);
      this.announcementIconFile = undefined;
      this.announcementClearIcon = false;
      this.saved = this.text.announcementSaved;
      await this.loadData();
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private saveAnnouncementGlobalSettings = async (): Promise<void> => {
    if (!this.session) return;
    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      const response = await saveAnnouncementBar(this.session.token, {
        bars: this.announcementBars,
        settings: this.announcementBarSettings,
        activeBarId: this.activeAnnouncementBarId || this.announcementBars[0]?.id || '',
      });
      this.announcementBarSettings = normalizeAnnouncementBarSettings(response.settings);
      this.announcementBars = normalizeAnnouncementBars({
        announcementBars: response.bars,
        announcementBar: response.activeBar,
      });
      this.saved = this.text.announcementGlobalSaved;
      await this.loadData();
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private updateShoppableVideos<Key extends keyof ShoppableVideosSettings>(
    key: Key,
    value: ShoppableVideosSettings[Key],
  ): void {
    if (key === 'pageUrl') {
      this.shoppablePageSections = [];
      this.shoppableSectionsError = '';
    }
    this.shoppableVideos = {
      ...this.shoppableVideos,
      [key]: value,
    };
  }

  private updateShoppablePlacement<
    Key extends keyof ShoppableVideosSettings['placement'],
  >(
    key: Key,
    value: ShoppableVideosSettings['placement'][Key],
  ): void {
    this.shoppableVideos = {
      ...this.shoppableVideos,
      placement: {
        ...this.shoppableVideos.placement,
        [key]: value,
      },
    };
  }

  private scanShoppableSections = async (): Promise<void> => {
    if (!this.session || !this.shoppableVideos.pageUrl.trim()) return;
    this.loadingShoppableSections = true;
    this.shoppableSectionsError = '';
    try {
      const response = await scanShoppablePageSections(
        this.session.token,
        this.shoppableVideos.pageUrl.trim(),
      );
      this.shoppablePageSections = response.sections || [];
      if (!this.shoppablePageSections.length) {
        this.shoppableSectionsError = this.text.shoppableNoSectionsFound;
      }
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.shoppablePageSections = [];
      this.shoppableSectionsError =
        error instanceof Error ? error.message : this.text.shoppableSectionsError;
    } finally {
      this.loadingShoppableSections = false;
    }
  };

  private selectScannedShoppableSection(value: string): void {
    if (!value) {
      this.shoppableVideos = {
        ...this.shoppableVideos,
        placement: { preset: 'auto', customSelector: '', customMode: 'after' },
      };
      return;
    }
    const [mode, ...selectorParts] = value.split('::');
    const selector = selectorParts.join('::').trim();
    if (!selector) return;
    this.shoppableVideos = {
      ...this.shoppableVideos,
      placement: {
        preset: 'custom',
        customSelector: selector,
        customMode: mode === 'before' ? 'before' : 'after',
      },
    };
  }

  private openShoppableCarousel = (id: string): void => {
    const selected = this.shoppableCarousels.find((carousel) => carousel.id === id);
    if (!selected) return;
    this.activeShoppableCarouselId = id;
    this.shoppableCarouselDraft = false;
    this.shoppableDeleteModalOpen = false;
    this.openShoppableProductPickerIndex = null;
    this.shoppablePageSections = [];
    this.shoppableSectionsError = '';
    this.shoppableVideos = normalizeShoppableVideos({
      ...selected,
      carousels: this.shoppableCarousels,
      activeCarouselId: id,
    });
    this.activeShoppableVideosView = 'settings';
  };

  private closeShoppableCarouselDetail = (): void => {
    if (this.shoppableCarouselDraft && this.activeShoppableCarouselId) {
      this.shoppableCarousels = this.shoppableCarousels.filter(
        (carousel) => carousel.id !== this.activeShoppableCarouselId,
      );
    }
    this.activeShoppableCarouselId = '';
    this.shoppableCarouselDraft = false;
    this.shoppableDeleteModalOpen = false;
    this.openShoppableProductPickerIndex = null;
    this.shoppablePageSections = [];
    this.shoppableSectionsError = '';
    this.activeShoppableVideosView = 'settings';
  };

  private createShoppableCarousel = (): void => {
    const carousel = normalizeShoppableCarousel(
      {
        title: 'Watch and buy',
        pageUrl: '/',
        active: false,
      },
      createShoppableCarouselId(),
    );
    this.shoppableCarousels = [...this.shoppableCarousels, carousel];
    this.activeShoppableCarouselId = carousel.id;
    this.shoppableCarouselDraft = true;
    this.shoppableDeleteModalOpen = false;
    this.openShoppableProductPickerIndex = null;
    this.shoppablePageSections = [];
    this.shoppableSectionsError = '';
    this.shoppableVideos = normalizeShoppableVideos({
      ...carousel,
      carousels: this.shoppableCarousels,
      activeCarouselId: carousel.id,
    });
    this.activeShoppableVideosView = 'settings';
  };

  private openShoppableDeleteModal = (): void => {
    if (!this.activeShoppableCarouselId) return;
    this.shoppableDeleteModalOpen = true;
  };

  private closeShoppableDeleteModal = (): void => {
    this.shoppableDeleteModalOpen = false;
  };

  private deleteShoppableCarousel = async (): Promise<void> => {
    if (!this.activeShoppableCarouselId) return;
    if (this.shoppableCarouselDraft) {
      this.closeShoppableCarouselDetail();
      return;
    }
    if (!this.session) return;

    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      const nextCarousels = this.shoppableCarousels.filter(
        (carousel) => carousel.id !== this.activeShoppableCarouselId,
      );
      const response = normalizeShoppableVideos(
        await saveShoppableVideos(this.session.token, {
          ...this.shoppableVideos,
          carousels: nextCarousels,
          activeCarouselId: nextCarousels[0]?.id || '',
        }),
      );
      this.shoppableCarousels = nextCarousels.length ? response.carousels : [];
      this.shoppableVideos = nextCarousels.length ? response : normalizeShoppableVideos({ carousels: [] });
      this.activeShoppableCarouselId = '';
      this.shoppableCarouselDraft = false;
      this.shoppableDeleteModalOpen = false;
      this.openShoppableProductPickerIndex = null;
      this.activeShoppableVideosView = 'settings';
      this.saved = this.text.shoppableDeleted;
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private getActiveProductCountdownItem(): ProductCountdownItem | undefined {
    return this.productCountdown.items.find((item) => item.id === this.activeProductCountdownId);
  }

  private openProductCountdown = (id: string): void => {
    if (!this.productCountdown.items.some((item) => item.id === id)) return;
    this.activeProductCountdownId = id;
    this.productCountdownDraft = false;
    this.productCountdownDeleteModalOpen = false;
  };

  private closeProductCountdownDetail = (): void => {
    if (this.productCountdownDraft && this.activeProductCountdownId) {
      this.productCountdown = {
        ...this.productCountdown,
        items: this.productCountdown.items.filter((item) => item.id !== this.activeProductCountdownId),
      };
    }
    this.activeProductCountdownId = '';
    this.productCountdownDraft = false;
    this.productCountdownDeleteModalOpen = false;
    this.productCountdownPickerOpen = false;
  };

  private createProductCountdown = (): void => {
    const item = normalizeProductCountdownItem({
      id: createProductCountdownId(),
      active: false,
      startsAt: '',
      endsAt: '',
    });
    this.productCountdown = {
      ...this.productCountdown,
      items: [...this.productCountdown.items, item],
      activeItemId: item.id,
    };
    this.activeProductCountdownId = item.id;
    this.productCountdownDraft = true;
    this.productCountdownDeleteModalOpen = false;
    this.productCountdownPickerOpen = false;
  };

  private updateProductCountdownItem(patch: Partial<ProductCountdownItem>): void {
    if (!this.activeProductCountdownId) return;
    this.productCountdown = {
      ...this.productCountdown,
      items: this.productCountdown.items.map((item) =>
        item.id === this.activeProductCountdownId ? normalizeProductCountdownItem({ ...item, ...patch }) : item,
      ),
    };
  }

  private toggleProductCountdownProductPicker = (): void => {
    this.productCountdownPickerOpen = !this.productCountdownPickerOpen;
    if (this.productCountdownPickerOpen && !this.shoppableProducts.length && !this.loadingShoppableProducts) {
      void this.loadShoppableProducts();
    }
  };

  private selectProductCountdownProduct(productId: string): void {
    const product = this.shoppableProducts.find((item) => item.id === productId);
    if (!product) {
      this.productCountdownPickerOpen = false;
      this.updateProductCountdownItem({
        productId: '',
        productTitle: '',
        productUrl: '',
        productImage: '',
      });
      return;
    }
    this.productCountdownPickerOpen = false;
    this.updateProductCountdownItem({
      productId: product.id,
      productTitle: product.title,
      productUrl: product.productUrl,
      productImage: product.image,
    });
  }

  private openProductCountdownDeleteModal = (): void => {
    if (!this.activeProductCountdownId) return;
    this.productCountdownDeleteModalOpen = true;
  };

  private closeProductCountdownDeleteModal = (): void => {
    this.productCountdownDeleteModalOpen = false;
  };

  private deleteProductCountdown = async (): Promise<void> => {
    if (!this.activeProductCountdownId) return;
    if (this.productCountdownDraft) {
      this.closeProductCountdownDetail();
      return;
    }
    if (!this.session) return;

    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      const nextItems = this.productCountdown.items.filter(
        (item) => item.id !== this.activeProductCountdownId,
      );
      const response = normalizeProductCountdown(
        await saveProductCountdown(this.session.token, {
          items: nextItems,
          activeItemId: nextItems[0]?.id || '',
        }),
      );
      this.productCountdown = response;
      this.activeProductCountdownId = '';
      this.productCountdownDraft = false;
      this.productCountdownDeleteModalOpen = false;
      this.saved = this.text.productCountdownDeleted;
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private addShoppableItem = (): void => {
    const nextIndex = this.shoppableVideos.items.length;
    this.shoppableVideos = {
      ...this.shoppableVideos,
      items: [
        ...this.shoppableVideos.items,
        {
          id: crypto.randomUUID(),
          videoUrl: '',
          title: '',
          productId: '',
          productTitle: '',
          productUrl: '',
        },
      ],
    };
    void this.focusShoppableVideoUrl(nextIndex);
  };

  private async focusShoppableVideoUrl(index: number): Promise<void> {
    await this.updateComplete;
    const input = this.renderRoot.querySelector<HTMLInputElement>(
      `input[data-shoppable-video-url="${index}"]`,
    );
    if (!input) return;
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => input.focus({ preventScroll: true }), 180);
  }

  private removeShoppableItem(index: number): void {
    this.shoppableVideos = {
      ...this.shoppableVideos,
      items: this.shoppableVideos.items.filter((_, currentIndex) => currentIndex !== index),
    };
  }

  private updateShoppableItem(index: number, patch: Partial<ShoppableVideoItem>): void {
    this.shoppableVideos = {
      ...this.shoppableVideos,
      items: this.shoppableVideos.items.map((item, currentIndex) =>
        currentIndex === index ? { ...item, ...patch } : item,
      ),
    };
  }

  private selectShoppableProduct(index: number, productId: string): void {
    const product = this.shoppableProducts.find((item) => item.id === productId);
    if (!product) {
      this.clearShoppableProduct(index);
      this.openShoppableProductPickerIndex = null;
      return;
    }

    this.openShoppableProductPickerIndex = null;
    this.updateShoppableItem(index, {
      productId: product.id,
      variantId: product.variantId,
      productTitle: product.title,
      productUrl: product.productUrl,
      productImage: product.image,
      productPrice: product.price,
      productCompareAtPrice: product.compareAtPrice,
      productDescription: product.description,
      title: this.shoppableVideos.items[index]?.title || product.title,
    });
  }

  private toggleShoppableProductPicker(index: number): void {
    this.openShoppableProductPickerIndex =
      this.openShoppableProductPickerIndex === index ? null : index;
    if (
      this.openShoppableProductPickerIndex === index &&
      !this.shoppableProducts.length &&
      !this.loadingShoppableProducts
    ) {
      void this.loadShoppableProducts();
    }
  }

  private clearShoppableProduct(index: number): void {
    this.updateShoppableItem(index, {
      productId: '',
      variantId: '',
      productTitle: '',
      productUrl: '',
      productImage: '',
      productPrice: 0,
      productCompareAtPrice: 0,
      productDescription: '',
    });
  }

  private loadShoppableProducts = async (): Promise<void> => {
    if (!this.session) return;
    this.loadingShoppableProducts = true;
    this.error = '';
    try {
      const response = await getHaravanProducts(this.session.token);
      this.shoppableProducts = response.products;
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableLoad;
    } finally {
      this.loadingShoppableProducts = false;
    }
  };

  private saveShoppableVideoSettings = async (): Promise<void> => {
    if (!this.session || !this.activeShoppableCarouselId) return;
    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      const nextCarousels = this.shoppableCarousels.some(
        (carousel) => carousel.id === this.activeShoppableCarouselId,
      )
        ? this.shoppableCarousels.map((carousel) =>
            carousel.id === this.activeShoppableCarouselId
              ? normalizeShoppableCarousel(this.shoppableVideos, carousel.id)
              : carousel,
          )
        : [...this.shoppableCarousels, normalizeShoppableCarousel(this.shoppableVideos)];
      const savedShoppableVideos = normalizeShoppableVideos(
        await saveShoppableVideos(this.session.token, {
          ...this.shoppableVideos,
          carousels: nextCarousels,
          activeCarouselId: this.activeShoppableCarouselId,
        }),
      );
      this.shoppableCarousels = savedShoppableVideos.carousels;
      this.shoppableVideos = savedShoppableVideos;
      this.saved = this.text.shoppableSaved;
      this.activeShoppableCarouselId = savedShoppableVideos.activeCarouselId || savedShoppableVideos.id;
      this.shoppableCarouselDraft = false;
      this.shoppableDeleteModalOpen = false;
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private saveProductCountdownSettings = async (): Promise<void> => {
    if (!this.session || !this.activeProductCountdownId) return;
    const activeItem = this.getActiveProductCountdownItem();
    if (!activeItem?.productId || !activeItem.endsAt) {
      this.error = this.text.productCountdownSelectProduct;
      return;
    }

    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      const response = normalizeProductCountdown(
        await saveProductCountdown(this.session.token, {
          items: this.productCountdown.items.map((item) =>
            item.id === this.activeProductCountdownId ? normalizeProductCountdownItem(activeItem) : item,
          ),
          activeItemId: this.activeProductCountdownId,
        }),
      );
      this.productCountdown = response;
      this.activeProductCountdownId = response.activeItemId || this.activeProductCountdownId;
      this.productCountdownDraft = false;
      this.productCountdownDeleteModalOpen = false;
      this.saved = this.text.productCountdownSaved;
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private savePassword = async (): Promise<void> => {
    if (!this.session) return;
    if (this.newPassword !== this.confirmPassword) {
      this.error = this.text.passwordMismatch;
      return;
    }

    this.saving = true;
    this.error = '';
    this.saved = '';
    try {
      this.settings = await saveDashboardSettings(this.session.token, {
        currentPassword: this.currentPassword,
        newPassword: this.newPassword,
      });
      this.closePasswordModal();
      this.saved = this.text.settingsSaved;
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.unableSave;
    } finally {
      this.saving = false;
    }
  };

  private openPasswordModal = (): void => {
    this.error = '';
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordModalOpen = true;
  };

  private closePasswordModal = (): void => {
    this.passwordModalOpen = false;
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
  };

  private addReward = (): void => {
    this.error = '';
    this.rewardModalIndex = null;
    this.rewardModalDraft = createRewardDraft();
    this.rewardModalOpen = true;
  };

  private openRewardModal(index: number): void {
    this.error = '';
    this.rewardModalIndex = index;
    this.rewardModalDraft = { ...this.rewards[index] };
    this.rewardModalOpen = true;
  }

  private closeRewardModal = (): void => {
    this.rewardModalOpen = false;
    this.rewardModalIndex = null;
    this.rewardModalDraft = createRewardDraft();
  };

  private updateRewardModalDraft<Key extends keyof RewardDraft>(
    key: Key,
    value: RewardDraft[Key],
  ): void {
    this.rewardModalDraft = { ...this.rewardModalDraft, [key]: value };
  }

  private handleRewardModalCouponChange(code: string): void {
    const selectedDiscount = this.haravanDiscounts.find((discount) => discount.code === code);

    this.rewardModalDraft = {
      ...this.rewardModalDraft,
      code,
      label: selectedDiscount?.label || code,
    };
  }

  private saveRewardModal = (): void => {
    const text = this.text;
    const code = this.rewardModalDraft.code.trim();
    if (!code) {
      this.error = text.emptyCouponCode;
      return;
    }

    const selectedDiscount = this.haravanDiscounts.find((discount) => discount.code === code);
    const probability = Number(this.rewardModalDraft.probability);
    const reward: RewardDraft = {
      ...this.rewardModalDraft,
      code,
      label:
        this.rewardModalDraft.label.trim() ||
        selectedDiscount?.label ||
        selectedDiscount?.code ||
        text.newCoupon,
      color: this.rewardModalDraft.color || '#ffffff',
      probability: Number.isFinite(probability) && probability >= 0 ? probability : 0,
      reusable: true,
      remainingUses: null,
    };

    if (this.rewardModalIndex === null) {
      this.rewards = [...this.rewards, reward];
    } else {
      this.rewards = this.rewards.map((currentReward, index) =>
        index === this.rewardModalIndex ? reward : currentReward,
      );
    }
    this.closeRewardModal();
  };

  private removeReward(index: number): void {
    this.error = '';
    this.rewards = this.rewards.filter((_, currentIndex) => currentIndex !== index);
  }

  private exportLeadsCsv = (): void => {
    if (!this.data) return;
    const rows = this.getLeadExportRows();
    const csv = rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.getExportBaseName()}-leads.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  private printLeadsPdf = (): void => {
    if (!this.data) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      this.error = this.text.unablePrint;
      return;
    }

    printWindow.document.open();
    printWindow.document.write(this.getLeadsPrintHtml());
    printWindow.document.close();
    printWindow.focus();
  };

  private getLeadsPrintHtml(): string {
    if (!this.data) return '';
    const rows = this.getLeadExportRows();

    return `<!doctype html>
      <html>
        <head>
          <title>${escapeHtml(this.getExportBaseName())} leads</title>
          <style>
            @page { margin: 18mm; }
            body { color: #172026; font-family: Arial, sans-serif; margin: 0; }
            h1 { margin: 0 0 6px; font-size: 22px; }
            p { margin: 0 0 18px; color: #667085; font-size: 12px; }
            .screenActions { margin: 0 0 16px; }
            button { min-height: 38px; border: 0; border-radius: 999px; background: #106d63; color: #fff; cursor: pointer; font: 700 13px Arial, sans-serif; padding: 0 16px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border-bottom: 1px solid #d8e1e8; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #f8fafc; color: #536173; text-transform: uppercase; }
            @media print { .screenActions { display: none; } }
          </style>
          <script>
            window.addEventListener('load', function () {
              window.setTimeout(function () {
                window.focus();
                window.print();
              }, 250);
            });
          </script>
        </head>
        <body>
          <div class="screenActions">
            <button type="button" onclick="window.print()">${escapeHtml(this.text.printPdf)}</button>
          </div>
          <h1>${escapeHtml(this.data.campaign.name)}</h1>
          <p>${escapeHtml(this.session?.shopDomain || '')} - ${escapeHtml(new Date().toLocaleString())}</p>
          <table>
            <thead>
              <tr>${rows[0].map((cell) => `<th>${escapeHtml(cell)}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${rows
                .slice(1)
                .map(
                  (row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`,
                )
                .join('')}
            </tbody>
          </table>
        </body>
      </html>`;
  }

  private getLeadExportRows(): string[][] {
    if (!this.data) return [];
    const text = this.text;
    return [
      [
        'Email',
        'Phone',
        text.prize,
        text.coupon,
        text.purchased,
        text.purchaseAmount,
        text.received,
      ],
      ...this.getSortedLeads().map((lead) => [
        lead.email,
        lead.phone,
        lead.reward_label || text.notAssigned,
        lead.coupon_code || text.noCoupon,
        lead.purchased ? text.yes : text.no,
        formatCurrency(lead.purchase_amount || 0),
        formatDate(lead.created_at, this.language),
      ]),
    ];
  }

  private getExportBaseName(): string {
    return slugify(`${this.session?.shopDomain || 'store'}-${this.data?.campaign.name || 'leads'}`);
  }

  private logout = (): void => {
    this.clearSessionExpiryTimer();
    localStorage.removeItem(sessionKey);
    this.session = null;
    this.data = undefined;
    this.settings = undefined;
    this.error = '';
    this.saved = '';
    this.shopDomain = '';
    this.username = '';
    this.password = '';
    this.loading = false;
    this.saving = false;
    this.checkingOrderConversions = false;
    this.haravanDiscounts = [];
    this.loadingHaravanDiscounts = false;
    this.haravanDiscountsError = '';
    this.activeView = 'spinWheel';
    this.activeSpinView = 'overview';
    this.passwordModalOpen = false;
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.haravanApiKey = '';
    this.apiKeyVisible = false;
    this.customCursor = { active: false, cursorUrl: '', fileName: '' };
    this.currencyConverter = normalizeCurrencyConverter();
    this.announcementBar = normalizeAnnouncementBar();
    this.announcementBars = [];
    this.announcementBarSettings = normalizeAnnouncementBarSettings();
    this.activeAnnouncementView = 'bars';
    this.activeAnnouncementBarId = '';
    this.announcementDeleteModalOpen = false;
    this.shoppableVideos = normalizeShoppableVideos();
    this.shoppableCarousels = [];
    this.activeShoppableCarouselId = '';
    this.shoppableCarouselDraft = false;
    this.shoppableDeleteModalOpen = false;
    this.shoppableProducts = [];
    this.loadingShoppableProducts = false;
    this.shoppablePageSections = [];
    this.loadingShoppableSections = false;
    this.shoppableSectionsError = '';
    this.openShoppableProductPickerIndex = null;
    this.productCountdown = normalizeProductCountdown();
    this.activeProductCountdownId = '';
    this.productCountdownDraft = false;
    this.productCountdownDeleteModalOpen = false;
    this.productCountdownPickerOpen = false;
    this.cursorFile = undefined;
    this.cursorClearFile = false;
  };

  private copyInstallSnippet = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(widgetSnippet);
      this.installCopied = true;
      window.setTimeout(() => {
        this.installCopied = false;
      }, 2200);
    } catch {
      this.error = this.text.unableCopy;
    }
  };

  private checkInstallation = async (): Promise<void> => {
    if (!this.session) return;
    this.checkingInstallation = true;
    this.error = '';
    try {
      this.installationCheck = await checkWidgetInstallation(this.session.token);
    } catch (error) {
      if (isSessionExpiredError(error)) {
        this.logout();
        return;
      }
      this.error = error instanceof Error ? error.message : this.text.installationCheckFailed;
    } finally {
      this.checkingInstallation = false;
    }
  };

  private scheduleSessionLogout(token: string): boolean {
    this.clearSessionExpiryTimer();
    const expiresAt = getTokenExpiresAt(token);
    if (!expiresAt || expiresAt <= Date.now()) {
      this.logout();
      return false;
    }

    const timeoutMs = Math.min(expiresAt - Date.now(), 2_147_483_647);
    this.sessionExpiryTimer = window.setTimeout(() => this.logout(), timeoutMs);
    return true;
  }

  private clearSessionExpiryTimer(): void {
    if (!this.sessionExpiryTimer) return;
    window.clearTimeout(this.sessionExpiryTimer);
    this.sessionExpiryTimer = undefined;
  }

  private isHaravanSession(): boolean {
    return this.session?.authProvider === 'haravan';
  }

  private shouldShowInstallationSection(): boolean {
    return this.data?.installation?.scriptTagInstalled !== true;
  }

  private shouldShowHaravanSigningIn(): boolean {
    return (
      !this.session &&
      isHaravanEmbeddedContext() &&
      (this.haravanAutoLoginLoading || !this.haravanAutoLoginAttempted)
    );
  }

  private get text() {
    return translations[this.language];
  }

  private setLanguage(language: DashboardLanguage): void {
    this.language = language;
    localStorage.setItem(languageKey, language);
    this.syncDocumentMetadata();
  }

  private syncDocumentMetadata(): void {
    const isDashboard = this.route === 'dashboard';
    const isBlogIndex = this.route === 'blogIndex';
    const isBlog = this.route === 'blog';
    const localizedLandingMeta = landingMeta[this.language];
    const localizedBlogIndexMeta = blogIndexMeta[this.language];
    const currentBlogPost = isBlog ? getCurrentBlogPost() : undefined;
    const title = isDashboard
      ? 'Dada Plugins | Dashboard'
      : isBlog
        ? currentBlogPost?.metaTitle || blogMeta.title
        : isBlogIndex
          ? localizedBlogIndexMeta.title
          : localizedLandingMeta.title;
    const description = isBlog
      ? currentBlogPost?.metaDescription || blogMeta.description
      : isBlogIndex
        ? localizedBlogIndexMeta.description
        : localizedLandingMeta.description;
    const keywords = isBlog
      ? currentBlogPost?.keywords || blogMeta.keywords
      : isBlogIndex
        ? localizedBlogIndexMeta.keywords
        : localizedLandingMeta.keywords;
    const canonical = isBlog
      ? `https://www.dadaplugins.com${currentBlogPost?.slug || blogSlug}`
      : isBlogIndex
        ? 'https://www.dadaplugins.com/blog'
        : 'https://www.dadaplugins.com/';
    const locale = isBlog || this.language === 'vi' ? 'vi_VN' : 'en_US';

    document.documentElement.lang = isBlog ? 'vi' : this.language;
    document.title = title;
    setNamedMeta('description', description);
    setNamedMeta('keywords', keywords);
    setNamedMeta('description:vi', landingMeta.vi.description);
    setNamedMeta('keywords:vi', landingMeta.vi.keywords);
    setNamedMeta(
      'robots',
      isDashboard ? 'noindex,nofollow' : 'index,follow,max-image-preview:large,max-snippet:-1',
    );
    setNamedMeta('googlebot', isDashboard ? 'noindex,nofollow' : 'index,follow');
    setPropertyMeta('og:title', title);
    setPropertyMeta('og:description', description);
    setPropertyMeta('og:type', isBlog ? 'article' : 'website');
    setPropertyMeta('og:url', canonical);
    setPropertyMeta('og:locale', locale);
    setPropertyMeta('og:locale:alternate', locale === 'vi_VN' ? 'en_US' : 'vi_VN');
    setPropertyMeta('og:image', 'https://www.dadaplugins.com/og-image.svg');
    setPropertyMeta('og:image:alt', 'Dada Plugins Haravan plugin dashboard and storefront preview');
    setNamedMeta('twitter:title', title);
    setNamedMeta('twitter:description', description);
    setNamedMeta('twitter:image', 'https://www.dadaplugins.com/og-image.svg');
    if (isBlog) {
      setPropertyMeta('article:published_time', currentBlogPost?.date || blogMeta.publishedAt);
      setPropertyMeta('article:modified_time', getBlogPostModifiedAt(currentBlogPost));
      setPropertyMeta('article:section', currentBlogPost?.category || blogMeta.category);
      setPropertyMeta(
        'article:tag',
        currentBlogPost?.kind === 'customCursor' ? 'Haravan Custom Cursor' : 'Haravan Spin Wheel',
      );
    } else {
      removePropertyMeta('article:published_time');
      removePropertyMeta('article:modified_time');
      removePropertyMeta('article:section');
      removePropertyMeta('article:tag');
    }
    setLinkHref('canonical', canonical);
    setJsonLd('dada-page-schema', buildPageSchema(this.route, this.language, currentBlogPost));
    void trackFirebasePageView(window.location.pathname, title);
  }
}

function readSession(): DashboardSession | null {
  try {
    const raw = localStorage.getItem(sessionKey);
    return raw ? (JSON.parse(raw) as DashboardSession) : null;
  } catch {
    return null;
  }
}

function readLanguage(): DashboardLanguage {
  try {
    return localStorage.getItem(languageKey) === 'vi' ? 'vi' : 'en';
  } catch {
    return 'en';
  }
}

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(sidebarCollapsedKey) === '1';
  } catch {
    return false;
  }
}

function getCurrentRoute(): AppRoute {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  if (pathname === '/dashboard') return 'dashboard';
  if (pathname === '/blog') return 'blogIndex';
  if (blogPosts.some((post) => post.slug === pathname)) return 'blog';
  return 'home';
}

function getCurrentBlogPost(): (typeof blogPosts)[number] | undefined {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  return blogPosts.find((post) => post.slug === pathname);
}

function getBlogPostModifiedAt(post: (typeof blogPosts)[number] | undefined): string {
  if (post?.kind === 'customCursor') return customCursorBlogMeta.modifiedAt;
  return blogMeta.modifiedAt;
}

function isAnalyticsPeriod(value: string): value is AnalyticsPeriod {
  return (
    value === 'today' ||
    value === 'yesterday' ||
    value === 'last7' ||
    value === 'last30' ||
    value === 'custom'
  );
}

function getAnalyticsWindow(
  period: AnalyticsPeriod,
  customFrom?: string,
  customTo?: string,
): DashboardAnalyticsWindow {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let from = startOfToday;
  let to = new Date(startOfToday);
  to.setDate(to.getDate() + 1);

  if (period === 'custom') {
    const customStart = parseDateInput(customFrom);
    const customEnd = parseDateInput(customTo);
    if (customStart && customEnd) {
      from = customStart <= customEnd ? customStart : customEnd;
      to = new Date((customStart <= customEnd ? customEnd : customStart).getTime());
      to.setDate(to.getDate() + 1);
    }
  }

  if (period === 'yesterday') {
    to = new Date(startOfToday);
    from = new Date(startOfToday);
    from.setDate(from.getDate() - 1);
  }

  if (period === 'last7') {
    from = new Date(startOfToday);
    from.setDate(from.getDate() - 6);
    to = now;
  }

  if (period === 'last30') {
    from = new Date(startOfToday);
    from.setDate(from.getDate() - 29);
    to = now;
  }

  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
}

function parseDateInput(value?: string): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    return null;
  }
  return date;
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function withDashboardConfigDefaults(config: WheelConfig): WheelConfig {
  const theme = config.theme || {};
  const copy = config.copy || {};

  return {
    ...config,
    theme: {
      ...theme,
      launcherColors: getLauncherColors(theme),
      launcherPointerColor: colorVar(theme.launcherPointerColor, '#ef4444'),
      launcherTextColor: colorVar(theme.launcherTextColor, '#ffffff'),
    },
    copy: {
      ...copy,
      triggerLabel: stringConfig(copy.triggerLabel, 'Spin to win'),
      submitLabel: stringConfig(copy.submitLabel, 'Spin now'),
      spinningLabel: stringConfig(copy.spinningLabel, 'Finding your reward...'),
    },
  };
}

function isSessionExpiredError(error: unknown): boolean {
  return error instanceof Error && /dashboard session|session expired/i.test(error.message);
}

function getTokenExpiresAt(token: string): number | null {
  try {
    const payload = token.split('.')[0];
    if (!payload) return null;
    const paddedPayload = `${payload}${'='.repeat((4 - (payload.length % 4)) % 4)}`;
    const session = JSON.parse(atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: unknown;
    };
    return typeof session.exp === 'number' ? session.exp * 1000 : null;
  } catch {
    return null;
  }
}

function formatDate(value: string, language: DashboardLanguage): string {
  return new Intl.DateTimeFormat(language === 'vi' ? 'vi-VN' : 'en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatCompactDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (part: number): string => String(part).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(value || 0);
}

async function readCursorFileAsPortablePng(file: File): Promise<string> {
  return readImageFileAsSquarePng(file, 32, 'Cursor');
}

async function readImageFileAsSquarePng(
  file: File,
  size: number,
  label: string,
): Promise<string> {
  const allowedTypes = ['image/png', 'image/webp', 'image/gif', 'image/svg+xml', 'image/jpeg'];
  if (!allowedTypes.includes(file.type)) {
    return Promise.reject(new Error(`${label} file type is not supported.`));
  }
  if (file.size > 2 * 1024 * 1024) {
    return Promise.reject(new Error(`${label} file must be smaller than 2 MB.`));
  }

  const sourceDataUrl = await readFileAsDataUrl(file);
  const image = await loadCursorImage(sourceDataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (!context) throw new Error(`Unable to prepare ${label.toLowerCase()} file.`);

  const scale = Math.min(size / image.naturalWidth, size / image.naturalHeight);
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const x = Math.round((size - width) / 2);
  const y = Math.round((size - height) / 2);
  context.clearRect(0, 0, size, size);
  context.drawImage(image, x, y, width, height);

  const pngDataUrl = canvas.toDataURL('image/png');
  if (pngDataUrl.length > 360 * 1024) {
    throw new Error(`${label} file must be smaller than 256 KB after optimization.`);
  }
  return pngDataUrl;
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(String(reader.result || '')));
    reader.addEventListener('error', () => reject(new Error('Unable to read cursor file.')));
    reader.readAsDataURL(file);
  });
}

function loadCursorImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image), { once: true });
    image.addEventListener('error', () => reject(new Error('Unable to prepare cursor file.')), {
      once: true,
    });
    image.src = dataUrl;
  });
}

function normalizeAnnouncementBar(
  value?: Partial<AnnouncementBarSettings>,
  fallbackId = 'announcement-1',
): AnnouncementBarSettings {
  return {
    id: value?.id || fallbackId,
    active: Boolean(value?.active),
    title: value?.title || 'Limited offer',
    subtitle: value?.subtitle || 'Unlock a special reward today.',
    iconUrl: value?.iconUrl || '',
    iconFileName: value?.iconFileName || '',
    backgroundColor: colorVar(value?.backgroundColor, '#111827'),
    backgroundEffect: normalizeAnnouncementBackgroundEffect(value?.backgroundEffect),
    textColor: colorVar(value?.textColor, '#ffffff'),
    alignment: normalizeAnnouncementAlignment(value?.alignment),
    behavior: value?.behavior === 'scrollable' ? 'scrollable' : 'fixed',
    placement: value?.placement === 'bottom' ? 'bottom' : 'top',
    linkUrl: value?.linkUrl || '',
    openInNewWindow: value?.openInNewWindow !== false,
    ctaEnabled: Boolean(value?.ctaEnabled),
    ctaText: value?.ctaText || 'Shop now',
    ctaTextColor: colorVar(value?.ctaTextColor, '#111827'),
    ctaBackgroundColor: colorVar(value?.ctaBackgroundColor, '#ffffff'),
    ctaCornerRadius: clampNumber(value?.ctaCornerRadius, 50, 0, 999),
    ctaBorderSize: clampNumber(value?.ctaBorderSize, 0, 0, 8),
    ctaBorderColor: colorVar(value?.ctaBorderColor, '#ffffff'),
    dismissEnabled: Boolean(value?.dismissEnabled),
    dismissOnce: Boolean(value?.dismissOnce),
    countdownEnabled: Boolean(value?.countdownEnabled),
    showCountdown: value?.showCountdown !== false,
    countdownStartsAt: value?.countdownStartsAt || '',
    countdownEndsAt: value?.countdownEndsAt || '',
  };
}

function normalizeAnnouncementBars(
  plugins?: Partial<{ announcementBar: AnnouncementBarSettings; announcementBars: AnnouncementBarSettings[] }>,
): AnnouncementBarSettings[] {
  const source = Array.isArray(plugins?.announcementBars)
    ? plugins.announcementBars
    : plugins?.announcementBar
      ? [plugins.announcementBar]
      : [normalizeAnnouncementBar()];

  return source
    .map((bar, index) =>
      normalizeAnnouncementBar(
        bar,
        index === 0 ? 'announcement-1' : createAnnouncementId(),
      ),
    )
    .slice(0, 20);
}

function normalizeAnnouncementBarSettings(
  value?: Partial<AnnouncementBarsGlobalSettings>,
): AnnouncementBarsGlobalSettings {
  const displayMode: AnnouncementBarsDisplayMode =
    value?.displayMode === 'alternate' ? 'alternate' : 'stacked';
  const transition: AnnouncementBarsTransition =
    value?.transition === 'none' || value?.transition === 'fade' ? value.transition : 'slide';

  return {
    allowMultiple: Boolean(value?.allowMultiple),
    displayMode,
    rotationSeconds: clampNumber(value?.rotationSeconds, 6, 2, 120),
    transition,
  };
}

function createAnnouncementId(): string {
  return `announcement-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeAnnouncementAlignment(
  value: unknown,
): AnnouncementBarSettings['alignment'] {
  return value === 'left' || value === 'right' || value === 'sliding' ? value : 'center';
}

function normalizeAnnouncementBackgroundEffect(
  value: unknown,
): AnnouncementBarSettings['backgroundEffect'] {
  return value === 'none' || value === 'spotlight' ? value : 'soft';
}

function normalizeCurrencyConverter(
  value?: Partial<CurrencyConverterSettings>,
): CurrencyConverterSettings {
  const targetCurrencies = Array.isArray(value?.targetCurrencies)
    ? value.targetCurrencies.filter((currency): currency is CurrencyCode =>
        supportedDashboardCurrencies.includes(currency),
      )
    : supportedDashboardCurrencies;

  return {
    active: Boolean(value?.active),
    baseCurrency: 'VND',
    targetCurrencies: targetCurrencies.length ? [...new Set(targetCurrencies)] : ['USD'],
    autoConvertByIp: true,
    showCurrencySwitcher: Boolean(value?.showCurrencySwitcher),
    showSwitcherOnlyOutsideVietnam: Boolean(value?.showSwitcherOnlyOutsideVietnam),
    switcherPosition: normalizeCurrencyPosition(value?.switcherPosition),
    switcherPrimaryColor: colorVar(value?.switcherPrimaryColor, '#0f766e'),
    showCurrencySymbol: value?.showCurrencySymbol !== false,
    showCurrencyCode: value?.showCurrencyCode !== false,
    ratesUpdatedAt: typeof value?.ratesUpdatedAt === 'string' ? value.ratesUpdatedAt : '',
    rates: normalizeCurrencyRates(value?.rates),
  };
}

function normalizeShoppableVideos(
  value?: Partial<ShoppableVideosSettings>,
): ShoppableVideosSettings {
  const carousels = normalizeShoppableCarousels(value);
  const activeCarouselId =
    value?.activeCarouselId && carousels.some((carousel) => carousel.id === value.activeCarouselId)
      ? value.activeCarouselId
      : carousels[0]?.id || '';
  if (!carousels.length) {
    return {
      id: '',
      active: false,
      title: '',
      pageUrl: '',
      cardCornerRadius: 20,
      cardBorderSize: 0,
      cardBorderColor: '#111827',
      cardPlaceholderColor: '#111827',
      productBarPrimaryColor: '#000000',
      productBarSecondaryColor: '#4b5563',
      productBarBackgroundColor: '#ffffff',
      placement: normalizeShoppablePlacement(undefined, 'after-hero'),
      items: [],
      carousels: [],
      activeCarouselId: '',
    };
  }
  const activeCarousel =
    carousels.find((carousel) => carousel.id === activeCarouselId) ||
    carousels[0];

  return {
    ...activeCarousel,
    carousels,
    activeCarouselId,
  };
}

function normalizeShoppableCarousels(
  value?: Partial<ShoppableVideosSettings>,
): ShoppableVideoCarousel[] {
  const rawCarousels = Array.isArray(value?.carousels) ? value.carousels : [];
  if (Array.isArray(value?.carousels) && rawCarousels.length === 0) return [];
  const source = rawCarousels.length ? rawCarousels : hasLegacyShoppableVideos(value) ? [value] : [];
  return source
    .map((carousel, index) =>
      normalizeShoppableCarousel(carousel || {}, carousel?.id || `shoppable-${index + 1}`),
    )
    .slice(0, 12);
}

function normalizeShoppablePlacement(
  value: Partial<ShoppableVideosSettings['placement']> | undefined,
  fallbackPreset: ShoppablePlacementPreset,
): ShoppableVideosSettings['placement'] {
  const allowed: ShoppablePlacementPreset[] = [
    'auto',
    'top',
    'after-hero',
    'before-footer',
    'after-product-grid',
    'after-product-images',
    'after-product-info',
    'before-recommendations',
    'custom',
  ];
  const preset = allowed.includes(value?.preset as ShoppablePlacementPreset)
    ? (value?.preset as ShoppablePlacementPreset)
    : fallbackPreset;
  return {
    preset,
    customSelector: typeof value?.customSelector === 'string' ? value.customSelector : '',
    customMode: value?.customMode === 'before' ? 'before' : 'after',
  };
}

function normalizeShoppableCarousel(
  value: Partial<ShoppableVideoCarousel> = {},
  fallbackId = createShoppableCarouselId(),
): ShoppableVideoCarousel {
  const items = Array.isArray(value?.items)
    ? value.items.map(normalizeShoppableVideoItem).filter((item) => item.id).slice(0, 12)
    : [];

  return {
    id: value.id || fallbackId,
    active: Boolean(value.active),
    title: value.title || 'Watch and buy',
    pageUrl: value.pageUrl || '/',
    cardCornerRadius: clampNumber(value.cardCornerRadius, 20, 0, 48),
    cardBorderSize: clampNumber(value.cardBorderSize, 0, 0, 12),
    cardBorderColor: colorVar(value.cardBorderColor, '#111827'),
    cardPlaceholderColor: colorVar(value.cardPlaceholderColor, '#111827'),
    productBarPrimaryColor: colorVar(value.productBarPrimaryColor, '#000000'),
    productBarSecondaryColor: colorVar(value.productBarSecondaryColor, '#4b5563'),
    productBarBackgroundColor: colorVar(value.productBarBackgroundColor, '#ffffff'),
    placement: normalizeShoppablePlacement(resolveShoppablePlacementInput(value), 'after-hero'),
    items,
  };
}

function hasLegacyShoppableVideos(value?: Partial<ShoppableVideosSettings>): boolean {
  return Boolean(value?.title || value?.active || (Array.isArray(value?.items) && value.items.length));
}

function getLegacyShoppablePlacement(
  value: Partial<ShoppableVideosSettings> | Partial<ShoppableVideoCarousel>,
) {
  const legacy = value as Partial<ShoppableVideosSettings> & {
    placements?: Record<string, ShoppablePlacementPreset>;
    showOnHomePage?: boolean;
    showOnProductsPage?: boolean;
    showOnProductDetailsPage?: boolean;
  };
  if (legacy.placement) return legacy.placement;
  if (legacy.showOnHomePage !== false && legacy.placements?.home) return legacy.placements.home;
  if (legacy.showOnProductDetailsPage && legacy.placements?.productDetails) {
    return legacy.placements.productDetails;
  }
  if (legacy.showOnProductsPage && legacy.placements?.products) return legacy.placements.products;
  return legacy.placements?.home || legacy.placements?.productDetails || legacy.placements?.products;
}

function resolveShoppablePlacementInput(
  value: Partial<ShoppableVideosSettings> | Partial<ShoppableVideoCarousel>,
): Partial<ShoppableVideosSettings['placement']> | undefined {
  const direct = value.placement;
  if (direct && typeof direct === 'object') return direct;
  const legacy = getLegacyShoppablePlacement(value);
  return legacy && typeof legacy === 'object' ? legacy : undefined;
}

function createShoppableCarouselId(): string {
  return `shoppable-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeShoppableVideoItem(value: Partial<ShoppableVideoItem>): ShoppableVideoItem {
  return {
    id: value.id || crypto.randomUUID(),
    videoUrl: value.videoUrl || '',
    title: value.title || '',
    productId: value.productId || '',
    variantId: value.variantId || '',
    productTitle: value.productTitle || '',
    productUrl: value.productUrl || '',
    productImage: value.productImage || '',
    productPrice: Number(value.productPrice || 0),
    productCompareAtPrice: Number(value.productCompareAtPrice || 0),
    productDescription: value.productDescription || '',
  };
}

function normalizeProductCountdown(
  value?: Partial<ProductCountdownSettings>,
): ProductCountdownSettings {
  const items = Array.isArray(value?.items)
    ? value.items.map(normalizeProductCountdownItem).filter((item) => item.id).slice(0, 40)
    : [];
  const activeItemId =
    value?.activeItemId && items.some((item) => item.id === value.activeItemId)
      ? value.activeItemId
      : items[0]?.id || '';
  return {
    items,
    activeItemId,
  };
}

function normalizeProductCountdownItem(
  value: Partial<ProductCountdownItem> = {},
): ProductCountdownItem {
  return {
    id: value.id || createProductCountdownId(),
    active: Boolean(value.active),
    productId: value.productId || '',
    productTitle: value.productTitle || '',
    productUrl: value.productUrl || '',
    productImage: value.productImage || '',
    startsAt: value.startsAt || '',
    endsAt: value.endsAt || '',
    position:
      value.position === 'left' || value.position === 'above' || value.position === 'below'
        ? value.position
        : 'right',
    backgroundColor: isHexColor(value.backgroundColor || '') ? value.backgroundColor || '#111827' : '#111827',
    textColor: isHexColor(value.textColor || '') ? value.textColor || '#ffffff' : '#ffffff',
    borderRadius: clampNumber(value.borderRadius, 50, 0, 999),
    borderWidth: clampNumber(value.borderWidth, 0, 0, 8),
    borderColor: isHexColor(value.borderColor || '') ? value.borderColor || '#111827' : '#111827',
  };
}

function createProductCountdownId(): string {
  return `product-countdown-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeCurrencyRates(
  value?: Partial<Record<CurrencyCode, number>>,
): Record<CurrencyCode, number> {
  return Object.fromEntries(
    supportedDashboardCurrencies.map((currency) => [
      currency,
      positiveNumber(value?.[currency], currencyRateFallbacks[currency]),
    ]),
  ) as Record<CurrencyCode, number>;
}

function normalizeCurrencyPosition(value: unknown): CurrencySwitcherPosition {
  return value === 'top-left' ||
    value === 'top-right' ||
    value === 'bottom-left' ||
    value === 'bottom-right'
    ? value
    : 'bottom-right';
}

function positiveNumber(value: unknown, fallback: number): number {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback;
}

function toDatetimeLocal(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function fromDatetimeLocal(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function getCountdownPreview(value: string | undefined): string {
  if (!value) return '00d 00h 00m';
  const remainingMs = Math.max(0, new Date(value).getTime() - Date.now());
  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function compareLeadValues(left: LeadRow, right: LeadRow, key: LeadsSortKey): number {
  if (key === 'purchased') {
    return Number(left.purchased) - Number(right.purchased);
  }

  if (key === 'purchaseAmount') {
    return Number(left.purchase_amount || 0) - Number(right.purchase_amount || 0);
  }

  return new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
}

function isHexColor(value: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(value);
}

function colorVar(value: unknown, fallback: string): string {
  return typeof value === 'string' && isHexColor(value) ? value : fallback;
}

function getColorFallback(key: string): string {
  const fallbacks: Record<string, string> = {
    primaryColor: '#106d63',
    accentColor: '#f59e0b',
    pointerColor: '#ef4444',
    surfaceColor: '#ffffff',
    textColor: '#172026',
    buttonTextColor: '#ffffff',
    launcherPointerColor: '#ef4444',
    launcherTextColor: '#ffffff',
  };

  return fallbacks[key] || '#0f766e';
}

function getLauncherColors(theme: Record<string, unknown>): string[] {
  const defaults = ['#0f766e', '#f59e0b', '#2563eb'];
  if (!Array.isArray(theme.launcherColors)) return defaults;

  const launcherColors = theme.launcherColors;
  return defaults.map((fallback, index) => colorVar(launcherColors[index], fallback));
}

function stringConfig(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function numberConfig(value: unknown, fallback: number): number {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function clampNumber(value: unknown, fallback: number, min: number, max: number): number {
  const numberValue = numberConfig(value, fallback);
  return Math.min(max, Math.max(min, numberValue));
}

function setNamedMeta(name: string, content: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.append(meta);
  }
  meta.content = content;
}

function setPropertyMeta(property: string, content: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.append(meta);
  }
  meta.content = content;
}

function removePropertyMeta(property: string): void {
  document.head
    .querySelectorAll<HTMLMetaElement>(`meta[property="${property}"]`)
    .forEach((meta) => meta.remove());
}

function setLinkHref(rel: string, href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.append(link);
  }
  link.href = href;
}

function setJsonLd(id: string, payload: unknown): void {
  let script = document.head.querySelector<HTMLScriptElement>(`script#${id}`);
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.append(script);
  }
  script.textContent = JSON.stringify(payload);
}

function buildPageSchema(
  route: AppRoute,
  language: DashboardLanguage,
  currentPost?: (typeof blogPosts)[number],
): unknown {
  const organization = {
    '@type': 'Organization',
    '@id': 'https://www.dadaplugins.com/#organization',
    name: 'Dada Plugins',
    url: 'https://www.dadaplugins.com/',
    logo: 'https://www.dadaplugins.com/dada-logo.png',
    description: landingMeta[language].description,
    knowsLanguage: ['en', 'vi'],
  };
  const website = {
    '@type': 'WebSite',
    '@id': 'https://www.dadaplugins.com/#website',
    url: 'https://www.dadaplugins.com/',
    name: 'Dada Plugins',
    publisher: { '@id': 'https://www.dadaplugins.com/#organization' },
    inLanguage: ['en', 'vi'],
  };
  const articlePostings = blogPosts.map((post) => ({
    '@type': 'BlogPosting',
    '@id': `https://www.dadaplugins.com${post.slug}#article`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.dadaplugins.com${post.slug}`,
    },
    headline: post.metaTitle,
    description: post.metaDescription,
    datePublished: post.date,
    dateModified: getBlogPostModifiedAt(post),
    inLanguage: 'vi',
    author: { '@id': 'https://www.dadaplugins.com/#organization' },
    publisher: { '@id': 'https://www.dadaplugins.com/#organization' },
    image: 'https://www.dadaplugins.com/og-image.svg',
    articleSection: post.category,
    keywords: post.keywords,
  }));
  const currentBlogPosting =
    articlePostings.find(
      (posting) => posting['@id'] === `https://www.dadaplugins.com${currentPost?.slug}#article`,
    ) || articlePostings[0];
  const blog = {
    '@type': 'Blog',
    '@id': 'https://www.dadaplugins.com/blog#blog',
    url: 'https://www.dadaplugins.com/blog',
    name: blogIndexMeta[language].title,
    description: blogIndexMeta[language].description,
    inLanguage: ['en', 'vi'],
    publisher: { '@id': 'https://www.dadaplugins.com/#organization' },
    blogPost: articlePostings.map((post) => ({ '@id': post['@id'] })),
  };
  const articleList = {
    '@type': 'ItemList',
    '@id': 'https://www.dadaplugins.com/#blog-posts',
    name: 'Dada Plugins blog articles',
    itemListElement: blogPosts.map((post, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `https://www.dadaplugins.com${post.slug}`,
      name: post.metaTitle,
      description: post.metaDescription,
    })),
  };

  const graph =
    route === 'blog'
      ? [organization, website, blog, currentBlogPosting]
      : route === 'blogIndex'
        ? [organization, website, blog, articleList, ...articlePostings]
        : route === 'dashboard'
          ? [organization, website]
          : [organization, website, articleList, blog, ...articlePostings];

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

function normalizeRequiredFields(value: unknown): LeadField[] {
  if (!Array.isArray(value)) return ['email', 'phone'];
  const fields = value.filter((item): item is LeadField => item === 'email' || item === 'phone');
  return fields.length ? fields : ['email', 'phone'];
}

function getReadableTextColor(color: string): string {
  const hex = color.replace('#', '');
  const red = Number.parseInt(hex.slice(0, 2), 16);
  const green = Number.parseInt(hex.slice(2, 4), 16);
  const blue = Number.parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
  return luminance > 0.62 ? '#172026' : '#ffffff';
}

function escapeCsvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function localizedBackendMessage(error: unknown, haravanApiKeyRequired: string): string {
  if (!(error instanceof Error)) return '';
  const message = error.message.trim();
  const lower = message.toLowerCase();
  if (
    lower.includes('enter your haravan api key') ||
    lower.includes('inserire api key haravan')
  ) {
    return haravanApiKeyRequired;
  }
  return '';
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return entities[character] || character;
  });
}

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'leads'
  );
}

function storefrontUrl(shopDomain: string): string {
  return `https://${shopDomain.replace(/^https?:\/\//i, '').replace(/\/.*$/, '')}`;
}
